/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";

// ─── MONETIZATION CONFIG ────────────────────────────────────────────────────
const AMAZON_TAG = "mkkmatchmakin-20";
const AFFILIATE_CONFIG = {
  amazon:   { tag: AMAZON_TAG, baseUrl: "https://www.amazon.com/s" },
  bookshop: { tag: "",         baseUrl: "https://bookshop.org/search" },
  etsy:     { tag: "",         baseUrl: "https://www.etsy.com/search" },
};
const buildAffiliateLink = (searchTerm, category = "") => {
  const cat = category.toLowerCase();
  if ((cat.includes("book") || cat.includes("novel")) && AFFILIATE_CONFIG.bookshop.tag) {
    return `${AFFILIATE_CONFIG.bookshop.baseUrl}?keywords=${encodeURIComponent(searchTerm)}&affiliate=${AFFILIATE_CONFIG.bookshop.tag}`;
  }
  if ((cat.includes("merch") || cat.includes("art") || cat.includes("gift")) && AFFILIATE_CONFIG.etsy.tag) {
    return `${AFFILIATE_CONFIG.etsy.baseUrl}?q=${encodeURIComponent(searchTerm)}&ref=${AFFILIATE_CONFIG.etsy.tag}`;
  }
  return `${AFFILIATE_CONFIG.amazon.baseUrl}?k=${encodeURIComponent(searchTerm)}&tag=${AFFILIATE_CONFIG.amazon.tag}`;
};

// ─── DESIGN TOKENS ──────────────────────────────────────────────────────────
const FONT = {
  display: `'Playfair Display', Georgia, 'Times New Roman', serif`,
  mono:    `'JetBrains Mono', ui-monospace, 'Courier New', monospace`,
};
const C = {
  paper:     "#f1e8d2",
  paperDark: "#e7dabb",
  paperSoft: "#ebe0c4",
  ink:       "#0f0f0f",
  inkSoft:   "#2a2a2a",
  inkMute:   "#666",
  red:       "#c8362d",
  redDark:   "#9c2419",
  black:     "#0a0a0a",
};
const hatched = (color = C.ink) =>
  `repeating-linear-gradient(135deg, ${color} 0 4px, ${color} 4px 5px, transparent 5px 9px)`;

const SECTIONS = [
  { id:"values", title:"Core Values", subtitle:"What matters most in life",
    questions:[
      {id:"q1", text:"I want to have kids someday.", dealbreaker:true},
      {id:"q2", text:"Family comes first when I make big choices."},
      {id:"q3", text:"I really want to succeed at work.", dealbreaker:true},
      {id:"q4", text:"Feeling safe with money matters more than chasing adventure."},
      {id:"q5", text:"My faith or spiritual side guides how I live.", dealbreaker:true},
      {id:"q6", text:"Staying close to family is important to me."},
      {id:"q7", text:"What someone believes in matters a lot when I'm dating.", dealbreaker:true},
    ] },
  { id:"lifestyle", title:"Lifestyle", subtitle:"How you live day to day",
    questions:[
      {id:"q8",  text:"I like routines more than surprises."},
      {id:"q9",  text:"I love going out and being around people."},
      {id:"q10", text:"I need alone time to recharge."},
      {id:"q11", text:"I like living in a busy city."},
      {id:"q12", text:"Traveling and seeing new places matter to me."},
      {id:"q13", text:"I'm more of a morning person than a night owl."},
    ] },
  { id:"personality", title:"Personality", subtitle:"Big Five — how you show up",
    questions:[
      {id:"q14", text:"Being around people gives me energy."},
      {id:"q15", text:"I try to keep the peace and avoid fights."},
      {id:"q16", text:"I plan things out and stay organized."},
      {id:"q17", text:"I get stressed or worried a lot."},
      {id:"q18", text:"I love new ideas, art, and learning new things."},
    ] },
  { id:"conflict", title:"Conflict Style", subtitle:"How you handle tension",
    questions:[
      {id:"q19", text:"When there's a problem, I want to talk about it right away."},
      {id:"q20", text:"I need space to cool off during a fight."},
      {id:"q21", text:"I'd rather fix a fight, even if it means giving in a little."},
      {id:"q22", text:"I hate it when things feel off and aren't fixed."},
    ] },
  { id:"emotional", title:"Emotional Needs", subtitle:"What you need to feel loved",
    questions:[
      {id:"q23", text:"I need my partner to remind me they care."},
      {id:"q24", text:"Hugs and touch matter a lot in a relationship."},
      {id:"q25", text:"I want a partner who makes me think."},
      {id:"q26", text:"I need to be physically attracted to someone to date them."},
    ] },
];

const ALL_QUESTIONS = SECTIONS.flatMap(s => s.questions);
const TOTAL_Qs = ALL_QUESTIONS.length;
const SCALE_LABELS = {1:"Strongly disagree", 2:"Disagree", 3:"Slightly disagree", 4:"Neutral", 5:"Slightly agree", 6:"Agree", 7:"Strongly agree"};

// ─── FANTASY ARENA QUESTIONS ────────────────────────────────────────────────
const ARENA_SECTIONS = [
  { id:"body", title:"Body", subtitle:"Your fight-day stats",
    questions:[
      {id:"age",     type:"number", text:"How old are you?",       placeholder:"e.g. 32", unit:"years"},
      {id:"height",  type:"choice", text:"How tall are you?",      choices:["Under 5'0\" (under 152 cm)","5'0\" – 5'3\" (152–161 cm)","5'4\" – 5'7\" (162–171 cm)","5'8\" – 5'11\" (172–181 cm)","6'0\" – 6'3\" (182–191 cm)","6'4\" or taller (192+ cm)"]},
      {id:"weight",  type:"choice", text:"How much do you weigh?", choices:["Under 120 lb (under 55 kg)","120–150 lb (55–68 kg)","150–180 lb (68–82 kg)","180–210 lb (82–95 kg)","210–240 lb (95–109 kg)","Over 240 lb (over 109 kg)"]},
      {id:"pushups", type:"choice", text:"Max pushups in one set?", choices:["0–5","6–15","16–30","31–50","50 or more"]},
      {id:"mile",    type:"choice", text:"What's your mile time?",  choices:["Under 6 min","6–7 min","7–8 min","8–10 min","10–12 min","Over 12 min, or can't run a mile"]},
    ] },
  { id:"cred", title:"Fight Cred", subtitle:"What you actually know",
    questions:[
      {id:"martial",   type:"choice", text:"Have you trained in fighting?",                 choices:["None","Dabbled","Train regularly","Advanced or pro"]},
      {id:"weapons",   type:"choice", text:"Have you trained with weapons?",                choices:["None","A little","Regular practice","Military or pro"]},
      {id:"realfight", type:"choice", text:"Been in a real fight, outside training?",       choices:["Never","Once","A few","Many"]},
    ] },
  { id:"head", title:"Head Game", subtitle:"What you'd actually do",
    questions:[
      {id:"barface",     type:"choice", text:"Someone gets right up in your face. First move?", choices:["Back off","Talk it down","Hold ground","Push back"]},
      {id:"dirty",       type:"choice", text:"In a real fight, how dirty would you go?",         choices:["Strictly fair","Bend a few rules","Eye-gouge, groin, whatever","Whatever it takes to survive"]},
      {id:"scars",       type:"choice", text:"Stitches, broken bones, or surgeries you've had?", choices:["None","One","A few","Many"]},
      {id:"hurt",        type:"likert", text:"I could hurt someone if I had to escape."},
      {id:"adrenaline",  type:"choice", text:"Sudden scary moment hits. Your gut reaction?",    choices:["Act","Freeze","Run"]},
      {id:"weapons_eye", type:"likert", text:"I notice things I could use as weapons wherever I am."},
    ] },
  { id:"style", title:"Style", subtitle:"Your fighter persona",
    questions:[
      {id:"vibe",      type:"choice", text:"Pick your fighter archetype.",         choices:["Cocky and underprepared","Scrappy underdog","Stoic and silent","Chaotic wild card","Calculated and patient"]},
      {id:"signature", type:"choice", text:"Your go-to move would be…",            choices:["The big swing — one shot, end it","The grapple — get them on the ground","The duck-and-weave — wear them out","The cheap shot — when they aren't looking","The psych-out — talk them down"]},
      {id:"entry",     type:"choice", text:"How do you walk into the fight?",      choices:["Silent and serious","Talking nonstop","Hyped up, bouncing","Eerily calm","Cracking jokes"]},
      {id:"aftermath", type:"choice", text:"After the fight is decided, you…",     choices:["Help them up and apologize","Walk away clean","Get one final dig in","Finish what you started"]},
    ] },
  { id:"survival", title:"Survival", subtitle:"The wilderness test",
    questions:[
      {id:"woods", type:"choice", text:"Could you make it 3 days alone in the woods?", choices:["No","Maybe","Yes","Definitely"]},
    ] },
];
const ARENA_QUESTIONS = ARENA_SECTIONS.flatMap(s => s.questions);
const ARENA_TOTAL = ARENA_QUESTIONS.length;
const ARENA_WEAPONS = [
  "Bare fists",
  "Knife",
  "Baseball bat",
  "Sword",
  "Handgun",
  "Taser",
  "Pepper spray",
  "Match opponent's gear",
];

// ─── FANTASY FEAST (eating contest) ─────────────────────────────────────────
const FEAST_SECTIONS = [
  { id:"body", title:"Body Basics", subtitle:"What you bring to the table",
    questions:[
      {id:"age",     type:"number", text:"How old are you?",       placeholder:"e.g. 32", unit:"years"},
      {id:"height",  type:"choice", text:"How tall are you?",      choices:["Under 5'0\" (under 152 cm)","5'0\" – 5'3\" (152–161 cm)","5'4\" – 5'7\" (162–171 cm)","5'8\" – 5'11\" (172–181 cm)","6'0\" – 6'3\" (182–191 cm)","6'4\" or taller (192+ cm)"]},
      {id:"weight",  type:"choice", text:"How much do you weigh?", choices:["Under 120 lb (under 55 kg)","120–150 lb (55–68 kg)","150–180 lb (68–82 kg)","180–210 lb (82–95 kg)","210–240 lb (95–109 kg)","Over 240 lb (over 109 kg)"]},
      {id:"appetite",type:"choice", text:"On a hungry day, you eat…",                   choices:["A normal meal","One big meal + dessert","Two adult-sized meals","I genuinely cannot fill up"]},
      {id:"speed",   type:"choice", text:"How fast do you usually eat?",                choices:["Slow / mindful","Average","Fast","Vacuum"]},
    ] },
  { id:"cred", title:"Eating Cred", subtitle:"Receipts, please",
    questions:[
      {id:"contests", type:"choice", text:"Ever entered an eating contest?",            choices:["Never","Once for fun","Several","I'm on the circuit"]},
      {id:"hotwings", type:"choice", text:"How many Buffalo wings could you eat?",      choices:["0–5","6–15","16–30","30+"]},
      {id:"spice",    type:"choice", text:"Spice tolerance?",                            choices:["Mild only","Medium","Hot","Carolina Reaper crowd"]},
    ] },
  { id:"head", title:"Stomach Game", subtitle:"The mental side",
    questions:[
      {id:"finishit", type:"choice", text:"When you're full, can you keep going?",      choices:["No, I tap out","With effort","Easily","I don't believe in 'full'"]},
      {id:"plate",    type:"choice", text:"Grew up 'clean your plate' household?",      choices:["Yes, ruthlessly","Sometimes","Rarely","Not at all"]},
      {id:"hurl",     type:"likert", text:"I can keep eating past my limit without getting sick."},
      {id:"behind",   type:"choice", text:"How do you handle being behind in a contest?", choices:["Panic-eat","Stay unflappable","Trash talk","Lock in"]},
      {id:"dessert",  type:"choice", text:"Sweet or savory winner?",                     choices:["Sweet, always","Savory, no contest","Switch back and forth","I want everything"]},
    ] },
  { id:"style", title:"Style", subtitle:"Your contest persona",
    questions:[
      {id:"vibe",      type:"choice", text:"Pick your eater archetype.",                choices:["Cocky and underprepared","Scrappy underdog","Stoic and silent","Chaotic wild card","Calculated and patient"]},
      {id:"signature", type:"choice", text:"Your signature move is…",                   choices:["Splash-and-swallow","Bite-and-stack","Chipmunk cheeks","Steady metronome tempo","The cocky pause-and-stare"]},
      {id:"entry",     type:"choice", text:"How do you walk to the table?",             choices:["Silent and serious","Talking nonstop","Hyped, bouncing","Eerily calm","Cracking jokes"]},
      {id:"aftermath", type:"choice", text:"After the final whistle, you…",             choices:["Help them up and apologize","Walk away clean","Get one final dig in","Eat one more, just to prove a point"]},
    ] },
];
const FEAST_QUESTIONS = FEAST_SECTIONS.flatMap(s => s.questions);
const FEAST_TOTAL = FEAST_QUESTIONS.length;
const FEAST_FOODS = [
  "Hot dogs",
  "Pancakes",
  "Buffalo wings",
  "Pizza slices",
  "Burritos",
  "Ramen bowls",
  "Burgers",
  "Tacos",
  "Cherry pie",
  "Match opponent's specialty",
];

// ─── OC PLAYGROUND (OC battler) ─────────────────────────────────────────────
// Quick-fill stat shortcuts — purely functional, no character identity attached.
const OC_QUICK_FILLS = [
  { label:"Balanced",     stats:{STR:6,SPD:6,DUR:6,INT:6,CUN:6,WIL:6} },
  { label:"Tank",         stats:{STR:8,SPD:3,DUR:9,INT:5,CUN:4,WIL:8} },
  { label:"Speedster",    stats:{STR:5,SPD:9,DUR:5,INT:6,CUN:7,WIL:5} },
  { label:"Brawler",      stats:{STR:9,SPD:6,DUR:7,INT:4,CUN:5,WIL:6} },
  { label:"Brain",        stats:{STR:3,SPD:5,DUR:5,INT:9,CUN:9,WIL:7} },
  { label:"Glass Cannon", stats:{STR:10,SPD:7,DUR:3,INT:5,CUN:5,WIL:4} },
];
const OC_SIZES    = ["Tiny","Small","Medium","Large","Huge"];
const OC_HABITATS = [
  { name:"Forest",     icon:"🌲" },
  { name:"Mountain",   icon:"⛰" },
  { name:"Sea",        icon:"🌊" },
  { name:"Sky",        icon:"☁" },
  { name:"Urban",      icon:"🏙" },
  { name:"Desert",     icon:"🏜" },
  { name:"Underworld", icon:"🔥" },
  { name:"Cosmic",     icon:"✨" },
  { name:"Suburbs",    icon:"🏚" },
  { name:"Carnival",   icon:"🎪" },
];
// Personality is six sliders covering the full sweet-to-dark, funny-to-serious spectrum.
const OC_PERSONALITY_AXES = [
  { key:"warmth",      low:"Cold",     high:"Warm"    },
  { key:"humor",       low:"Serious",  high:"Funny"   },
  { key:"energy",      low:"Calm",     high:"Chaotic" },
  { key:"confidence",  low:"Anxious",  high:"Cocky"   },
  { key:"optimism",    low:"Cynical",  high:"Hopeful" },
  { key:"earnestness", low:"Ironic",   high:"Sincere" },
];
const OC_PERSONALITY_DEFAULT = OC_PERSONALITY_AXES.reduce((acc, a) => ({ ...acc, [a.key]: 5 }), {});
const OC_STAT_KEYS = ["STR","SPD","DUR","INT","CUN","WIL"];
const OC_STAT_LABELS = { STR:"Strength", SPD:"Speed", DUR:"Durability", INT:"Intelligence", CUN:"Cunning", WIL:"Willpower" };
const OC_RANDOM_NAMES = ["Sparkle","Boris","Mango","Crank","Nibbles","Vex","Trout","Doris","Hex","Pickle","Gloomy","Sunbeam","Bramble","Norma","Skitter","Yarn","Toad","Stormy","Mossy","Crumb"];
const OC_ABILITIES = [
  "Fire breath", "Ice control", "Tidal waves", "Lightning", "Wind / storm",
  "Earthquake", "Telekinesis", "Teleportation", "Mind reading", "Invisibility",
  "Healing", "Super speed", "Super strength", "Flight", "Sonic scream",
  "Acid / venom", "Laser beams", "Shadow magic", "Plant control", "Time stop",
];
const OC_FEATURES = [
  "Horns", "Wings", "Tail", "Claws", "Fangs",
  "Scales", "Fur", "Armor plating", "Extra arms", "Extra eyes",
  "Glowing eyes", "Tentacles", "Spikes", "Shell", "Camouflage",
  "Hooves", "Forked tongue", "Long limbs", "Webbed hands", "Stinger tail",
];
const OC_GENDERS = ["Female", "Male", "Non-binary", "Other / skip"];

// Internally height is cm and weight is kg (so existing characters stay readable). Display is inches/pounds.
const cmToIn  = (cm) => Math.round((Number(cm) || 0) / 2.54);
const inToCm  = (inches) => Math.round(Number(inches) * 2.54);
const kgToLb  = (kg) => Math.round((Number(kg) || 0) * 2.2046);
const lbToKg  = (lb) => Math.round(Number(lb) / 2.2046);
const inToFtIn = (inches) => {
  const total = Math.round(Number(inches) || 0);
  const ft = Math.floor(total / 12);
  const remIn = total - ft * 12;
  return `${ft}'${remIn}"`;
};

// ─── OC HISTORY SHAPE ────────────────────────────────────────────────────────
// Every OC (and youChar) carries a `history` bucket — one slot per experience.
// As the OC plays Arena/MKK/Feast/Matchmaker, sessions append here. Bio + future
// cross-OC features read from this.
//   history.mkk              = [{ date, results: [{ celeb, verdict }] }]
//   history.mkkCameos        = [{ date, byOcId, byOcName, verdict, attraction }]
//   history.arena            = [{ date, opponent, opponentId, result, quotes: [{role, text}] }]
//   history.feast            = [{ date, opponent, opponentId, food, result, finalCount }]
//   history.feastCameos      = [{ date, byOcId, byOcName, result, food }]
//   history.matchmaker       = [{ date, crush, crushUniverse, crushOcId, verdict, score }]
//   history.matchmakerCameos = [{ date, byOcId, byOcName, verdict, score }]
// `ensureOcHistory` is idempotent — safe to call on any character at any time.
// It also migrates the older top-level `oc.quotes` array (per-quote rows) into
// the new arena history shape (one fight record per quote).
const ensureOcHistory = (c) => {
  if (!c) return c;
  const h = c.history && typeof c.history === "object" ? c.history : {};
  const out = {
    mkk: Array.isArray(h.mkk) ? h.mkk : [],
    mkkCameos: Array.isArray(h.mkkCameos) ? h.mkkCameos : [],
    arena: Array.isArray(h.arena) ? h.arena : [],
    feast: Array.isArray(h.feast) ? h.feast : [],
    feastCameos: Array.isArray(h.feastCameos) ? h.feastCameos : [],
    matchmaker: Array.isArray(h.matchmaker) ? h.matchmaker : [],
    matchmakerCameos: Array.isArray(h.matchmakerCameos) ? h.matchmakerCameos : [],
  };
  // Migrate legacy top-level quotes (each quote becomes a one-quote arena entry).
  if (Array.isArray(c.quotes) && c.quotes.length > 0 && out.arena.length === 0) {
    out.arena = c.quotes.map(q => ({
      date: q.date || "",
      opponent: q.opponent || "?",
      opponentId: null,
      result: q.role === "winner" ? "win" : q.role === "loser" ? "loss" : null,
      quotes: [{ role: q.role || "pre-fight", text: q.text || "" }],
    }));
  }
  const { quotes, ...rest } = c;
  return { ...rest, history: out };
};

// Flatten an OC's arena history into a quote board view (most recent first).
const flattenArenaQuotes = (c) => {
  const arena = c?.history?.arena;
  if (!Array.isArray(arena)) return [];
  const flat = [];
  for (const fight of arena) {
    if (!Array.isArray(fight.quotes)) continue;
    for (const q of fight.quotes) {
      flat.push({
        text: q.text,
        role: q.role,
        opponent: fight.opponent,
        date: fight.date,
      });
    }
  }
  return flat.reverse();
};

// Preset venues for the location picker. Coin-flip decides who chooses; when the user wins the toss they get this list + a free-form input.
const OC_VENUES = [
  "An empty parking garage at midnight",
  "A rooftop helicopter pad",
  "A frozen lake",
  "The edge of an active volcano",
  "An abandoned subway tunnel",
  "A medieval castle courtyard",
  "The deck of a pirate ship in a storm",
  "A cosmic void with floating asteroids",
  "A neon-lit Tokyo back alley",
  "A school gymnasium after hours",
  "A glowing mushroom forest",
  "The center of a Roman colosseum",
  "A bouncy castle at a kid's birthday party",
  "An IKEA showroom",
  "A Las Vegas casino floor",
  "A scrapyard at sunset",
  "Inside a haunted lighthouse",
  "A treehouse fort in the suburbs",
];

// Each weapon carries a real damage bonus that flows through the score function.
// Heavier / more lethal = bigger multiplier. Capped so 'Gun' isn't an automatic win.
const OC_WEAPONS = [
  { name:"Knife",          bonus: 1.10 },
  { name:"Baseball bat",   bonus: 1.12 },
  { name:"Sword",          bonus: 1.20 },
  { name:"Spear",          bonus: 1.18 },
  { name:"Bow",            bonus: 1.15 },
  { name:"Hammer",         bonus: 1.22 },
  { name:"Whip",           bonus: 1.10 },
  { name:"Brass knuckles", bonus: 1.08 },
  { name:"Magic wand",     bonus: 1.22 },
  { name:"Gun",            bonus: 1.30 },
  { name:"Mystery weapon", bonus: 1.15 },
];

const OC_RANDOM_SIGNATURES = [
  { name:"Yeet",                effect:"Throws something heavy. Hopefully not back." },
  { name:"The Old Switcheroo",  effect:"Swaps places mid-swing. Confuses everyone." },
  { name:"Bear Hug",            effect:"Holds opponent close. Awkward and effective." },
  { name:"Goose Honk",          effect:"Shrieks at deafening volume." },
  { name:"The Pretzel",         effect:"Wrestling move involving questionable flexibility." },
  { name:"Bonk",                effect:"Hits with whatever's nearby. Usually a sign." },
  { name:"The Long Goodbye",    effect:"Distracts with a lengthy farewell speech." },
  { name:"Snack Attack",        effect:"Eats opponent's food in front of them." },
  { name:"Reverse Psychology",  effect:"Talks opponent into surrendering." },
  { name:"Wide Smile",          effect:"Unsettling enough to pause the fight." },
  { name:"Side Eye",            effect:"Withers opponent's confidence at 20 paces." },
  { name:"Slow Clap",           effect:"Sarcastic applause breaks their concentration." },
  { name:"Cry On Cue",          effect:"Real tears. Opponent feels weird about hitting." },
  { name:"The Compliment",      effect:"Says something so kind opponent loses focus." },
  { name:"Dramatic Pause",      effect:"Holds eye contact too long. Opponent blinks first." },
  { name:"Glitter Bomb",        effect:"Blinds opponent. Ruins their outfit." },
  { name:"Read Them For Filth", effect:"Devastating verbal observation." },
  { name:"The Hair Flip",       effect:"Confidence rises 100%. Opponent's drops." },
  { name:"Loud Sigh",           effect:"Makes opponent question every life choice." },
  { name:"Sleeper Hold",        effect:"Puts them out with a story about traffic." },
];
const OC_RANDOM_CATCHPHRASES = [
  "Don't make me run.",
  "I had bigger plans for this evening.",
  "You're going to wish you skipped breakfast.",
  "This will only hurt a lot.",
  "I'm doing this for the story.",
  "I came here to win and chew bubble gum.",
  "Sorry in advance.",
  "I am genuinely uncoachable.",
  "Look me in the eyes. No, the other ones.",
  "I will not be answering questions after.",
  "Please don't tell my mom about this.",
  "I love you, but I have to do this.",
  "Once more, with feeling.",
  "I dreamed about this. Now I'm bored.",
  "We don't have to do this. We could just talk.",
  "Hi. I'm a little nervous.",
  "Born tired. Stayed that way.",
  "Let's get this over with so I can cry in the car.",
  "Honestly? I just want a snack.",
  "I have already forgotten your name.",
];

const MATCH_CONFIG = {
  marry: { label:"MARRY", glyph:"♛", verdict:"FOREVER PERSON",        scoreLabel:"Compatible" },
  screw: { label:"KISS",  glyph:"❤", verdict:"TOO HOT TO HANDLE",     scoreLabel:"Chemistry"  },
  kill:  { label:"KILL",  glyph:"✕", verdict:"MUTUAL DESTRUCTION",    scoreLabel:"Incompatible" },
};
const EVIDENCE = [
  { key:"values",      label:"Core Values" },
  { key:"lifestyle",   label:"Lifestyle" },
  { key:"personality", label:"Personality" },
  { key:"conflict",    label:"Conflict Style" },
  { key:"emotional",   label:"Emotional Needs" },
];

// Playful per-axis status labels for score bars (no trial framing).
const SCORE_STATUS = {
  values:      { high:"VALUES ON LOCK",         low:"DOES IT YOUR WAY" },
  lifestyle:   { high:"KNOWS WHAT YOU LIKE",    low:"MAKES YOUR OWN RULES" },
  personality: { high:"MAIN CHARACTER ENERGY",  low:"FULL OF SURPRISES" },
  conflict:    { high:"DRAMA-AVERSE",           low:"CHAOS COORDINATOR" },
  emotional:   { high:"HEART ON SLEEVE",        low:"PLAYS IT COOL" },
};

// ─── LOCALE / PROFILE / API HELPERS (unchanged) ─────────────────────────────
const detectLocale = () => {
  const lang = navigator.language || "en-US";
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const tzCountry = {
    "Asia/Tokyo":"Japan","Asia/Seoul":"South Korea","Asia/Shanghai":"China","Asia/Hong_Kong":"Hong Kong","Asia/Taipei":"Taiwan","Asia/Bangkok":"Thailand","Asia/Jakarta":"Indonesia","Asia/Singapore":"Singapore","Asia/Manila":"Philippines","Asia/Kolkata":"India","Asia/Mumbai":"India","Asia/Dubai":"UAE",
    "Europe/Paris":"France","Europe/Berlin":"Germany","Europe/Madrid":"Spain","Europe/Rome":"Italy","Europe/Lisbon":"Portugal","Europe/Amsterdam":"Netherlands","Europe/Warsaw":"Poland","Europe/Moscow":"Russia","Europe/Stockholm":"Sweden","Europe/Oslo":"Norway","Europe/Copenhagen":"Denmark","Europe/Helsinki":"Finland",
    "America/Sao_Paulo":"Brazil","America/Mexico_City":"Mexico","America/Buenos_Aires":"Argentina","America/Bogota":"Colombia","America/Lima":"Peru","America/Santiago":"Chile",
    "Africa/Cairo":"Egypt","Africa/Lagos":"Nigeria","Africa/Johannesburg":"South Africa","Australia/Sydney":"Australia","Pacific/Auckland":"New Zealand",
  };
  const country = tzCountry[tz] || null;
  const langCode = lang.split("-")[0];
  const langNames = {"fr":"French","ja":"Japanese","ko":"Korean","zh":"Chinese","de":"German","es":"Spanish","pt":"Portuguese","it":"Italian","nl":"Dutch","ru":"Russian","ar":"Arabic","hi":"Hindi","th":"Thai","id":"Indonesian","pl":"Polish","sv":"Swedish","da":"Danish","no":"Norwegian","fi":"Finnish","tr":"Turkish"};
  const language = langNames[langCode] || "English";
  return { lang, langCode, language, country, tz };
};

const computeProfile = (answers) => {
  const out = {};
  SECTIONS.forEach(sec => {
    const vals = sec.questions.map(q => answers[q.id] ?? 4);
    out[sec.id] = vals.reduce((a,b)=>a+b,0)/vals.length;
  });
  return out;
};

const callClaude = async (prompt) => {
  const res = await fetch("/api/claude", {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({model:"claude-sonnet-4-20250514", max_tokens:1000, messages:[{role:"user", content:prompt}]})
  });
  const d = await res.json();
  if (d.error) throw new Error(d.error.message);
  return d.content?.find(b => b.type === "text")?.text ?? "";
};

const parseJSON = (text) => {
  let t = (text || "").replace(/```json/gi,"").replace(/```/g,"").trim();
  const a = t.indexOf("{"), b = t.lastIndexOf("}");
  if (a === -1 || b === -1) throw new Error("No JSON in response");
  const slice = t.slice(a, b+1);
  try {
    return JSON.parse(slice);
  } catch(e) {
    // Surface a useful message so callers can show it instead of swallowing.
    throw new Error(`JSON parse failed: ${e.message}`);
  }
};

// Try to find an actor headshot for a specific fictional character.
// Search universe → title → cast credits → fuzzy-match character name → profile_path.
// Falls back to null; caller can fall back to the universe still.
const fetchTMDBCharacterImage = async (character, universe) => {
  const TMDB_KEY = "1f54bd990f1cdfb230adb312546d765d";
  if (!character || !universe) return null;
  const universeQ = encodeURIComponent(universe.replace(/\s*\(.*?\)/g, "").trim());
  const charLower = character.toLowerCase().trim();
  try {
    let titleId = null, mediaType = null;
    const tvRes  = await fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB_KEY}&query=${universeQ}&page=1`);
    const tvData = await tvRes.json();
    if (tvData.results?.[0]) { titleId = tvData.results[0].id; mediaType = "tv"; }
    if (!titleId) {
      const mvRes  = await fetch(`https://api.themoviedb.org/3/search/movie?api_key=${TMDB_KEY}&query=${universeQ}&page=1`);
      const mvData = await mvRes.json();
      if (mvData.results?.[0]) { titleId = mvData.results[0].id; mediaType = "movie"; }
    }
    if (!titleId) return null;

    const credRes  = await fetch(`https://api.themoviedb.org/3/${mediaType}/${titleId}/${mediaType === "tv" ? "aggregate_credits" : "credits"}?api_key=${TMDB_KEY}`);
    const credData = await credRes.json();
    const cast = credData.cast || [];

    const norm = s => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
    const charN = norm(charLower);
    const exact = cast.find(c => norm(c.character) === charN || norm(c.roles?.[0]?.character) === charN);
    const partial = exact || cast.find(c => {
      const cc = norm(c.character || c.roles?.[0]?.character);
      return cc && (cc.includes(charN) || charN.includes(cc));
    });
    const member = exact || partial;
    if (member?.profile_path) return `https://image.tmdb.org/t/p/w500${member.profile_path}`;
  } catch (e) {}
  return null;
};

const fetchTMDBImage = async (universe) => {
  const TMDB_KEY = "1f54bd990f1cdfb230adb312546d765d";
  const query = encodeURIComponent(universe.replace(/\s*\(.*?\)/g, "").trim());
  try {
    const tvRes = await fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB_KEY}&query=${query}&page=1`);
    const tvData = await tvRes.json();
    const tvHit = tvData.results?.[0];
    if (tvHit?.backdrop_path) return `https://image.tmdb.org/t/p/w780${tvHit.backdrop_path}`;
    if (tvHit?.poster_path)   return `https://image.tmdb.org/t/p/w500${tvHit.poster_path}`;
    const mvRes = await fetch(`https://api.themoviedb.org/3/search/movie?api_key=${TMDB_KEY}&query=${query}&page=1`);
    const mvData = await mvRes.json();
    const mvHit = mvData.results?.[0];
    if (mvHit?.backdrop_path) return `https://image.tmdb.org/t/p/w780${mvHit.backdrop_path}`;
    if (mvHit?.poster_path)   return `https://image.tmdb.org/t/p/w500${mvHit.poster_path}`;
  } catch(e) {}
  return null;
};

const STREAMING_LINKS = {
  "Netflix":"https://www.netflix.com","HBO Max":"https://www.max.com","Max":"https://www.max.com","Hulu":"https://www.hulu.com",
  "Disney+":"https://www.disneyplus.com","Apple TV+":"https://tv.apple.com","Amazon Prime Video":"https://www.amazon.com/gp/video/storefront",
  "Peacock":"https://www.peacocktv.com","Paramount+":"https://www.paramountplus.com","Starz":"https://www.starz.com",
};

const getAdRecommendations = async (results) => {
  const universes = [results.marry?.universe, results.screw?.universe, results.kill?.universe].filter(Boolean).join(", ");
  const characters = [results.marry?.character, results.screw?.character, results.kill?.character].filter(Boolean).join(", ");
  const marryUniverse = results.marry?.universe || "";
  const prompt = `You matched someone with: ${characters} from ${universes}.
Think like a Cosmo editor recommending things to a friend. Keep all suggestions PG — no explicit, crude, or graphic items. Generate:
1. Three editorial product picks (books, merch, games, experiences) they would genuinely love based on these universes
2. One streaming recommendation — the single best show/movie to watch next based on their marry match (${marryUniverse}), with the streaming service it's on

Return JSON only. No markdown.
{"recs":[
{"emoji":"emoji","title":"Short punchy title","description":"One sentence — why they'd love this specifically","category":"Books/Merch/Games/Travel/Beauty/Food","searchTerm":"amazon search term"},
{"emoji":"emoji","title":"title","description":"one sentence","category":"category","searchTerm":"search term"},
{"emoji":"emoji","title":"title","description":"one sentence","category":"category","searchTerm":"search term"}
],
"streaming":{"title":"Show or Movie Title","why":"One sentence on why it's perfect for them","service":"Netflix/HBO Max/Hulu/Disney+/Apple TV+/Amazon Prime Video/Peacock/Paramount+/Starz","genre":"Genre tag"}}`;
  try {
    const text = await callClaude(prompt);
    const parsed = parseJSON(text);
    return { recs: parsed.recs || [], streaming: parsed.streaming || null };
  } catch(e) { return { recs: [], streaming: null }; }
};

// Convert 6 personality sliders into a short descriptive string Claude can use.
// Only mentions axes where the value sits at one of the poles (≤3 or ≥7).
const describeOcPersonality = (p) => {
  if (!p) return "";
  const parts = OC_PERSONALITY_AXES.map(axis => {
    const v = p[axis.key] ?? 5;
    if (v <= 3) return axis.low.toLowerCase();
    if (v >= 7) return axis.high.toLowerCase();
    return null;
  }).filter(Boolean);
  return parts.join(", ") || "balanced";
};

// Ask Claude to suggest 3 taglines or catchphrases that match the character.
// Generate an image via the Replicate proxy. Returns { ok: true, url } or { ok: false, error }.
const generateImage = async (prompt, opts = {}) => {
  try {
    const res = await fetch("/api/image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, model: opts.model || "black-forest-labs/flux-schnell", aspect_ratio: opts.aspect_ratio || "1:1" }),
    });
    const data = await res.json();
    if (!res.ok || !data.url) return { ok: false, error: data.error || "Image generation failed." };
    return { ok: true, url: data.url };
  } catch (e) {
    return { ok: false, error: e.message || "Network error." };
  }
};

const ocPortraitPrompt = (c) => {
  const persona = describeOcPersonality(c.personality);
  const abilities = (c.abilities && c.abilities.length) ? c.abilities.join(", ") : "";
  const features  = (c.features  && c.features.length)  ? c.features.join(", ")  : "";
  const parts = [];
  parts.push(`Magazine cover illustration portrait of ${c.name}${c.tagline ? `, ${c.tagline}` : ""}.`);
  if (features) parts.push(`Physical features: ${features}.`);
  if (abilities) parts.push(`Powers: ${abilities}.`);
  if (c.gender && c.gender !== "Other / skip") parts.push(`Gender: ${c.gender}.`);
  if (c.habitat) parts.push(`Set in: ${c.habitat.toLowerCase()}.`);
  if (persona) parts.push(`Personality: ${persona}.`);
  parts.push(`Bold cosmopolitan tabloid illustration style, full color, dramatic lighting, magazine cover quality, no text.`);
  return parts.join(" ");
};

// Generate a tabloid-voice bio for an OC based on stats + personality + backstory + saved quotes.
const getOcProfile = async (c) => {
  const persona = describeOcPersonality(c.personality);
  const abilities = (c.abilities && c.abilities.length) ? c.abilities.join(", ") : "no special powers";
  const features  = (c.features  && c.features.length)  ? c.features.join(", ")  : "none on file";
  const back = c.backstory ? `\nBackstory (in the player's words): ${c.backstory}` : "";
  const flatQuotes = flattenArenaQuotes(c).reverse(); // oldest → newest for slicing
  const quotes = flatQuotes.length ? `\nMemorable lines from past fights:\n${flatQuotes.slice(-6).map(q => `  - "${q.text}" (${q.role}, vs ${q.opponent})`).join("\n")}` : "";
  const mkkHist = Array.isArray(c?.history?.mkk) ? c.history.mkk : [];
  const mkkLine = mkkHist.length
    ? `\nTheir Marry / Kiss / Kill picks (most recent session):\n${(mkkHist[mkkHist.length-1].results || []).map(r => `  - ${r.verdict.toUpperCase()}: ${r.celeb}${r.universe ? ` (${r.universe})` : ""}`).join("\n")}`
    : "";
  const cameos = Array.isArray(c?.history?.mkkCameos) ? c.history.mkkCameos : [];
  const cameoLine = cameos.length
    ? `\nOther fighters in the roster have picked ${c.name} in their own MKK sessions:\n${cameos.slice(-4).map(cam => `  - ${cam.byOcName} picked them as ${(cam.verdict || "").toUpperCase()}`).join("\n")}`
    : "";
  const mmHist = Array.isArray(c?.history?.matchmaker) ? c.history.matchmaker : [];
  const mmLine = mmHist.length
    ? `\nCrushes ${c.name} has chased (Matchmaker sessions):\n${mmHist.slice(-4).map(s => `  - ${s.crush}${s.crushUniverse ? ` (${s.crushUniverse})` : ""}${s.verdict ? ` — verdict: ${s.verdict}` : ""}${s.score != null ? `, ${s.score}%` : ""}`).join("\n")}`
    : "";
  const mmCameos = Array.isArray(c?.history?.matchmakerCameos) ? c.history.matchmakerCameos : [];
  const mmCameoLine = mmCameos.length
    ? `\nOther OCs in the roster have crushed on ${c.name}:\n${mmCameos.slice(-4).map(cam => `  - ${cam.byOcName} — verdict from ${c.name}'s side: ${cam.verdict || "?"}${cam.score != null ? `, ${cam.score}%` : ""}`).join("\n")}`
    : "";
  const feastHist = Array.isArray(c?.history?.feast) ? c.history.feast : [];
  const feastLine = feastHist.length
    ? `\nEating contests ${c.name} has been in:\n${feastHist.slice(-4).map(s => `  - ${s.result === "win" ? "OUT-ATE" : "LOST TO"} ${s.opponent} · ${s.food || "food"}${s.finalCount ? ` (${s.finalCount})` : ""}`).join("\n")}`
    : "";
  const prompt = `Write a tabloid magazine profile for this fighter. Cosmopolitan voice — punchy, character-driven, witty. 2-3 paragraphs. Make them feel like a person, not a stat block. Mention their personality, signature look (features), what they can do, their backstory if given, and quote one of their past lines if any are good. If they have Marry/Kiss/Kill picks, weave in what their taste says about them. End with a one-line tag.

NAME: ${c.name}${c.tagline ? ` ("${c.tagline}")` : ""}
SIZE: ${inToFtIn(cmToIn(c.height ?? 170))} (${cmToIn(c.height ?? 170)} in), ${kgToLb(c.weight ?? 70)} lb${c.gender && c.gender !== "Other / skip" ? ", " + c.gender : ""}${c.age ? `, age ${c.age}` : ""}
FROM: ${c.hometown || "unknown"}
HABITAT: ${c.habitat || "anywhere"}
HP: ${c.hp ?? 200}
ABILITIES: ${abilities}
FEATURES: ${features}
PERSONALITY: ${persona}
CATCHPHRASE: "${c.catchphrase || ""}"${back}${quotes}${mkkLine}${cameoLine}${mmLine}${mmCameoLine}${feastLine}

CONTENT RATING: PG. No drinking / alcohol references.

Return JSON only. No markdown.
{"profile":"2-3 paragraphs of prose, separated by \\n\\n. End with a one-line tag."}`;
  try {
    const text = await callClaude(prompt);
    const data = parseJSON(text);
    if (!data || !data.profile) return { ok: false, error: "No profile returned." };
    return { ok: true, profile: data.profile };
  } catch (e) {
    return { ok: false, error: (e && e.message) || "Profile generation failed." };
  }
};

// Run a romantic / psych analysis on an OC's compatibility-survey answers.
// Cosmo-voice 2-3 sentences, plus a one-line headline. Stored as oc.psychAnalysis.
const runOcPsychAnalysis = async (c) => {
  if (!c?.mkkSurvey?.answers) return { ok: false, error: "No survey on file." };
  const a = c.mkkSurvey.answers;
  const persona = describeOcPersonality(c.personality);
  const wantsKids       = a.q1>=6  ? "wants kids"          : a.q1<=2  ? "doesn't want kids"             : "is open about kids";
  const isAmbitious     = a.q3>=6  ? "very career-driven"  : a.q3<=2  ? "not career-focused"            : "moderately ambitious";
  const needsAlone      = a.q10>=6 ? "needs lots of alone time" : a.q10<=2 ? "thrives on togetherness" : "balanced on alone time";
  const lovesTravel     = a.q12>=6 ? "loves adventure and travel" : a.q12<=2 ? "prefers staying home"   : "sometimes adventurous";
  const conflictStyle   = a.q19>=5 ? "addresses conflict head-on" : a.q20>=5 ? "needs space to process" : "handles conflict carefully";
  const needsReassurance= a.q23>=6 ? "craves reassurance and closeness" : a.q23<=2 ? "very independent emotionally" : "moderately needs reassurance";
  const looksImportance = a.q26>=6 ? "considers physical attraction very important" : a.q26<=2 ? "prioritizes personality and connection over looks" : "considers looks moderately important";
  const isIntellectual  = a.q18>=6 ? "deeply intellectual and curious" : a.q18<=2 ? "more practical than intellectual" : "intellectually curious";
  const isOrganized     = a.q16>=6 ? "highly organized and structured" : a.q16<=2 ? "spontaneous and flexible" : "moderately organized";
  const name = c.name || "this character";
  const prompt = `Write a romantic personality analysis for ${name} — third person, Cosmopolitan voice — warm, direct, insightful, a horoscope crossed with therapy.

TRAITS: ${wantsKids}, ${isAmbitious}, ${needsAlone}, ${lovesTravel}, ${conflictStyle}, ${needsReassurance}, ${isIntellectual}, ${isOrganized}, ${looksImportance}.
PERSONALITY: ${persona || "balanced"}.${c.backstory ? ` Backstory: ${c.backstory.slice(0, 200)}.` : ""}

Return JSON only. No markdown.
{"headline":"A short 4-7 word tabloid headline that captures their romantic type — e.g. 'The Slow-Burn Loyalist' or 'Magnet For Beautiful Disasters'.","analysis":"2-3 flowing sentences in third person about what kind of partner ${name} is and what they need. Specific and resonant. No bullet points, no labels."}`;
  try {
    const text = await callClaude(prompt);
    const data = parseJSON(text);
    if (!data || !data.analysis) return { ok: false, error: "Couldn't read analysis." };
    return { ok: true, analysis: data.analysis, headline: data.headline || "" };
  } catch (e) {
    return { ok: false, error: (e && e.message) || "Analysis failed." };
  }
};

// Run Marry. Kiss. Kill. for an OC (or youChar). Optionally injects one of the
// player's other roster OCs as one of the three picks for cross-OC drama.
// Returns { ok, results } where results = { marry, screw, kill, injectedOcId? }.
const runOcMkkSession = async (player, opts = {}) => {
  const { attraction = "anyone", universe = "", injectOc = null } = opts;
  const persona = describeOcPersonality(player.personality);
  const name = player.name || "this character";
  const tagline = player.tagline ? ` ("${player.tagline}")` : "";
  const gender = (player.gender && player.gender !== "Other / skip") ? player.gender : "person";
  const pp = pronounsFor(player.gender);
  const backstory = player.backstory ? `\nBACKSTORY: ${player.backstory}` : "";
  const abilities = (player.abilities && player.abilities.length) ? `\nABILITIES: ${player.abilities.join(", ")}` : "";
  const features = (player.features && player.features.length) ? `\nFEATURES: ${player.features.slice(0,4).join(", ")}` : "";
  const catchphrase = player.catchphrase ? `\nCATCHPHRASE: "${player.catchphrase}"` : "";
  const surveyLine = describeOcSurvey(player.mkkSurvey);
  const cat = universe.trim();
  const universeLine = cat ? `Focus on: ${cat}. Be creative — include obvious and lesser-known characters.` : "any fictional universe you know";
  const injectLine = injectOc ? `\n\nROSTER GUEST: Include this fictional character as ONE of the three matches (pick whichever verdict — marry, kiss, or kill — fits the chemistry best). Use them EXACTLY as their slot.\n  - Name: ${injectOc.name}${injectOc.tagline ? ` ("${injectOc.tagline}")` : ""}\n  - Universe: "${name}'s playground" (this is another OC the player has built — NOT any famous character with the same name)\n  - Personality: ${describeOcPersonality(injectOc.personality) || "balanced"}\n  - Vibe: ${injectOc.backstory ? injectOc.backstory.slice(0, 200) : "a fellow OC on the player's roster"}` : "";
  const prompt = `You are writing for a Cosmopolitan magazine compatibility quiz. Match ${name} with three fictional characters using Marry / Kiss / Kill.
${name} is ${gender}, attracted to ${attraction || "anyone"}. Pronouns: ${pp.subj}/${pp.obj}/${pp.poss}.${tagline ? ` Their tagline: "${player.tagline}".` : ""}
${name} is an ORIGINAL CHARACTER (OC) created by the user — NOT any famous fictional character with the same name. Use only the traits below; don't invent abilities or canon associations from a same-named celebrity.
PERSONALITY: ${persona || "balanced"}${abilities}${features}${catchphrase}${backstory}${surveyLine}

RULES: Only characters that fit "${attraction || "anyone"}" as an attraction target. Be creative. CATEGORY: ${cat || "any fictional universe"}. Draw from: ${universeLine}. Refer to ${name} using ${pp.subj}/${pp.obj}/${pp.poss} pronouns consistently throughout the prose.${injectLine}

WRITING STYLE: Like Cosmopolitan magazine. Second person, addressed to ${name}. Specific to each character. Funny, dramatic, over-the-top — Marry should feel swoony and aspirational, Kiss should feel electric and dangerous, Kill should be hilariously brutal (lean into why this would be a disaster, in each character's specific voice). Channel each character's personality. NO numbers, NO percentages.

CONTENT RATING: PG. No profanity, no explicit sexual or romantic content, no graphic violence, no slurs, no drinking / alcohol references. Spicy, savage, swoony, melodramatic — all great. Crude is not.

Return JSON only. No markdown.
{"marry":{"character":"Name","universe":"Show","emoji":"emoji","score":91,"why":"2-3 second-person Cosmo sentences addressed to ${name}.","warning":"One sharp funny sentence.","opening_line":"Charming opener — written as if ${name.toUpperCase()} is approaching THEM. First person, confident, specific to their world."},"screw":{"character":"Name","universe":"Show","emoji":"emoji","score":76,"why":"2-3 second-person sentences.","warning":"One funny sentence about how this crashes.","opening_line":"A flirty line ${name.toUpperCase()} would say TO this character — first-person, bold, a little dangerous."},"kill":{"character":"Name","universe":"Show","emoji":"emoji","score":8,"why":"2-3 second-person darkly funny sentences.","warning":"One savage sentence.","opening_line":"The line ${name.toUpperCase()} would say TO this character that immediately causes conflict."}}`;
  try {
    const text = await callClaude(prompt);
    const data = parseJSON(text);
    if (!data || !data.marry || !data.screw || !data.kill) return { ok: false, error: "Couldn't read the results from Claude." };
    return { ok: true, results: { ...data, injectedOcId: injectOc?.id || null } };
  } catch (e) {
    return { ok: false, error: (e && e.message) || "MKK generation failed." };
  }
};

// Map gender to a pronoun set Claude can use unambiguously.
const pronounsFor = (gender) => {
  if (gender === "Female") return { subj: "she", obj: "her", poss: "her" };
  if (gender === "Male")   return { subj: "he",  obj: "him", poss: "his" };
  return { subj: "they", obj: "them", poss: "their" };
};

// Compact natural-language summary of a saved OC MKK survey for the prompt.
// `survey` is { answers: { q1..q26 } }. Returns "" if no survey.
const describeOcSurvey = (survey) => {
  if (!survey || !survey.answers) return "";
  const a = survey.answers;
  const profile = computeProfile(a);
  const pct = k => Math.round((profile[k]/7)*100);
  const desc = [];
  if (a.q1 != null) desc.push(a.q1>=6 ? "wants kids" : a.q1<=2 ? "doesn't want kids" : "open about kids");
  if (a.q3 != null) desc.push(a.q3>=6 ? "very career-driven" : a.q3<=2 ? "not career-focused" : "moderately ambitious");
  if (a.q10 != null) desc.push(a.q10>=6 ? "needs lots of alone time" : a.q10<=2 ? "thrives on togetherness" : "balanced on alone time");
  if (a.q12 != null) desc.push(a.q12>=6 ? "loves adventure and travel" : a.q12<=2 ? "prefers staying home" : "sometimes adventurous");
  if (a.q19 != null || a.q20 != null) desc.push(a.q19>=5 ? "addresses conflict head-on" : a.q20>=5 ? "needs space to process" : "handles conflict carefully");
  if (a.q23 != null) desc.push(a.q23>=6 ? "craves reassurance" : a.q23<=2 ? "very independent emotionally" : "moderately needs reassurance");
  if (a.q18 != null) desc.push(a.q18>=6 ? "deeply intellectual" : a.q18<=2 ? "more practical than intellectual" : "intellectually curious");
  if (a.q16 != null) desc.push(a.q16>=6 ? "highly organized" : a.q16<=2 ? "spontaneous and flexible" : "moderately organized");
  if (a.q26 != null) desc.push(a.q26>=6 ? "looks matter a lot" : a.q26<=2 ? "prioritizes connection over looks" : "looks matter moderately");
  return `\nCOMPATIBILITY SURVEY: CoreValues ${pct("values")}%, Lifestyle ${pct("lifestyle")}%, Conflict ${pct("conflict")}%, EmotionalNeeds ${pct("emotional")}%. ${desc.join(", ")}.`;
};

// Run Matchmaker (Fantasy Crush-style compatibility) for an OC vs a target.
// The target can be a free-form fictional character or another roster OC.
// Returns { ok, result } where result has the same FC-shape fields the existing
// fc results screen knows about, plus crushOcId if cross-OC.
const runOcMatchmaker = async (player, target) => {
  const persona = describeOcPersonality(player.personality);
  const playerName = player.name || "this character";
  const gender = (player.gender && player.gender !== "Other / skip") ? player.gender : "person";
  const pp = pronounsFor(player.gender);
  const tagline = player.tagline ? ` ("${player.tagline}")` : "";
  const back = player.backstory ? `\nBACKSTORY: ${player.backstory}` : "";
  const abilities = (player.abilities && player.abilities.length) ? `\nABILITIES: ${player.abilities.join(", ")}` : "";
  const features = (player.features && player.features.length) ? `\nFEATURES: ${player.features.slice(0,4).join(", ")}` : "";
  const surveyLine = describeOcSurvey(player.mkkSurvey);
  const isOcCrush = !!target.ocId;
  const crushBlock = isOcCrush
    ? `Crush is another OC on the player's roster.
  - Name: ${target.name}${target.tagline ? ` ("${target.tagline}")` : ""}
  - Universe: "the same playground" (treat them as a fellow OC the player knows — they are NOT any famous fictional character with the same name)
  - Personality: ${describeOcPersonality(target.personality) || "balanced"}
  - Vibe: ${target.backstory ? target.backstory.slice(0, 200) : "a fellow fighter"}
  - Pronoun: ${target.gender === "Female" ? "she/her" : target.gender === "Male" ? "he/him" : "they/them"}`
    : `Crush is a fictional character.
  - Name: ${target.name}
  - Universe: ${target.universe || "any"}`;
  const prompt = `You are writing for a Cosmopolitan magazine compatibility quiz. Tell ${playerName}${tagline} whether their crush is into them — in the crush's voice.

PLAYER: ${playerName}${tagline}, ${gender}. Pronouns: ${pp.subj}/${pp.obj}/${pp.poss}.
PLAYER is an ORIGINAL CHARACTER (OC) created by the user. Do NOT conflate them with any famous fictional character who shares their name — ignore canon associations with that name. Treat only the traits below as canonical.
PERSONALITY: ${persona || "balanced"}${abilities}${features}${back}${surveyLine}

CRUSH:
${crushBlock}

SPELLING: Use canonical spelling for the crush's name and universe.

SCORING: 0-100 based on actual fit between the crush and ${playerName}. Use the verdict to anchor: SMITTEN 85-99, INTRIGUED 65-84, CURIOUS 45-64, FRIEND-ZONED 30-44, COMPLICATED 15-29, NOT INTERESTED 0-14.

WRITING STYLE: First-person — the crush is speaking about ${playerName}. Refer to ${playerName} using ${pp.subj}/${pp.obj}/${pp.poss} pronouns consistently. Specific, opinionated, in character. Cosmopolitan tone. Funny, sharp, dramatic. NO numbers in prose. Only mention traits the prompt actually establishes — do not invent abilities the player doesn't have.

CONTENT RATING: PG. No drinking / alcohol references, no explicit content, no slurs.

Return JSON only. No markdown.
{"character":"Canonical character name","universe":"Canonical universe","verdict":"SMITTEN | INTRIGUED | CURIOUS | FRIEND-ZONED | COMPLICATED | NOT INTERESTED","score":<integer 0-100>,"pronoun":"she | he | they | it","first_impression":"One sharp first-person sentence — the crush's gut reaction.","what_they_think":"2-3 sentences in the crush's voice — their honest take on ${playerName}.","relationship":"2-3 sentences describing what the relationship would actually look like.","they_love":"One first-person sentence about what they'd love most about ${playerName}.","they_dislike":"One first-person sentence about what would drive them up the wall.","opening_line":"The first thing the crush would say to ${playerName} — in character."}`;
  try {
    const text = await callClaude(prompt);
    const data = parseJSON(text);
    if (!data || !data.what_they_think) return { ok: false, error: "Couldn't read the result from Claude." };
    return { ok: true, result: { ...data, crushOcId: target.ocId || null } };
  } catch (e) {
    return { ok: false, error: (e && e.message) || "Matchmaker failed." };
  }
};

// Run an eating contest between two OCs (player + opponent). Opponent can be a
// free-form fictional character or another roster OC. Returns the play-by-play
// result, same shape the existing feast results screen reads.
const runOcFeast = async (player, opponent, food) => {
  const pPersona = describeOcPersonality(player.personality);
  const oPersona = opponent.ocId ? describeOcPersonality(opponent.personality) : "";
  const playerName = player.name || "Fighter A";
  const opName = opponent.name || "Fighter B";
  const opUniverse = opponent.universe || (opponent.ocId ? "the same playground" : "any");
  const opIsOc = !!opponent.ocId;
  const opBlock = opIsOc
    ? `OPPONENT is another OC on the player's roster.
  - Name: ${opName}${opponent.tagline ? ` ("${opponent.tagline}")` : ""}
  - Personality: ${oPersona || "balanced"}
  - Backstory: ${opponent.backstory ? opponent.backstory.slice(0, 200) : "a fellow fighter"}
  - Habitat: ${opponent.habitat || "anywhere"}
  Treat them as a normal person with these traits — no canonical eating abilities to weigh.`
    : `OPPONENT: ${opName} from ${opUniverse}. Use their canonical eating abilities — if they're a known glutton or have supernatural stomach in ${opUniverse}, that dominates. If not, treat them as a normal person their size.`;
  const prompt = `You are writing a Cosmopolitan magazine eating-contest column — stylized but with real outcomes — about a competitive eating contest between ${playerName} and ${opName}.

THE PLAYER: ${playerName}${player.tagline ? ` ("${player.tagline}")` : ""}
  - ${player.gender || "person"}${player.age ? `, age ${player.age}` : ""}
  - Personality: ${pPersona || "balanced"}
  - Backstory: ${player.backstory ? player.backstory.slice(0, 200) : "—"}
  - Catchphrase: "${player.catchphrase || ""}"

${opBlock}

THE FOOD: ${food || "Hot dogs"}.${food === "Match opponent's specialty" ? ` Pick the food the OPPONENT would canonically eat the most of and use that.` : ""}

SCENE: ${playerName} challenged ${opName} to an eating contest. ${opName} picks the venue — a spot that fits an eating contest. Write "environment" from ${opName}'s POV as the host. Frame it as an INTENTIONAL challenge.

RUN THE CONTEST. Five plate-by-plate beats: opening minutes, mid-contest stomach wall, when one pulls away, comeback attempt, the final plate. Decide the winner honestly based on capacity + canon ability (where applicable) + persona.

WRITING STYLE: Cosmo magazine voice. Third-person using ${playerName}'s and ${opName}'s names. Funny when honest.

CONTENT RATING: PG. No vomit detail, no gross-out, no drinking / alcohol references. Bloated, mustard-stained, food coma — all fine.

VARIETY: This is replayable — vary your output between runs.

Return JSON only. No markdown.
{"player":"${playerName}","opponent":"${opName}","opponent_universe":"${opUniverse}","food":"${food || "Hot dogs"}","location_pick":"Short name of the specific venue ${opName} picked","environment":"1-2 sentence scene-set from ${opName}'s POV as the host","play_by_play":["five short beats — opening, mid-contest, lead change, comeback, final plate"],"winner_role":"the literal string 'player' or 'opponent'","time_seconds":<integer total contest duration in seconds; range from ~90s blowout up to 30+ minutes drawn-out grind; use odd numbers, avoid clustering on 300/600/900>,"time_narrative":"One short line for color.","final_count":"Scoreboard at the end — e.g. '${playerName}: 22 ${food || "hot dogs"} · ${opName}: 4.5'","winner_quote":"One short quotable line the WINNER says, leaning back from the table.","loser_quote":"One short line the LOSER mutters or yells.","condition_of_loser":"One funny sentence about the loser's state after."}`;
  try {
    const text = await callClaude(prompt);
    const data = parseJSON(text);
    if (!data || !data.play_by_play || !data.winner_role) return { ok: false, error: "Couldn't read the contest result." };
    return { ok: true, result: { ...data, opponentOcId: opponent.ocId || null } };
  } catch (e) {
    return { ok: false, error: (e && e.message) || "Feast failed." };
  }
};

const ocFightScenePrompt = (a, b, narration, opts = {}) => {
  const desc = (c) => {
    const feats = (c.features && c.features.length) ? c.features.slice(0,3).join(", ") : "";
    return `${c.name}${feats ? ` (${feats})` : ""}`;
  };
  const scene = narration?.environment || "";
  // Anchor the image to the FIRST BEAT of the play-by-play so the photo matches what the caption describes.
  const firstBeat = narration?.play_by_play?.[0] || "";
  const ANGLES = [
    "tight close-up — both fighters fill the frame, mid-action, you can see every expression",
    "low angle hero shot — fighters loom over the camera, mid-strike",
    "over-the-shoulder of fighter A looking at fighter B, ready to clash",
    "mid-shot from chest up — both fighters mid-trade, faces clear and intense",
    "dramatic ¾ angle with sparks flying, fighters at center frame, no negative space",
  ];
  const angle = ANGLES[Math.floor((opts.variant ? hashStr(opts.variant) : Math.random() * ANGLES.length)) % ANGLES.length];
  return `${angle}. ${desc(a)} fighting ${desc(b)}. ${scene} Show this specific moment: ${firstBeat} Bold cosmopolitan tabloid magazine illustration style, dynamic action, dramatic lighting, faces clearly visible, no text, no logos.`;
};

const hashStr = (s) => {
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const getOcSuggestions = async (field, character) => {
  const persona = describeOcPersonality(character.personality);
  const sig = character.signatureName ? `Signature move: "${character.signatureName}"${character.signatureEffect ? ` (${character.signatureEffect})` : ""}.` : "";
  const tagline = character.tagline ? `Their tagline is "${character.tagline}".` : "";
  const isSignature = field === "signature";
  const targetCopy = field === "tagline"
    ? `Write 3 short TAGLINES (4-8 words each) for this character. A tagline is a one-line descriptor that goes under their name on the card — e.g. "Sad clown with a sword", "Glitter tornado, somehow", "Just trying his best". Funny preferred. No quotes around them.

Return JSON only. No markdown.
{"suggestions":["one","two","three"]}`
    : field === "signature"
    ? `Write 3 SIGNATURE MOVES for this character. Each is a name + a one-sentence effect that fits their stats and personality (a brain-heavy character has clever moves; a STR-heavy brute has bonk moves; a chaotic character has wild moves; a sweet character has weird-gentle moves). Funny preferred.

Return JSON only. No markdown.
{"suggestions":[
  {"name":"Move Name","effect":"What it does, one sentence."},
  {"name":"Move Name","effect":"What it does, one sentence."},
  {"name":"Move Name","effect":"What it does, one sentence."}
]}`
    : `Write 3 short CATCHPHRASES (1 sentence, under 12 words) the character says before a fight. Match their personality — a sincere warm character speaks very differently from an ironic cynical one. No quotes around them.

Return JSON only. No markdown.
{"suggestions":["one","two","three"]}`;
  const prompt = `You're writing for a fantasy battle card. Character info:
- Name: ${character.name || "unnamed"}
- Size: ${character.size || "medium"} · Habitat: ${character.habitat || "anywhere"}${character.age ? ` · Age: ${character.age}` : ""}
- Personality: ${persona}
- Stats out of 10 — STR ${character.stats?.STR ?? "?"}, SPD ${character.stats?.SPD ?? "?"}, DUR ${character.stats?.DUR ?? "?"}, INT ${character.stats?.INT ?? "?"}, CUN ${character.stats?.CUN ?? "?"}, WIL ${character.stats?.WIL ?? "?"}
${sig}
${tagline}

${targetCopy}`;
  try {
    const text = await callClaude(prompt);
    const parsed = parseJSON(text);
    if (!Array.isArray(parsed.suggestions)) return [];
    const list = parsed.suggestions.slice(0, 3);
    if (isSignature) {
      return list.filter(s => s && typeof s === "object" && s.name && s.effect);
    }
    return list;
  } catch(e) { return []; }
};

// ─── OC battle engine (deterministic; Claude narrates around the math) ─────
// Each character has HP (raw durability, 1-500), height (cm), weight (kg), and lists of
// abilities + physical features. The score function combines all of those.
const ocHp = (c) => Math.max(20, Math.min(500, Math.round(c?.hp ?? 200)));

// Fight score: HP + abilities (each carries real weight) + features + size proxies.
// Designed so a 'super strong' character (max HP, many abilities, big) >> 'super weak' (low HP, no abilities, small).
// opts.weaponBonus: number > 1 multiplies score (e.g. 1.20 for a sword). opts.armed (legacy bool) maps to 1.15.
const ocFightScore = (c, opts = {}) => {
  const hp     = ocHp(c);
  const abils  = Array.isArray(c?.abilities) ? c.abilities.length : 0;
  const feats  = Array.isArray(c?.features)  ? c.features.length  : 0;
  const height = Number(c?.height) || 170;
  const weight = Number(c?.weight) || 70;
  let score = (hp / 50)                              // 0.4 — 10.0 from HP
            + abils * 2.0                            // each ability = +2
            + feats * 1.0                            // each feature = +1
            + Math.max(-3, Math.min(8, (height - 150) / 25))  // height adds reach
            + Math.max(-3, Math.min(10, (weight - 60) / 30)); // weight adds force
  const bonus = opts.weaponBonus && opts.weaponBonus > 1 ? opts.weaponBonus : (opts.armed ? 1.15 : 1);
  score *= bonus;
  return Math.max(1, score);
};

const ocPersonalityMods = (c) => {
  const p = c?.personality || {};
  const mods = [];
  if (p.confidence >= 8) mods.push({ delta: -3, why: "too cocky — overconfidence hurts" });
  if (p.confidence <= 3) mods.push({ delta: -3, why: "too anxious — freezes when it counts" });
  if (p.optimism <= 3)   mods.push({ delta: +2, why: "cynical — already expected the worst" });
  if (p.earnestness <= 3) mods.push({ delta: +2, why: "ironic — hard to read" });
  if (p.humor >= 8)      mods.push({ delta: -2, why: "too funny — distracted by the bit" });
  return mods;
};

const OC_HOME_FIELD_BONUS = 5;

// Compute final odds via a power-bracket so mismatched fighters get decisive odds — strong
// characters almost always win against weak ones. Miracles still happen (clamp 3%-97%).
const ocOdds = (a, b, opts = {}) => ocOddsBreakdown(a, b, opts).oddsA;

const ocOddsBreakdown = (a, b, opts = {}) => {
  const sa = ocFightScore(a, { armed: opts.aArmed, weaponBonus: opts.aWeaponBonus });
  const sb = ocFightScore(b, { armed: opts.bArmed, weaponBonus: opts.bWeaponBonus });
  const k = 1.9; // amplifies gaps — a 4x score advantage becomes a ~93% odds
  const raw = Math.pow(sa, k) / (Math.pow(sa, k) + Math.pow(sb, k));
  const aMods = ocPersonalityMods(a);
  const bMods = ocPersonalityMods(b);
  const aModTotal = aMods.reduce((s, m) => s + m.delta, 0);
  const bModTotal = bMods.reduce((s, m) => s + m.delta, 0);
  const aHome = opts.aIsHome === true;
  const bHome = opts.bIsHome === undefined && opts.aIsHome === undefined ? true : opts.bIsHome === true;
  const homeFieldA = aHome ? OC_HOME_FIELD_BONUS : 0;
  const homeFieldB = bHome ? OC_HOME_FIELD_BONUS : 0;
  // Clamp 0.03 - 0.97: miracles still possible, but a real mismatch should read as a mismatch.
  const final = Math.max(0.03, Math.min(0.97, raw + (aModTotal - bModTotal + homeFieldA - homeFieldB) / 100));
  return { oddsA: final, oddsB: 1 - final, aMods, bMods, homeFieldA, homeFieldB, homeField: homeFieldB, scoreA: sa, scoreB: sb };
};

// Run a fight: outcome + per-beat HP trajectory (5 beats). All math, no narration.
// opts.aIsHome / opts.bIsHome / opts.aArmed / opts.bArmed are per-fighter flags.
// opts.strategyBump: percentage points added to a's odds (used on rematches when the user picks a strategy).
const simulateOcBattle = (a, b, opts = {}) => {
  const aIsHome = opts.aIsHome === true;
  const bIsHome = opts.bIsHome !== undefined ? opts.bIsHome === true : !!(b && b.universe);
  const baseOddsA = ocOdds(a, b, { aIsHome, bIsHome, aArmed: opts.aArmed, bArmed: opts.bArmed, aWeaponBonus: opts.aWeaponBonus, bWeaponBonus: opts.bWeaponBonus });
  const strategyDelta = (opts.strategyBump || 0) / 100;
  const oddsA = Math.max(0.03, Math.min(0.97, baseOddsA + strategyDelta));
  const aWins = Math.random() < oddsA;
  const winner = aWins ? a : b, loser = aWins ? b : a;
  const winnerHp = ocHp(winner), loserHp = ocHp(loser);
  // Margin: 0 = coin flip, 0.5 = total blowout. Closer fight = winner takes more damage.
  const margin = Math.abs(oddsA - 0.5);
  const winnerDamageRatio = Math.max(0.25, Math.min(0.85, 0.85 - margin * 1.4));
  const winnerDamageTotal = Math.round(winnerHp * winnerDamageRatio);
  // 5 beats. Damage distribution weighted toward the back half + jittered.
  const distribute = (total) => {
    const weights = [0.14, 0.18, 0.20, 0.22, 0.26].map(w => Math.max(0.05, w + (Math.random()-0.5)*0.1));
    const sum = weights.reduce((x,y)=>x+y, 0);
    const dmg = weights.map(w => Math.round(total * w / sum));
    // Fix rounding so it sums exactly.
    const diff = total - dmg.reduce((x,y)=>x+y, 0);
    dmg[dmg.length-1] += diff;
    return dmg;
  };
  const loserDmg  = distribute(loserHp);
  const winnerDmg = distribute(winnerDamageTotal);
  const aDmg = aWins ? winnerDmg : loserDmg;
  const bDmg = aWins ? loserDmg  : winnerDmg;
  // Build HP trajectories (length 6: starting HP + 5 beats).
  const aHpStart = ocHp(a), bHpStart = ocHp(b);
  const aHp = [aHpStart], bHp = [bHpStart];
  let ah = aHpStart, bh = bHpStart;
  for (let i = 0; i < 5; i++) {
    ah = Math.max(0, ah - aDmg[i]); aHp.push(ah);
    bh = Math.max(0, bh - bDmg[i]); bHp.push(bh);
  }
  // Force loser to exactly 0 at end so it reads cleanly.
  if (aWins) bHp[5] = 0; else aHp[5] = 0;
  // Duration: more decisive fights end faster. Range ~45s to ~5min, with jitter.
  const durationSeconds = Math.round(40 + (1 - margin*2) * 200) + Math.floor(Math.random() * 80);
  return { winner: aWins ? "a" : "b", oddsA, margin, aHp, bHp, durationSeconds };
};

// Ask Claude to generate a fantasy opponent's OC-shaped sheet based on canon.
// Returns { ok: true, data } on success, { ok: false, error } with a real message on failure.
const getOcFantasyOpponent = async (charName, universe) => {
  const prompt = `Generate a fantasy battle stat sheet for ${charName} from ${universe}.

GUIDELINES:
- Be HONEST to canon. Strong characters get high HP (close to 500). Frail or kid characters get low HP (50-150). Average humans land around 150-250.
- Pick abilities and physical features that match the character. Don't invent powers they don't have. If they're just a regular human, return an empty abilities array.
- If the character is obscure or you can't place them, do your best guess based on the universe.

FIELD VALUES:
- hp: integer 20-500. Reflects durability AND raw power capacity. Hulk = 500. Hermione = 180. Hagrid = 380.
- height: cm. Realistic for the character.
- weight: kg. Realistic.
- gender: "Female", "Male", "Non-binary", or "Other / skip"
- habitat: best canon fit from "Forest", "Mountain", "Sea", "Sky", "Urban", "Desert", "Underworld", "Cosmic", "Suburbs", "Carnival"
- habitatIcon: matching emoji from 🌲 ⛰ 🌊 ☁ 🏙 🏜 🔥 ✨ 🏚 🎪
- age: integer, reasonable canon age
- abilities: array of strings from this list — pick ones that match canon. Possible: ${OC_ABILITIES.map(s => `"${s}"`).join(", ")}. Use 0-6 entries.
- features: array of physical features the character canonically has. Possible: ${OC_FEATURES.map(s => `"${s}"`).join(", ")}. Use 0-4 entries.
- personality keys: warmth (cold→warm), humor (serious→funny), energy (calm→chaotic), confidence (anxious→cocky), optimism (cynical→hopeful), earnestness (ironic→sincere). Integers 1-10.

Return JSON only. No markdown. Match this exact shape:
{
  "name": "Canonical name (fix typos)",
  "universe": "Canonical universe (fix typos)",
  "tagline": "Short tabloid-voice descriptor, 4-8 words",
  "hp": 200,
  "height": 175,
  "weight": 70,
  "gender": "Female",
  "habitat": "Forest",
  "habitatIcon": "🌲",
  "age": 25,
  "abilities": [],
  "features": [],
  "personality": {"warmth": 5, "humor": 5, "energy": 5, "confidence": 5, "optimism": 5, "earnestness": 5},
  "catchphrase": "Short canon-voice line they'd say before a fight"
}`;
  try {
    const text = await callClaude(prompt);
    const data = parseJSON(text);
    if (!data || typeof data !== "object" || !data.hp) {
      return { ok: false, error: "Got a response but no stats — try again." };
    }
    // Default missing fields just in case.
    if (!Array.isArray(data.abilities)) data.abilities = [];
    if (!Array.isArray(data.features))  data.features  = [];
    return { ok: true, data };
  } catch(e) {
    console.error("getOcFantasyOpponent failed:", e);
    const msg = (e && e.message) ? String(e.message) : "Network or parse error.";
    return { ok: false, error: msg };
  }
};

// Narrate a pre-determined fight outcome. The math is locked; Claude just writes the beats.
const getOcBattleNarration = async (a, b, sim, opts = {}) => {
  const strategy = typeof opts === "string" ? opts : (opts.strategy || "");
  const aArmed = !!opts.aArmed;
  const bArmed = !!opts.bArmed;
  const aWeapon = opts.aWeapon || "";
  const bWeapon = opts.bWeapon || "";
  const chosenVenue = (opts.chosenVenue || "").trim();
  // Randomly choose grumble vs hype mode for the opening — keeps replay value fresh.
  const openingMode = "neutral";
  const winnerName = sim.winner === "a" ? a.name : b.name;
  const loserName  = sim.winner === "a" ? b.name : a.name;
  const aPersona = describeOcPersonality(a.personality);
  const bPersona = describeOcPersonality(b.personality);
  const fighterLines = (c, armed, weapon) => {
    const abils = (c.abilities && c.abilities.length) ? c.abilities.join(", ") : "no special abilities";
    const feats = (c.features && c.features.length) ? c.features.join(", ") : "no notable physical features";
    const gender = c.gender ? `, ${c.gender}` : "";
    const ageStr = c.age ? `, age ${c.age}` : "";
    const back = c.backstory ? `\n- Backstory: ${c.backstory}` : "";
    const weaponDesc = armed ? (weapon ? `${weapon}` : "yes (unspecified)") : "no";
    return `- ${inToFtIn(cmToIn(c.height ?? 170))} (${cmToIn(c.height ?? 170)} in), ${kgToLb(c.weight ?? 70)} lb${gender}${ageStr} · Habitat: ${c.habitat}
- HP: ${ocHp(c)} (max ${c.hp || 200})
- Abilities: ${abils}
- Physical features: ${feats}
- Personality: ${aPersona && c === a ? aPersona : bPersona}
- Weapon: ${weaponDesc}
- Catchphrase: "${c.catchphrase}"${back}`;
  };
  const beatLine = (i) => `Beat ${i+1}: ${a.name} HP ${sim.aHp[i+1]}/${sim.aHp[0]} (took ${sim.aHp[i]-sim.aHp[i+1]} damage). ${b.name} HP ${sim.bHp[i+1]}/${sim.bHp[0]} (took ${sim.bHp[i]-sim.bHp[i+1]} damage).`;
  const prompt = `You are writing a Cosmopolitan magazine tabloid fight column. Two fighters, pre-determined outcome (the math is locked) — your job is to write beat-by-beat narration that MATCHES the numbers.

FIGHTER A — ${a.name}${a.tagline ? ` ("${a.tagline}")` : ""}
${fighterLines(a, aArmed, aWeapon)}

FIGHTER B — ${b.name}${b.tagline ? ` ("${b.tagline}")` : ""}
${fighterLines(b, bArmed, bWeapon)}

OUTCOME (locked):
- Winner: ${winnerName}. Loser: ${loserName}.
- Duration: ${sim.durationSeconds} seconds.
- HP trajectory across 5 beats (you MUST match the damage to each beat's narration):
  ${[0,1,2,3,4].map(beatLine).join("\n  ")}
${strategy ? `\nREADER'S STRATEGY THIS REMATCH: ${a.name} is trying this: "${strategy}". Show them attempting it — make the strategy visible in at least 2 beats.\n` : ""}
${chosenVenue ? `VENUE: The user picked the location for this fight. Use this venue exactly: "${chosenVenue}". Write the 'environment' line around this venue — describe it in 1-2 sentences. Do not relocate the fight to anywhere else.\n\n` : ""}PRE-FIGHT REMARK: ${loserName} (or either fighter, your choice) says something to camera before the bell. Could be a quip, a calm observation, a quiet warning, a question, a one-liner, a self-aware aside, an in-character bit — whatever fits their personality. NOT a complaint about wanting to skip the fight and NOT trash talk / bravado specifically. Just a real-feeling pre-fight remark in their voice. 1-2 sentences. No drinking / alcohol references.

WRITE the beat-by-beat:
- 5 short tabloid beats, in order. Each ~1 sentence. The damage in each beat should match what your narration suggests — small damage = glancing, big damage = solid hit.
- Reference at least 2-3 of the fighters' abilities AND at least 1 physical feature each over the course of the fight.
- Voice their personalities (a Sincere Warm fighter sounds nothing like an Ironic Cynical one).
- Match the location to a habitat that fits one of them.
- The final beat shows ${winnerName} putting ${loserName} at 0 HP.
- If a fighter has a weapon (named in their info above), name-check the weapon in at least one beat and use it correctly — a Sword swings, a Gun fires, a Magic wand casts.
- If one side is armed and the other isn't, that gap should show up in the action.

VARIETY: Don't always start with the entry. Vary opening beats. Vary winner-quote tone — sometimes triumphant, sometimes deadpan, sometimes a one-word burn. Avoid 'I underestimated you' or 'Better luck next time'.

CONTENT RATING: PG. Bruises, sprains, wounded pride. No gore. Don't reach for drinking / alcohol / hangover jokes in any quote or beat — kid-friendly.

Return JSON only. No markdown.
{
  "pre_fight_remark_speaker": "${loserName} or ${winnerName} — pick whoever the remark fits better",
  "pre_fight_remark": "What that fighter says to camera before the bell. 1-2 sentences, in their voice. Not a complaint, not pure trash talk — a real pre-fight moment.",
  "environment": "1-2 sentence scene-set — where the fight is happening, why it fits one of them.",
  "fight_caption": "Magazine-style caption describing a specific MOMENT FROM THE FIRST OR SECOND BEAT of the play_by_play — that's roughly what the fight photo will depict (a close-up of the opening exchange). 8-15 words, punchy, present-tense. Reference at least one specific physical thing happening in that beat — a swing, a duck, a clash, a feature being used. NOT a generic summary. Example for a beat that says 'Sparkle ducks left and lobs a glitter bomb that catches Wolverine mid-swing': 'Wolverine's claws swing through the spray as Sparkle's glitter bomb detonates inches away.'",
  "play_by_play": ["beat 1","beat 2","beat 3","beat 4","beat 5"],
  "winner_quote": "What ${winnerName} says walking off — match their personality.",
  "loser_quote": "What ${loserName} mutters or yells — match their personality.",
  "time_narrative": "Short color line about the duration. Varied phrasing.",
  "condition_of_loser": "One funny sentence about ${loserName}'s state after.",
  "rematch_strategies": ["three specific tactical suggestions for ${a.name}'s rematch"]
}`;
  try {
    const text = await callClaude(prompt);
    return parseJSON(text);
  } catch(e) { return null; }
};

// Arena: single streaming pick based on the opponent + universe (where to watch them).
const getArenaStreaming = async (arenaResult) => {
  const ch = arenaResult?.character || "";
  const uni = arenaResult?.universe || "";
  if (!ch || !uni) return null;
  const prompt = `Someone just simulated a fight with ${ch} from ${uni}. Recommend the single best show or movie to watch next — usually the work where ${ch} actually appears. If the universe spans multiple entries, pick the most iconic / accessible starting point.

Return JSON only. No markdown.
{"title":"Show or Movie Title","why":"One sentence on why to watch","service":"Netflix/HBO Max/Hulu/Disney+/Apple TV+/Amazon Prime Video/Peacock/Paramount+/Starz","genre":"Genre tag"}`;
  try {
    const text = await callClaude(prompt);
    return parseJSON(text);
  } catch(e) { return null; }
};

// Loading screen flavor lines — pools rolled once per loading entry for replay value.
const LOADING_LINES_1 = [
  "The matchmaker is working her magic.",
  "The matchmaker is consulting her little black book.",
  "The matchmaker is on a long, expensive phone call.",
  "The matchmaker is pulling the receipts.",
];
const LOADING_LINES_2 = [
  "Brace yourself. This one's a doozy.",
  "We'd say sit down, but we know you won't.",
  "You're going to want to screenshot this.",
  "Cancel your plans tonight.",
  "Even we didn't see this coming.",
  "Hold our coffee.",
  "Get ready to gasp.",
  "The receipts are juicy.",
];

const ARENA_LOADING_LINES_1 = [
  "The ref is checking gloves.",
  "Ringside is on the edge of their seats.",
  "Bookies are taking last-minute bets.",
  "The crowd is on their feet.",
  "Cornermen are huddled tight.",
];
const ARENA_LOADING_LINES_2 = [
  "Bell about to ring.",
  "Touch gloves and back to your corners.",
  "Last chance to back out.",
  "Hope you stretched.",
  "No referees were harmed.",
  "You signed the waiver, right?",
];

const FEAST_LOADING_LINES_1 = [
  "The cooks are loading the table.",
  "The judges are sharpening pencils.",
  "The stopwatches are wound.",
  "The mustard is on standby.",
  "Trays coming out hot.",
  "The crowd is on its feet, napkins ready.",
];
const FEAST_LOADING_LINES_2 = [
  "Hope you didn't eat lunch.",
  "Untuck your shirt.",
  "Pace yourself. Or don't.",
  "No throwing up at the table.",
  "Bibs are non-optional.",
  "Don't say we didn't warn you.",
];

// ─── TABLOID HELPERS ────────────────────────────────────────────────────────
const generateCaseNo = () => {
  const r = () => String(Math.floor(10 + Math.random()*89));
  return `${r()}.${r()}.${r()}`;
};

const generateHeadline = (name) => {
  const N = (name || "Anonymous").toUpperCase();
  // Three-line tabloid reveal headline, focused on the matches.
  return [`${N}'S`, "FANTASY THREE,", "REVEALED"];
};

// Identify the two lowest-scoring axes — they get the red hatched bars.
const findLowAxes = (profile) => {
  const sorted = EVIDENCE.map(e => ({ key: e.key, val: profile[e.key] })).sort((a, b) => a.val - b.val);
  return new Set([sorted[0].key, sorted[1].key]);
};

// ─── REUSABLE TABLOID COMPONENTS ────────────────────────────────────────────
const Masthead = ({ no = "1", subtitle, compact = false, title = "MARRY. KISS. KILL." }) => (
  <div style={{ borderTop:`3px double ${C.ink}`, borderBottom:`3px double ${C.ink}`, padding: compact ? "8px 0 6px" : "12px 0 10px", marginBottom:"18px", textAlign:"center" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"0 4px", marginBottom:"4px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1px" }}>
      <span>VOL. I</span>
      <span style={{ color:C.red, letterSpacing:"4px" }}>★ ★ ★</span>
      <span>NO. {no}</span>
    </div>
    <h1 style={{ margin:"2px 0 0", fontFamily:FONT.display, fontWeight:900, fontSize: compact ? "26px" : "38px", lineHeight:1, letterSpacing:"-0.5px" }}>{title}</h1>
    {subtitle && <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", marginTop:"8px" }}>— {subtitle} —</div>}
  </div>
);

const RuleHr = ({ double = false, mb = "14px", mt = "0" }) => (
  <div style={{ borderTop: double ? `3px double ${C.ink}` : `1px solid ${C.ink}`, marginTop: mt, marginBottom: mb }} />
);

const SectionHeader = ({ title, suffix }) => (
  <div style={{ borderBottom:`1px solid ${C.ink}`, paddingBottom:"6px", marginBottom:"14px", display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:"8px" }}>
    <h3 style={{ margin:0, fontFamily:FONT.display, fontSize:"20px", fontWeight:900, letterSpacing:"0.5px", textTransform:"uppercase" }}>{title}</h3>
    {suffix && <span style={{ fontFamily:FONT.mono, fontSize:"10px", color:C.inkSoft, textTransform:"uppercase", letterSpacing:"1px", whiteSpace:"nowrap" }}>— {suffix} —</span>}
  </div>
);

const Stamp = ({ children, rotate = -4, color = C.red, size = "11px" }) => (
  <span style={{ display:"inline-block", border:`2px solid ${color}`, color, padding:"4px 10px 3px", fontFamily:FONT.mono, fontSize:size, fontWeight:700, letterSpacing:"2px", transform:`rotate(${rotate}deg)`, textTransform:"uppercase", lineHeight:1.2, whiteSpace:"nowrap" }}>{children}</span>
);

const MetaRow = ({ left, right, border = true }) => (
  <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"11px", textTransform:"uppercase", letterSpacing:"1px", padding:"8px 0", borderTop: border ? `1px solid ${C.ink}` : "none" }}>
    <span>{left}</span><span>{right}</span>
  </div>
);

const HatchedBar = ({ label, score, exhibit, status, isLow }) => {
  const fill = isLow ? C.red : C.ink;
  return (
    <div style={{ marginBottom:"18px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"5px" }}>
        <span style={{ fontFamily:FONT.display, fontSize:"17px", fontWeight:700 }}>{label}</span>
        <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"24px", color: isLow ? C.red : C.ink, lineHeight:1 }}>{score}%</span>
      </div>
      <div style={{ height:"14px", border:`1px solid ${C.ink}`, position:"relative", background:C.paperSoft }}>
        <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${score}%`, background:hatched(fill), borderRight: score < 100 ? `1px solid ${fill}` : "none" }} />
      </div>
      <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.2px", textTransform:"uppercase", color:C.inkSoft, marginTop:"5px" }}>
        EXHIBIT {exhibit} — {status}
      </div>
    </div>
  );
};

const BlackCallout = ({ header, title, body }) => (
  <div style={{ background:C.black, color:C.paper, padding:"22px 20px", border:`3px solid ${C.red}`, textAlign:"center", marginBottom:"24px" }}>
    <div style={{ color:C.red, fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"3px", textTransform:"uppercase", marginBottom:"10px" }}>★ {header} ★</div>
    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"26px", lineHeight:1.1, marginBottom:"10px" }}>{title}</div>
    <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.5, fontStyle:"italic", color:"rgba(241,232,210,0.7)" }}>{body}</p>
  </div>
);

const PullQuote = ({ quote, attribution }) => (
  <div style={{ textAlign:"center", padding:"24px 12px 22px", marginBottom:"22px" }}>
    <div style={{ color:C.red, fontSize:"36px", fontFamily:FONT.display, lineHeight:1, marginBottom:"2px", letterSpacing:"-2px" }}>❝❞</div>
    <p style={{ margin:"0 0 12px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"22px", lineHeight:1.3, fontWeight:500 }}>{quote}</p>
    <div style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.ink }}>— {attribution}</div>
  </div>
);

const TabloidButton = ({ onClick, children, disabled = false, variant = "primary", type = "button" }) => {
  const isPrimary = variant === "primary";
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      width:"100%", padding:"15px 16px",
      border:`3px solid ${C.ink}`,
      background: disabled ? C.paperDark : (isPrimary ? C.red : C.paper),
      color:      disabled ? C.inkMute  : (isPrimary ? C.paper : C.ink),
      fontFamily:FONT.mono, fontSize:"13px", fontWeight:700, letterSpacing:"2.5px", textTransform:"uppercase",
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "none" : `4px 4px 0 ${C.ink}`,
      transition:"transform 0.1s, box-shadow 0.1s",
      lineHeight:1.2,
    }}
    onMouseDown={e => { if(!disabled){ e.currentTarget.style.transform="translate(2px,2px)"; e.currentTarget.style.boxShadow=`2px 2px 0 ${C.ink}`; }}}
    onMouseUp={e => { if(!disabled){ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=`4px 4px 0 ${C.ink}`; }}}
    onMouseLeave={e => { if(!disabled){ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=`4px 4px 0 ${C.ink}`; }}}
    >{children}</button>
  );
};

const TextLink = ({ onClick, children }) => (
  <button onClick={onClick} style={{ background:"none", border:"none", color:C.ink, fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", cursor:"pointer", textDecoration:"underline", padding:"10px" }}>{children}</button>
);

const Label = ({ children }) => (
  <label style={{ display:"block", fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkSoft, marginBottom:"8px", fontWeight:500 }}>{children}</label>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <input type="text" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} style={{
    width:"100%", padding:"12px 14px",
    border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
    fontFamily:FONT.display, fontSize:"17px", outline:"none",
  }} />
);

const Pill = ({ selected, onClick, children, size = "md" }) => (
  <button onClick={onClick} style={{
    background: selected ? C.ink : "transparent",
    color:      selected ? C.paper : C.ink,
    border:`1.5px solid ${C.ink}`,
    padding: size === "sm" ? "8px 12px" : "10px 14px",
    fontFamily:FONT.mono, fontSize: size === "sm" ? "11px" : "12px",
    letterSpacing:"1.5px", textTransform:"uppercase", fontWeight:600,
    cursor:"pointer", textAlign:"left",
  }}>{children}</button>
);

const PageFooter = () => (
  <div style={{ borderTop:`3px double ${C.ink}`, paddingTop:"14px", marginTop:"32px", textAlign:"center", fontFamily:FONT.mono, fontSize:"9.5px", letterSpacing:"1.8px", textTransform:"uppercase", lineHeight:1.7, color:C.inkSoft }}>
    MARRYKISSKILL.COM · NO REFUNDS ON FANTASIES · TAKE IT AGAIN ANYTIME
  </div>
);

const LoadingDots = () => (
  <div style={{ display:"flex", gap:"8px", justifyContent:"center", padding:"16px" }}>
    {[0,1,2].map(i => <div key={i} style={{ width:"10px", height:"10px", background:C.red, animation:"db 1.2s infinite", animationDelay:`${i*0.2}s` }}/>)}
    <style>{`@keyframes db{0%,80%,100%{transform:translateY(0);opacity:0.4}40%{transform:translateY(-10px);opacity:1}}`}</style>
  </div>
);

// ─── MATCH EXHIBIT CARD ─────────────────────────────────────────────────────
const MatchCard = ({ matchType, match, tmdbImage, idx }) => {
  const cfg = MATCH_CONFIG[matchType];

  // Per-type visual treatment — Marry is elegant, Kiss is red-hot, Kill is danger tape.
  const isMarry = matchType === "marry";
  const isKiss  = matchType === "screw";
  const isKill  = matchType === "kill";

  const headerBg     = isKiss ? C.red : C.ink;
  const headerLabel  = isKill
    ? <span style={{ color:C.red }}>{cfg.label}</span>
    : cfg.label;
  const headerScoreColor = isKill ? C.red : "rgba(241,232,210,0.55)";

  const whyLabel       = isMarry ? "WHY YOU TWO"     : isKiss ? "THE CHEMISTRY"  : "THE PROBLEM";
  const twistLabel     = isMarry ? "★ PLOT TWIST"    : isKiss ? "★ FAIR WARNING" : "★ DO NOT ENGAGE";
  const twistBodyColor = isKill ? C.red : C.paper;
  const openingLabel   = isMarry ? "YOUR OPENING LINE" : isKiss ? "YOUR PICK-UP LINE" : "YOUR PARTING SHOT";
  const openingDash    = (isKiss || isKill) ? C.red : C.ink;
  const scoreColor     = isKill ? C.red : C.ink;

  return (
    <div style={{ border:`2px solid ${C.ink}`, background:C.paper, marginBottom:"22px", position:"relative" }}>
      {/* Red+ink caution tape for KILL */}
      {isKill && (
        <div style={{ height:"10px", background:`repeating-linear-gradient(45deg, ${C.red} 0 10px, ${C.ink} 10px 20px)`, borderBottom:`1.5px solid ${C.ink}` }}/>
      )}

      {/* Header strip */}
      <div style={{ background:headerBg, color:C.paper, padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase" }}>★ #{idx}</span>
        <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px", letterSpacing:"4px" }}>{headerLabel}</span>
        <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:headerScoreColor }}>{match.score}%</span>
      </div>

      {/* Photo / portrait region */}
      {tmdbImage && (
        <div style={{ height:"140px", overflow:"hidden", borderBottom:`1px solid ${C.ink}`, position:"relative" }}>
          <img
            src={tmdbImage}
            alt=""
            style={{
              width:"100%", height:"100%", objectFit:"cover",
              filter: isKill  ? "grayscale(100%) contrast(1.2) brightness(0.78)"
                    : isKiss  ? "grayscale(40%) contrast(1.1) sepia(0.15)"
                    :           "grayscale(85%) contrast(1.1)",
            }}
          />
          <div style={{ position:"absolute", inset:0, background: isKiss ? "linear-gradient(180deg, rgba(200,54,45,0.10), rgba(15,15,15,0.35))" : "linear-gradient(180deg, rgba(241,232,210,0.05), rgba(15,15,15,0.40))" }}/>
          <div style={{ position:"absolute", bottom:"6px", left:"10px", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.paper, textShadow:"0 1px 4px rgba(0,0,0,0.7)" }}>★ AS SEEN IN · {match.universe}</div>
        </div>
      )}

      {/* Character + score block */}
      <div style={{ padding:"16px 18px 12px", display:"flex", alignItems:"flex-end", gap:"14px", borderBottom:`1px solid ${C.ink}` }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"4px" }}>NOW STARRING</div>
          <div style={{ fontFamily:FONT.display, fontSize:"24px", fontWeight:900, lineHeight:1.05 }}>{match.character}</div>
          <div style={{ fontFamily:FONT.display, fontSize:"13px", fontStyle:"italic", color:C.inkSoft, marginTop:"2px" }}>of {match.universe}</div>
          <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginTop:"8px" }}>★ {cfg.verdict}</div>
        </div>
        <div style={{ textAlign:"right", flexShrink:0 }}>
          <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"42px", color:scoreColor, lineHeight:1 }}>{match.score}%</div>
          <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute }}>{cfg.scoreLabel}</div>
        </div>
      </div>

      {/* Why */}
      <div style={{ padding:"14px 18px 0" }}>
        <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"4px" }}>{whyLabel}</div>
        <p style={{ margin:0, fontFamily:FONT.display, fontSize:"16px", lineHeight:1.5, color:C.ink }}>{match.why}</p>
      </div>

      {/* Warning — black callout */}
      <div style={{ padding:"14px 18px" }}>
        <div style={{ background:C.black, color:twistBodyColor, padding:"12px 14px", borderLeft:`4px solid ${C.red}` }}>
          <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginBottom:"4px" }}>{twistLabel}</div>
          <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.5 }}>{match.warning}</p>
        </div>
      </div>

      {/* Opening line — dashed quote box */}
      <div style={{ padding:"0 18px 18px" }}>
        <div style={{ border:`1.5px dashed ${openingDash}`, padding:"14px 16px", background:C.paperSoft }}>
          <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color: openingDash === C.red ? C.red : C.inkMute, marginBottom:"4px" }}>{openingLabel}</div>
          <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", lineHeight:1.5, color:C.ink }}>“{match.opening_line}”</p>
        </div>
      </div>
    </div>
  );
};

// ─── INSTAGRAM SHARE CARD — TABLOID FRONT PAGE ──────────────────────────────
const ShareCard = ({ results, name, tmdbImages, caseNo, onClose }) => {
  const types = ["marry","screw","kill"];
  const copy = () => {
    const t = `MARRY. KISS. KILL. · Starring: ${name||"Anonymous"}\n\n`+
      types.map(t => `${MATCH_CONFIG[t].label}: ${results[t].character} (${results[t].universe})`).join("\n")+
      `\n\nFind your three → marrykisskill.com`;
    navigator.clipboard?.writeText(t).catch(()=>{});
    alert("Caption copied. Screenshot the card above and post.");
  };

  const shareLines = generateHeadline(name);

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(10,10,10,0.94)", zIndex:1000, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start", padding:"12px 16px", overflowY:"auto" }}>
      <div style={{ width:"100%", maxWidth:"320px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px" }}>
          <span style={{ color:"rgba(241,232,210,0.55)", fontSize:"11px", fontFamily:FONT.mono, letterSpacing:"1.5px", textTransform:"uppercase" }}>Screenshot to share</span>
          <button onClick={onClose} style={{ background:"transparent", border:`1.5px solid ${C.paper}`, color:C.paper, width:"30px", height:"30px", cursor:"pointer", fontSize:"16px", fontFamily:FONT.mono }}>×</button>
        </div>

        {/* Tabloid front-page card (9:16) */}
        <div style={{ background:C.paper, color:C.ink, aspectRatio:"9/16", padding:"18px 16px", display:"flex", flexDirection:"column", border:`4px solid ${C.ink}`, position:"relative" }}>
          {/* Masthead */}
          <div style={{ borderTop:`3px double ${C.ink}`, borderBottom:`3px double ${C.ink}`, padding:"6px 0 4px", textAlign:"center", marginBottom:"10px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1px", marginBottom:"2px" }}>
              <span>VOL. I</span><span style={{color:C.red}}>★ ★ ★</span><span>NO. 1</span>
            </div>
            <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1 }}>MARRY. KISS. KILL.</div>
            <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"2px", textTransform:"uppercase", marginTop:"4px" }}>— THE FANTASY EDITION —</div>
          </div>

          <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1px", textTransform:"uppercase", marginBottom:"10px" }}>
            <span>STARRING: {(name||"ANONYMOUS").toUpperCase()}</span><span>ISSUE #{caseNo}</span>
          </div>

          {/* Headline */}
          <div style={{ textAlign:"center", marginBottom:"10px" }}>
            <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, marginBottom:"3px" }}>★ EXCLUSIVE · THE BIG REVEAL ★</div>
            <h2 style={{ margin:0, fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>
              {shareLines.map((line, i) => (
                <React.Fragment key={i}>
                  {i === shareLines.length - 1
                    ? <span style={{ fontStyle:"italic", color:C.red }}>{line}</span>
                    : <>{line}<br/></>}
                </React.Fragment>
              ))}
            </h2>
          </div>

          {/* Three exhibits */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:"6px", marginBottom:"8px" }}>
            {types.map((type, i) => {
              const m = results[type], cfg = MATCH_CONFIG[type];
              const bg = tmdbImages?.[type];
              return (
                <div key={type} style={{ border:`1.5px solid ${C.ink}`, padding:"6px 8px", background: bg ? "transparent" : C.paperSoft, position:"relative", overflow:"hidden", display:"flex", alignItems:"center", gap:"8px" }}>
                  {bg && <img src={bg} alt="" style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", filter:"grayscale(100%)", opacity:0.18 }}/>}
                  <div style={{ position:"relative", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, width:"38px" }}>EX. {i+1}</div>
                  <div style={{ position:"relative", flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"11px", letterSpacing:"2px" }}>{cfg.label}</div>
                    <div style={{ fontFamily:FONT.display, fontSize:"13px", fontWeight:700, lineHeight:1.1 }}>{m.character}</div>
                    <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"10px", color:C.inkSoft, lineHeight:1.2 }}>of {m.universe}</div>
                  </div>
                  <div style={{ position:"relative", fontFamily:FONT.display, fontWeight:900, fontSize:"18px", color: type==="kill" ? C.red : C.ink }}>{m.score}%</div>
                </div>
              );
            })}
          </div>

          {/* Pull quote */}
          <div style={{ borderTop:`1px solid ${C.ink}`, paddingTop:"8px", textAlign:"center" }}>
            <div style={{ color:C.red, fontFamily:FONT.display, fontSize:"14px", lineHeight:1 }}>❝❞</div>
            <p style={{ margin:"2px 0 4px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", lineHeight:1.3 }}>Three matches, zero notes.</p>
            <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", textTransform:"uppercase" }}>MARRYKISSKILL.COM</div>
          </div>

          {/* Stamp */}
          <div style={{ position:"absolute", top:"22%", right:"6px" }}>
            <Stamp rotate={8} size="9px">★ JUICY ★</Stamp>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display:"flex", gap:"8px", marginTop:"12px" }}>
          <button onClick={copy} style={{ flex:1, background:C.red, color:C.paper, border:`2px solid ${C.paper}`, padding:"12px", fontFamily:FONT.mono, fontSize:"11px", fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", cursor:"pointer" }}>📋 Copy Caption</button>
          <button onClick={onClose} style={{ flex:1, background:"transparent", color:C.paper, border:`2px solid ${C.paper}`, padding:"12px", fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", cursor:"pointer" }}>Dismiss</button>
        </div>
        <p style={{ color:"rgba(241,232,210,0.4)", fontSize:"9.5px", textAlign:"center", fontFamily:FONT.mono, letterSpacing:"1.5px", textTransform:"uppercase", marginTop:"10px" }}>Screenshot the card → post to Instagram Stories</p>
      </div>
    </div>
  );
};

// Ready-to-rumble countdown shown between Ring The Bell and the fight result.
// Runs a 3 - 2 - 1 - FIGHT! sequence while Claude narrates in the background.
// Hypey cycling loading screen while we look up a fantasy opponent's stat block.
const OC_OPPONENT_LOADING_LINES = [
  "Pulling their file.",
  "Cross-checking the canon.",
  "Is this even legal? Probably.",
  "The ringside reporter is putting on lipstick.",
  "Bookies are setting opening lines.",
  "Stretching out the matchmaker.",
  "Steaming the championship belt.",
  "Counting their canon weapons.",
  "Are you sure you want to do this?",
  "Hope you didn't have plans tonight.",
];
const OcOpponentLoadingScreen = ({ opponentName, opponentUni, challengerName }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % OC_OPPONENT_LOADING_LINES.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ paddingTop:"60px" }}>
      <Masthead title="OC PLAYGROUND." subtitle="WHO ARE WE DEALING WITH" compact />
      <div style={{ textAlign:"center", marginTop:"30px" }}>
        <Stamp rotate={-3} size="12px">★ MATCHMAKING ★</Stamp>
        <h2 style={{ margin:"22px 0 6px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1.05 }}>
          {challengerName
            ? <>WILL {challengerName.toUpperCase()} BEAT<br/>{(opponentName || "THEM").toUpperCase()}?</>
            : <>LOOKING UP<br/>{(opponentName || "THEM").toUpperCase()}.</>}
        </h2>
        {opponentUni && (
          <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.inkSoft, margin:"0 16px 16px" }}>
            of {opponentUni}
          </div>
        )}
        <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", color:C.inkSoft, margin:"6px 16px 8px", minHeight:"22px" }}>
          {OC_OPPONENT_LOADING_LINES[idx]}
        </p>
        <LoadingDots />
      </div>
      <PageFooter />
    </div>
  );
};

const OcRumbleScreen = ({ a, b, narrReady, narrError, onAdvance, onCancel }) => {
  const SEQ = ["3", "2", "1", "FIGHT!"];
  const [stepIdx, setStepIdx] = useState(0);
  const [countdownDone, setCountdownDone] = useState(false);
  useEffect(() => {
    if (stepIdx < SEQ.length - 1) {
      const t = setTimeout(() => setStepIdx(stepIdx + 1), 700);
      return () => clearTimeout(t);
    }
    // Linger on FIGHT! for 600ms before flagging the countdown done.
    const t = setTimeout(() => setCountdownDone(true), 600);
    return () => clearTimeout(t);
  }, [stepIdx]);
  useEffect(() => {
    if (countdownDone && narrReady) onAdvance();
  }, [countdownDone, narrReady, onAdvance]);
  const step = SEQ[stepIdx];
  const isFightStamp = step === "FIGHT!";
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
        <TextLink onClick={onCancel}>← Back</TextLink>
        <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
        <span style={{ width:"60px" }}/>
      </div>
      <Masthead title="OC PLAYGROUND." subtitle="READY TO RUMBLE" compact />

      {/* Face-off row */}
      <div style={{ display:"flex", alignItems:"stretch", gap:"8px", marginBottom:"22px" }}>
        <div style={{ flex:1, background:C.red, color:C.paper, padding:"16px 12px", border:`3px solid ${C.ink}`, boxShadow:`4px 4px 0 ${C.ink}` }}>
          <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.85, marginBottom:"4px" }}>CHALLENGER</div>
          <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, textTransform:"uppercase", letterSpacing:"-0.5px" }}>{a.name}</div>
          {a.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", opacity:0.85, marginTop:"3px" }}>— {a.tagline} —</div>}
        </div>
        <div style={{ alignSelf:"center", fontFamily:FONT.display, fontWeight:900, fontSize:"20px", letterSpacing:"2px", color:C.ink }}>VS</div>
        <div style={{ flex:1, background:C.ink, color:C.paper, padding:"16px 12px", border:`3px solid ${C.ink}`, boxShadow:`4px 4px 0 ${C.red}`, textAlign:"right" }}>
          <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.85, marginBottom:"4px" }}>OPPONENT</div>
          <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, textTransform:"uppercase", letterSpacing:"-0.5px" }}>{b.name}</div>
          {b.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", opacity:0.85, marginTop:"3px" }}>— {b.tagline} —</div>}
        </div>
      </div>

      {/* The countdown */}
      <div style={{ background:C.ink, color:C.paper, padding:"36px 16px", border:`3px solid ${C.ink}`, textAlign:"center", boxShadow:`8px 8px 0 ${C.red}`, marginBottom:"22px" }}>
        <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", color:C.red, fontWeight:700, marginBottom:"12px" }}>★ ON YOUR MARKS ★</div>
        <div style={{
          fontFamily:FONT.display, fontWeight:900,
          fontSize: isFightStamp ? "60px" : "96px",
          lineHeight:1, letterSpacing:"-2px",
          color: isFightStamp ? C.red : C.paper,
          fontStyle: isFightStamp ? "italic" : "normal",
          transition:"all 0.2s ease",
        }}>
          {step}
        </div>
      </div>

      {/* Waiting state if narration not ready when countdown finishes */}
      {countdownDone && !narrReady && !narrError && (
        <div style={{ textAlign:"center", marginBottom:"22px" }}>
          <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", color:C.inkSoft, margin:"0 16px 8px" }}>Ringside reporter writing the play-by-play.</p>
          <LoadingDots />
        </div>
      )}

      {narrError && (
        <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
          <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>SOMETHING WENT WRONG</div>
          <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", color:C.ink }}>{narrError}</p>
        </div>
      )}

      <PageFooter />
    </div>
  );
};

const ArenaShareCard = ({ result, name, tmdbImage, caseNo, variant = "arena", onClose }) => {
  const isFeast = variant === "feast";
  const youWon = (result.winner_role||"").toLowerCase().trim() === "reader";
  const winnerName = youWon ? (name || "YOU") : (result.character || "");
  const loserName  = youWon ? (name || "YOU") : (result.character || "");
  const fmtT = (s) => { const n = Number(s)||0; return n < 60 ? `${n}s` : `${Math.floor(n/60)}m ${n%60}s`; };
  const verb = isFeast
    ? (youWon ? "OUT-ATE" : "GOT OUT-EATEN BY")
    : (youWon ? "TOOK DOWN" : "GOT TAKEN OUT BY");
  const t = `${(name||"I").toUpperCase()} ${verb} ${(result.character||"").toUpperCase()}${isFeast && result.final_count ? "\n🍴 "+result.final_count : ""}${!isFeast && result.time_seconds ? " · "+fmtT(result.time_seconds) : ""}${result.location_pick ? "\n📍 "+result.location_pick : ""}\n\n${isFeast ? "See who'd out-eat YOU" : "See who'd win YOUR fight"} → marrykisskill.com`;
  const copy = () => {
    navigator.clipboard?.writeText(t).catch(()=>{});
    alert("Caption copied. Screenshot the card above and post.");
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(10,10,10,0.94)", zIndex:1000, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start", padding:"12px 16px", overflowY:"auto" }}>
      <div style={{ width:"100%", maxWidth:"320px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px" }}>
          <span style={{ color:"rgba(241,232,210,0.55)", fontSize:"11px", fontFamily:FONT.mono, letterSpacing:"1.5px", textTransform:"uppercase" }}>Screenshot to share</span>
          <button onClick={onClose} style={{ background:"transparent", border:`1.5px solid ${C.paper}`, color:C.paper, width:"30px", height:"30px", cursor:"pointer", fontSize:"16px", fontFamily:FONT.mono }}>×</button>
        </div>

        {/* Fight-card front page (9:16) */}
        <div style={{ background:C.paper, color:C.ink, aspectRatio:"9/16", padding:"18px 16px", display:"flex", flexDirection:"column", border:`4px solid ${C.ink}`, position:"relative" }}>
          {/* Masthead */}
          <div style={{ borderTop:`3px double ${C.ink}`, borderBottom:`3px double ${C.ink}`, padding:"6px 0 4px", textAlign:"center", marginBottom:"10px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1px", marginBottom:"2px" }}>
              <span>VOL. I</span><span style={{color:C.red}}>★ ★ ★</span><span>NO. 1</span>
            </div>
            <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1 }}>{isFeast ? "FANTASY FEAST." : "FANTASY ARENA."}</div>
            <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"2px", textTransform:"uppercase", marginTop:"4px" }}>— {isFeast ? "THE CONTEST EDITION" : "THE FIGHT EDITION"} —</div>
          </div>

          <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1px", textTransform:"uppercase", marginBottom:"10px" }}>
            <span>STARRING: {(name||"ANONYMOUS").toUpperCase()}</span><span>CASE #{caseNo}</span>
          </div>

          {/* Result stamp */}
          <div style={{ textAlign:"center", marginBottom:"8px" }}>
            <div style={{ display:"inline-block", border:`2px solid ${C.red}`, color:C.red, padding:"4px 12px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", fontWeight:700, transform:"rotate(-3deg)" }}>
              ★ {isFeast ? (youWon ? "STILL HUNGRY" : "TAPPED OUT") : (youWon ? "UNDISPUTED" : "KO CONFIRMED")} ★
            </div>
          </div>

          {/* Headline */}
          <div style={{ textAlign:"center", marginBottom:"10px" }}>
            <h2 style={{ margin:0, fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>
              {(name||"I").toUpperCase()}<br/>
              <span style={{ fontStyle:"italic", color:C.red, fontSize:"14px" }}>{isFeast ? (youWon ? "OUT-ATE" : "LOST TO") : (youWon ? "TOOK DOWN" : "LOST TO")}</span><br/>
              {(result.character||"").toUpperCase()}
            </h2>
          </div>

          {/* Opponent image (if available) */}
          {tmdbImage && (
            <div style={{ border:`2px solid ${C.ink}`, marginBottom:"8px", position:"relative" }}>
              <img src={tmdbImage} alt={result.character} style={{ display:"block", width:"100%", height:"110px", objectFit:"cover", filter:"grayscale(20%) contrast(108%)" }} />
              <div style={{ position:"absolute", bottom:"4px", left:"6px", right:"6px", fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.paper, textShadow:"0 1px 4px rgba(0,0,0,0.8)" }}>
                {result.character} · {result.universe}
              </div>
            </div>
          )}

          {/* Stats row */}
          <div style={{ display:"flex", gap:"6px", marginBottom:"8px" }}>
            <div style={{ flex:1, border:`1.5px solid ${C.ink}`, padding:"6px 8px", textAlign:"center" }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute }}>{isFeast ? "DURATION" : "TIME"}</div>
              <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px", color:C.red, lineHeight:1, marginTop:"2px" }}>{result.time_seconds ? fmtT(result.time_seconds) : "?"}</div>
            </div>
            <div style={{ flex:1, border:`1.5px solid ${C.ink}`, padding:"6px 8px", textAlign:"center" }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute }}>REMATCH</div>
              <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px", color:C.red, lineHeight:1, marginTop:"2px" }}>{result.rematch_chance ?? "?"}%</div>
            </div>
          </div>

          {isFeast && result.final_count && (
            <div style={{ border:`1.5px solid ${C.ink}`, padding:"6px 8px", textAlign:"center", marginBottom:"8px" }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute }}>FINAL TALLY</div>
              <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"13px", lineHeight:1.2, marginTop:"2px" }}>{result.final_count}</div>
            </div>
          )}

          {/* Location */}
          {result.location_pick && (
            <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.ink, textAlign:"center", marginBottom:"8px", paddingBottom:"6px", borderBottom:`1px dashed ${C.ink}` }}>
              📍 {result.location_pick}
            </div>
          )}

          {/* Pull quote — winner's line */}
          {result.winner_quote && (
            <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
              <div style={{ color:C.red, fontFamily:FONT.display, fontSize:"14px", lineHeight:1, textAlign:"center" }}>❝❞</div>
              <p style={{ margin:"2px 0 4px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", lineHeight:1.3, textAlign:"center" }}>"{result.winner_quote}"</p>
              <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", textTransform:"uppercase", textAlign:"center", color:C.inkMute }}>— {winnerName}, the winner</div>
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop:`1px solid ${C.ink}`, paddingTop:"6px", textAlign:"center", marginTop:"8px" }}>
            <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", textTransform:"uppercase" }}>MARRYKISSKILL.COM</div>
          </div>

          {/* Stamp */}
          <div style={{ position:"absolute", top:"22%", right:"6px" }}>
            <Stamp rotate={8} size="9px">★ {isFeast ? (youWon ? "STUFFED" : "BLOATED") : (youWon ? "UNDISPUTED" : "BRUTAL")} ★</Stamp>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display:"flex", gap:"8px", marginTop:"12px" }}>
          <button onClick={copy} style={{ flex:1, background:C.red, color:C.paper, border:`2px solid ${C.paper}`, padding:"12px", fontFamily:FONT.mono, fontSize:"11px", fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", cursor:"pointer" }}>📋 Copy Caption</button>
          <button onClick={onClose} style={{ flex:1, background:"transparent", color:C.paper, border:`2px solid ${C.paper}`, padding:"12px", fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", cursor:"pointer" }}>Dismiss</button>
        </div>
        <p style={{ color:"rgba(241,232,210,0.4)", fontSize:"9.5px", textAlign:"center", fontFamily:FONT.mono, letterSpacing:"1.5px", textTransform:"uppercase", marginTop:"10px" }}>Screenshot the card → post to Instagram Stories</p>
      </div>
    </div>
  );
};

// ─── APP ────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen]           = useState("home");
  const [experience, setExperience]   = useState(null); // "mkk" | "fc"
  const [answers, setAnswers]         = useState({});
  const [name, setName]               = useState("");
  const [attraction, setAttraction]   = useState("");
  const [currentSection, setCurrentSection] = useState(0);
  const [secQIdx, setSecQIdx]         = useState(0);
  const [results, setResults]         = useState(null);
  const [fcResult, setFcResult]       = useState(null);
  const [error, setError]             = useState(null);
  const [tmdbImages, setTmdbImages]   = useState({});
  const [adRecs, setAdRecs]           = useState([]);
  const [streamingRec, setStreamingRec] = useState(null);
  const [romanticProfile, setRomanticProfile] = useState(null);
  const [showShare, setShowShare]     = useState(false);
  const [gender, setGender]           = useState("");
  const [universe, setUniverse] = useState("");
  const [crushCharacter, setCrushCharacter] = useState("");
  const [crushUniverse, setCrushUniverse]   = useState("");
  // ─── Fantasy Arena state ───
  const [arenaCharacter, setArenaCharacter] = useState("");
  const [arenaUniverse, setArenaUniverse]   = useState("");
  const [arenaMode, setArenaMode]           = useState("");      // "armed" | "unarmed"
  const [arenaWeapon, setArenaWeapon]       = useState("");
  const [arenaResult, setArenaResult]       = useState(null);
  const [arenaShowRematch, setArenaShowRematch] = useState(false);
  const [arenaRematchInput, setArenaRematchInput] = useState("");
  const [arenaCustomStrategy, setArenaCustomStrategy] = useState(false);
  // ─── Fantasy Duel (universal battler) state ───
  // Persistent roster — one saved OC, one saved You. Either can be used in either slot.
  // Hydrate the OC roster + You profile from localStorage. Migrates the old single-character key if found.
  const [ocRoster, setOcRoster] = useState(() => {
    try {
      const list = localStorage.getItem("mkk:ocRoster");
      if (list) {
        const parsed = JSON.parse(list);
        if (Array.isArray(parsed)) return parsed.map(ensureOcHistory);
      }
      const legacy = localStorage.getItem("mkk:ocChar");
      if (legacy) {
        const c = JSON.parse(legacy);
        if (c && c.name) return [ensureOcHistory({ ...c, id: c.id || `oc_${Date.now()}` })];
      }
    } catch {}
    return [];
  });
  // Creator state: ocEditIdx = -1 means 'building new' (writes go to ocDraft). >=0 means 'editing roster[idx]' (writes go straight to the roster entry).
  const [ocEditIdx, setOcEditIdx] = useState(-1);
  const [ocDraft, setOcDraft] = useState(null);
  const [youChar, setYouChar] = useState(() => {
    try {
      const s = localStorage.getItem("mkk:youChar");
      if (!s) return null;
      const c = JSON.parse(s);
      return c ? ensureOcHistory(c) : null;
    } catch { return null; }
  });
  useEffect(() => {
    try {
      if (ocRoster && ocRoster.length > 0) {
        localStorage.setItem("mkk:ocRoster", JSON.stringify(ocRoster));
      } else {
        localStorage.removeItem("mkk:ocRoster");
      }
      localStorage.removeItem("mkk:ocChar"); // clean up legacy key
    } catch {}
  }, [ocRoster]);
  useEffect(() => {
    try { if (youChar) localStorage.setItem("mkk:youChar", JSON.stringify(youChar)); else localStorage.removeItem("mkk:youChar"); } catch {}
  }, [youChar]);
  // Roster helpers
  const addOcToRoster = (oc) => {
    const withId = oc.id ? oc : { ...oc, id: `oc_${Date.now()}` };
    setOcRoster(prev => [...prev, withId]);
  };
  const updateOcAt = (idx, oc) => {
    setOcRoster(prev => prev.map((c, i) => i === idx ? { ...oc, id: c.id || oc.id } : c));
  };
  const deleteOcAt = (idx) => {
    setOcRoster(prev => prev.filter((_, i) => i !== idx));
  };
  // Creator target: 'oc' writes to ocChar; 'you' writes to youChar; 'slotA'/'slotB' writes a transient fighter for that slot.
  const [ocBuildTarget, setOcBuildTarget] = useState("oc");
  const [ocSuggestions, setOcSuggestions] = useState({ tagline: [], catchphrase: [], signature: [] });
  const [ocSuggesting,  setOcSuggesting]  = useState({ tagline: false, catchphrase: false, signature: false });
  // Active battle slots — populated by the slot picker. Type is 'you' | 'oc' | 'fantasy' | 'oc-transient'.
  const [slotA, setSlotA] = useState(null);
  const [slotB, setSlotB] = useState(null);
  const [slotAType, setSlotAType] = useState(null);
  const [slotBType, setSlotBType] = useState(null);
  // Fantasy input is targeted at a specific slot.
  const [ocFantasySlot, setOcFantasySlot] = useState("B"); // "A" or "B"
  const [ocFantasyChar, setOcFantasyChar] = useState("");
  const [ocFantasyUni,  setOcFantasyUni]  = useState("");
  const [ocBattleSim,   setOcBattleSim]   = useState(null);
  const [ocBattleNarr,  setOcBattleNarr]  = useState(null);
  const [ocShowCustomStrategy, setOcShowCustomStrategy] = useState(false);
  const [ocCustomStrategyInput, setOcCustomStrategyInput] = useState("");
  // Scenario flow: tracks the picked matchup so each sub-step (build, fantasy input) can resume it.
  const [ocScenario, setOcScenario] = useState(null);
  // When the scenario needs an OC slot filled and roster has 2+, this tracks which slot the picker is choosing for.
  const [ocRosterPickFor, setOcRosterPickFor] = useState(null); // "A" | "B" | null
  // Image-gen status keyed by OC id (or 'fight' / 'youChar' for special targets).
  const [ocImagingFor, setOcImagingFor] = useState({});
  // Portrait-generation status used inside the creator screen.
  const [ocCreatorImaging, setOcCreatorImaging] = useState({ loading: false, error: null });
  // OC profile view: ocProfileFor = "youChar" or an OC id, or null when not active.
  const [ocProfileFor, setOcProfileFor] = useState(null);
  const [ocProfileGenStatus, setOcProfileGenStatus] = useState({ loading: false, error: null });
  const [ocFightImage, setOcFightImage] = useState(null);
  // ── OC MKK (Marry. Kiss. Kill. played as You or as an OC) ──
  // ocMkkPlayer: "youChar" | OC id | "__new__" (build new) | null. setupAttraction/setupUniverse
  // are the inputs to the celebrity-panel prompt. ocMkkResult holds the rendered panel.
  const [ocMkkPlayer, setOcMkkPlayer] = useState(null);
  const [ocMkkAttraction, setOcMkkAttraction] = useState("");
  const [ocMkkUniverse, setOcMkkUniverse] = useState("");
  const [ocMkkResult, setOcMkkResult] = useState(null);
  const [ocMkkLoading, setOcMkkLoading] = useState(false);
  // ── OC Matchmaker (FC-style compatibility — picks a player + a crush) ──
  // ocMmTarget: "free" | OC id. Free target uses ocMmCrushName + ocMmCrushUniverse.
  const [ocMmPlayer, setOcMmPlayer] = useState(null);
  const [ocMmTarget, setOcMmTarget] = useState("free");
  const [ocMmCrushName, setOcMmCrushName] = useState("");
  const [ocMmCrushUniverse, setOcMmCrushUniverse] = useState("");
  const [ocMmResult, setOcMmResult] = useState(null);
  // ── OC Feast (eating contest — player vs free-form character or another OC) ──
  const [ocFeastPlayer, setOcFeastPlayer] = useState(null);
  const [ocFeastTarget, setOcFeastTarget] = useState("free");
  const [ocFeastOppName, setOcFeastOppName] = useState("");
  const [ocFeastOppUniverse, setOcFeastOppUniverse] = useState("");
  const [ocFeastFood, setOcFeastFood] = useState("Hot dogs");
  const [ocFeastResult, setOcFeastResult] = useState(null);
  // ── OC compatibility survey (the original 26-question MKK form, per-OC) ──
  // Cached on each character as `c.mkkSurvey = { answers, completedAt }`. The
  // gate runs before OC MKK and OC Matchmaker the first time a fighter plays.
  const [ocSurveyFor, setOcSurveyFor] = useState(null); // "youChar" | OC id
  const [ocSurveyAnswers, setOcSurveyAnswers] = useState({});
  const [ocSurveyReturnScreen, setOcSurveyReturnScreen] = useState(null);
  const openOcSurvey = (whoId, returnScreen) => {
    const c = whoId === "youChar" ? youChar : ocRoster.find(o => o.id === whoId);
    setOcSurveyFor(whoId);
    setOcSurveyAnswers(c?.mkkSurvey?.answers ? { ...c.mkkSurvey.answers } : {});
    setOcSurveyReturnScreen(returnScreen);
    setError(null);
    setScreen("oc-survey");
  };
  // Build one arena history record per saved fighter and append it to oc.history.arena
  // (or youChar.history.arena). Transient OCs and fantasy characters are skipped.
  const recordFightQuotes = (narr, sim) => {
    if (!narr || !sim || !slotA || !slotB) return;
    const aWon = sim.winner === "a";
    const today = new Date().toISOString().slice(0, 10);
    const aQuotes = [];
    const bQuotes = [];
    if (narr.winner_quote) {
      (aWon ? aQuotes : bQuotes).push({ role: "winner", text: narr.winner_quote });
    }
    if (narr.loser_quote) {
      (aWon ? bQuotes : aQuotes).push({ role: "loser", text: narr.loser_quote });
    }
    if (narr.pre_fight_remark) {
      const speaker = (narr.pre_fight_remark_speaker || "").trim().toLowerCase();
      const aMatch = speaker && slotA.name && speaker === slotA.name.toLowerCase();
      const bMatch = speaker && slotB.name && speaker === slotB.name.toLowerCase();
      const target = aMatch ? aQuotes : bMatch ? bQuotes : null;
      if (target) target.push({ role: "pre-fight", text: narr.pre_fight_remark });
    }
    const aRecord = {
      date: today,
      opponent: slotB.name || "?",
      opponentId: slotBType === "oc" ? (slotB.id || null) : null,
      result: aWon ? "win" : "loss",
      quotes: aQuotes,
    };
    const bRecord = {
      date: today,
      opponent: slotA.name || "?",
      opponentId: slotAType === "oc" ? (slotA.id || null) : null,
      result: aWon ? "loss" : "win",
      quotes: bQuotes,
    };
    const appendArena = (c, rec) => {
      const base = ensureOcHistory(c);
      return { ...base, history: { ...base.history, arena: [...base.history.arena, rec] } };
    };
    if (slotAType === "you" && youChar) {
      setYouChar(appendArena(youChar, aRecord));
    } else if (slotAType === "oc") {
      const idx = ocRoster.findIndex(o => o.id === slotA.id);
      if (idx >= 0) updateOcAt(idx, appendArena(ocRoster[idx], aRecord));
    }
    if (slotBType === "you" && youChar) {
      setYouChar(prev => appendArena(prev || youChar, bRecord));
    } else if (slotBType === "oc") {
      const idx = ocRoster.findIndex(o => o.id === slotB.id);
      if (idx >= 0) updateOcAt(idx, appendArena(ocRoster[idx], bRecord));
    }
  };

  const generateOcPortrait = async (idx) => {
    const oc = ocRoster[idx];
    if (!oc) return;
    setOcImagingFor(prev => ({ ...prev, [oc.id]: { loading: true } }));
    const res = await generateImage(ocPortraitPrompt(oc));
    if (res.ok) {
      updateOcAt(idx, { ...oc, imageUrl: res.url });
      setOcImagingFor(prev => ({ ...prev, [oc.id]: { loading: false } }));
    } else {
      setOcImagingFor(prev => ({ ...prev, [oc.id]: { loading: false, error: res.error } }));
    }
  };
  const generateYouPortrait = async () => {
    if (!youChar) return;
    setOcImagingFor(prev => ({ ...prev, youChar: { loading: true } }));
    const res = await generateImage(ocPortraitPrompt(youChar));
    if (res.ok) {
      setYouChar({ ...youChar, imageUrl: res.url });
      setOcImagingFor(prev => ({ ...prev, youChar: { loading: false } }));
    } else {
      setOcImagingFor(prev => ({ ...prev, youChar: { loading: false, error: res.error } }));
    }
  };
  // Per-fight venue. Set when the user wins the coin-flip and picks. Empty string = Claude picks freely in the narration.
  const [ocChosenVenue, setOcChosenVenue] = useState("");
  const [ocVenueCustom, setOcVenueCustom] = useState("");
  // Per-fight armed flags — set on the pre-fight screen. ocWeaponA/B holds the chosen weapon name (null = unarmed).
  const [ocArmedA, setOcArmedA] = useState(false);
  const [ocArmedB, setOcArmedB] = useState(false);
  const [ocWeaponA, setOcWeaponA] = useState(null);
  const [ocWeaponB, setOcWeaponB] = useState(null);
  const caseNo = useMemo(generateCaseNo, []);

  const activeSections = experience === "arena" ? ARENA_SECTIONS
                       : experience === "feast" ? FEAST_SECTIONS
                       : SECTIONS;
  const activeTotal    = experience === "arena" ? ARENA_TOTAL
                       : experience === "feast" ? FEAST_TOTAL
                       : TOTAL_Qs;
  const sec = activeSections[currentSection];
  const secQs = sec?.questions ?? [];
  const currentQ = secQs[secQIdx];
  const globalIdx = activeSections.slice(0, currentSection).reduce((a,s) => a + s.questions.length, 0) + secQIdx;
  const progress = ((globalIdx+1)/activeTotal)*100;
  const answerOk = (q, v) => {
    if (v === undefined || v === null || v === "") return false;
    if (q.type === "number") return Number.isFinite(Number(v)) && Number(v) > 0;
    if (q.type === "text") return typeof v === "string" && v.trim().length > 0;
    return true;
  };
  const canAdvance = currentQ && answerOk(currentQ, answers[currentQ.id]);

  const setAnswer = (qid, val) => setAnswers(prev => ({ ...prev, [qid]: val }));
  const goNext = () => {
    if (secQIdx < secQs.length-1) setSecQIdx(secQIdx+1);
    else if (currentSection < activeSections.length-1) { setCurrentSection(s => s+1); setSecQIdx(0); }
    else runAnalysis();
  };
  const goBack = () => {
    if (secQIdx > 0) setSecQIdx(q => q-1);
    else if (currentSection > 0) { const prev = activeSections[currentSection-1]; setCurrentSection(s => s-1); setSecQIdx(prev.questions.length-1); }
  };

  const randomAnswers = () => {
    const r = {};
    ALL_QUESTIONS.forEach(q => { r[q.id] = Math.ceil(Math.random()*7); });
    return r;
  };
  const testMode = () => {
    if (!name.trim()) setName("Alex");
    if (!attraction) setAttraction("Men");
    if (!gender) setGender("Woman");
    if (!experience) setExperience("mkk");
    if (experience === "fc") {
      if (!crushCharacter.trim()) setCrushCharacter("Hermione Granger");
      if (!crushUniverse.trim()) setCrushUniverse("Harry Potter");
    }
    setAnswers(randomAnswers());
    runAnalysis();
  };

  const runArenaAnalysis = async (strategyForRematch = "") => {
    setScreen("loading"); setError(null);
    if (!strategyForRematch) setStreamingRec(null);
    const locale = detectLocale();
    const ch = arenaCharacter.trim();
    const uni = arenaUniverse.trim();
    if (!ch || !uni) { setError("Pick an opponent and a universe."); setScreen("intro"); return; }
    const a = answers;
    const arenaPrompt = `You are writing a Cosmopolitan magazine fight column — stylized but with real outcomes — about a fictional fight between ${name||"the reader"} and ${ch} from ${uni}.

THE READER: ${gender||"person"}, ${a.age||"?"} years old, height ${a.height||"?"}, weight ${a.weight||"?"}. Max pushups in one set: ${a.pushups||"?"}. Mile time: ${a.mile||"?"} minutes.
TRAINING: Martial arts ${a.martial||"unknown"}. Weapons ${a.weapons||"unknown"}. Real fights outside training: ${a.realfight||"unknown"}.
HEAD GAME: Face-off reaction: ${a.barface||"unknown"}. How dirty they'd fight: ${a.dirty||"unknown"}. Injury history: ${a.scars||"unknown"}. Killer-instinct self-rating ${a.hurt||"?"}/7. Sudden-threat reaction: ${a.adrenaline||"unknown"}. Weapons-eye self-rating ${a.weapons_eye||"?"}/7.
PERSONA: Fighter archetype: ${a.vibe||"unknown"}. Signature move: ${a.signature||"unknown"}. How they enter: ${a.entry||"unknown"}. Post-fight behavior: ${a.aftermath||"unknown"}.
SURVIVAL: 3 days in the woods alone: ${a.woods||"unknown"}.

MODE: ${arenaMode}. ${arenaMode === "armed" ? `Reader's weapon: ${arenaWeapon}.` : "Both fighters unarmed."}
OPPONENT'S GEAR: ${ch} fights with whatever they canonically have in ${uni} — abilities, weapons, allies if always-present (e.g. Daenerys with her dragons, Wolverine with claws).
${arenaWeapon === "Match opponent's gear" ? `MATCHED-GEAR RULE: Reader gets the closest mundane equivalent of ${ch}'s primary weapon — sword vs sword, gun vs gun, baseball bat vs club. For supernatural opponents (claws, magic, dragons) reader gets the best mundane substitute and you note the gap in the play-by-play.` : ""}

${strategyForRematch ? `READER'S NEW STRATEGY THIS REMATCH: "${strategyForRematch}". Weigh this strategy honestly — if it's smart, give them better odds; if it's still bad against ${ch}, narrate why.` : ""}

LOCALIZATION: User is from ${locale.country||"unknown country"}, language ${locale.language}. Write ALL copy in ${locale.language}.

SCENE: ${name||"The reader"} chose the opponent (${ch} from ${uni}). ${ch} picks the location — make it a spot in ${uni} where ${ch} has the home-turf advantage, somewhere they'd realistically want to fight. Write "environment" from ${ch}'s POV as the host: e.g. "${ch} called the meet at the Quidditch pitch — high ground, his rules." Frame it as an INTENTIONAL confrontation — both fighters know exactly why they're there. ${name||"The reader"} stepped into this on purpose; do not write it as an accident, a misunderstanding, a wrong door, or a bathroom mix-up. Open the scene already loaded with tension. Run the fight. Decide the winner honestly based on the matchup — strength, training, equipment, willingness to fight dirty, ${ch}'s actual canon abilities. Then narrate it like a tabloid fight column — punchy, dramatic, third-person using ${name||"the reader"}'s name and ${ch}'s name.

WRITING STYLE: Cosmo magazine voice. Stylized but real results — losses are losses, wins are wins. Channel ${ch}'s actual voice and worldview in any quoted lines. Funny when honest. The reader can win against weaker characters and absolutely will lose to stronger ones.

MIRROR THE READER'S PERSONA. Their PERSONA fields above are not flavor — they should shape:
- play_by_play: the FIRST beat reflects how they enter (silent / talking / hyped / calm / joking). At least one middle beat showcases their signature move (or them attempting it and failing).
- winner_quote / loser_quote when the reader is the speaker: written in the voice of their archetype (cocky → smug; underdog → grateful or disbelieving; stoic → terse; chaotic → unhinged; calculated → wry analytical).
- The final beat or condition_of_loser: reflect their post-fight behavior (apology, clean walk, parting dig, or finishing move) — applied whether they won or lost.
The reader's persona should be unmistakable in the writing — a chaotic wild card and a calculated patient fighter should never read the same way.

CONTENT RATING: PG. Stylized fight choreography. No graphic injuries, no blood detail, no slurs. Bruises, sprains, missing eyebrows, wounded pride — all fine. Keep it the kind of thing you could read aloud at brunch.

VARIETY: This is a replayable game. Vary your output between runs even for similar fights.
- time_seconds: avoid clustering on 30/45/60/90/120/180. Use the FULL plausible range: 8 seconds for an instant KO, 12 for a quick mismatch, 27, 53, 71, 142, 246, 8m+ for drawn-out brawls. Use odd numbers. Match the fight's actual pace, not a default.
- winner_quote & loser_quote: vary tone widely — sometimes a one-word burn ("Pathetic."), sometimes a question ("That was it?"), sometimes a deadpan observation, sometimes triumphant, sometimes self-deprecating, sometimes in-character canon catchphrase, sometimes a quote from the loser's hometown/era. Avoid generic 'I underestimated you' or 'Better luck next time' phrasing.
- play_by_play beats: don't always open with the entry move. Vary structure — sometimes start mid-action, sometimes with a feint, sometimes with the opponent striking first.
- time_narrative: never start with 'Just long enough for'. Use different sentence shapes each run.

Return JSON only. No markdown.
{"character":"Canonical name of ${ch} (fix typos)","universe":"Canonical title of ${uni} (fix typos)","location_pick":"Short name of the specific spot ${ch} picked — e.g. 'Quidditch pitch, Hogwarts'","environment":"1-2 sentence scene-set, framed as ${ch}'s home-turf pick — open with ${ch} picking the spot","play_by_play":["five short beats of action, in order, in tabloid voice — each 1 sentence"],"winner_role":"the literal string 'reader' if ${name||"the reader"} won, or 'opponent' if ${ch} won. Must match the play_by_play.","time_seconds":<integer total fight duration in seconds — see VARIETY above. Do NOT default to round numbers.>,"time_narrative":"One short line for color, varied phrasing each run.","winner_quote":"One short quotable line the WINNER says walking off. See VARIETY above for tone range. If reader won, use ${name||"the reader"}'s voice; if opponent won, use ${ch}'s canon voice.","loser_quote":"One short line the LOSER mutters or yells. See VARIETY above. If reader lost, ${name||"the reader"}'s voice; if opponent lost, ${ch}'s canon voice.","condition_of_loser":"One funny sentence about the loser's state after — bruised ego, missing eyebrow, etc. Vary the body part / image each run.","rematch_chance":<integer 0–100, realistic odds the reader would WIN a rematch with a better strategy>,"rematch_strategies":["three specific tactical suggestions for the rematch — e.g. 'go for the wand first', 'use the railing for leverage'"]}`;

    try {
      const text = await callClaude(arenaPrompt);
      const parsed = parseJSON(text);
      if (!parsed.play_by_play || !parsed.winner_role) throw new Error("Missing fields");
      const canonCh  = (parsed.character && parsed.character.trim()) || ch;
      const canonUni = (parsed.universe && parsed.universe.trim()) || uni;
      const finalResult = { ...parsed, character: canonCh, universe: canonUni, mode: arenaMode, weapon: arenaWeapon, strategy: strategyForRematch };
      setArenaResult(finalResult);
      setArenaShowRematch(false);
      setArenaRematchInput("");
      setArenaCustomStrategy(false);
      setScreen("results");
      (async () => {
        // Cascade: try the character-titled work, then combined character+universe, then the universe poster.
        // Posters tend to show the character in costume rather than the actor's headshot.
        const img = (await fetchTMDBImage(canonCh))
                 || (await fetchTMDBImage(`${canonCh} ${canonUni}`))
                 || (await fetchTMDBImage(canonUni));
        // null is fine — the results screen renders a styled placeholder when arena image is missing.
        setTmdbImages(prev => ({ ...prev, arena: img || null }));
      })();
      // Streaming rec — fetch in parallel, fire and forget (skip on rematches since opponent didn't change).
      if (!strategyForRematch) {
        getArenaStreaming(finalResult).then(rec => { if (rec) setStreamingRec(rec); });
      }
    } catch (e) { setError(e.message); setScreen("survey"); }
  };

  const runFeastAnalysis = async (strategyForRematch = "") => {
    setScreen("loading"); setError(null);
    if (!strategyForRematch) setStreamingRec(null);
    const locale = detectLocale();
    const ch = arenaCharacter.trim();
    const uni = arenaUniverse.trim();
    if (!ch || !uni) { setError("Pick an opponent and a universe."); setScreen("intro"); return; }
    const food = arenaWeapon || "Hot dogs";
    const a = answers;
    const feastPrompt = `You are writing a Cosmopolitan magazine eating-contest column — stylized but with real outcomes — about a competitive eating contest between ${name||"the reader"} and ${ch} from ${uni}.

THE READER: ${gender||"person"}, ${a.age||"?"} years old, height ${a.height||"?"}, weight ${a.weight||"?"}.
APPETITE: On a hungry day they eat ${a.appetite||"unknown"}. Eating speed: ${a.speed||"unknown"}.
EATING CRED: Contest experience: ${a.contests||"unknown"}. Hot wings capacity: ${a.hotwings||"unknown"}. Spice tolerance: ${a.spice||"unknown"}.
STOMACH GAME: Keeps going past full: ${a.finishit||"unknown"}. Clean-plate upbringing: ${a.plate||"unknown"}. Past-the-limit-without-getting-sick self-rating ${a.hurl||"?"}/7. Reaction when behind: ${a.behind||"unknown"}. Sweet vs savory: ${a.dessert||"unknown"}.
PERSONA: Eater archetype: ${a.vibe||"unknown"}. Signature move: ${a.signature||"unknown"}. How they enter: ${a.entry||"unknown"}. Post-contest behavior: ${a.aftermath||"unknown"}.

THE FOOD: ${food}.${food === "Match opponent's specialty" ? ` Pick the food that ${ch} would canonically eat the most of in ${uni} and use that.` : ""}
OPPONENT: ${ch} competes with whatever canonical eating abilities they have in ${uni} — appetite, magic, supernatural stomach, gluttonous reputation, etc. If ${ch} is a known glutton or has supernatural eating ability, that dominates. If ${ch} has no special advantage, treat them as a normal person their size.

${strategyForRematch ? `READER'S NEW STRATEGY THIS REMATCH: "${strategyForRematch}". Weigh this strategy honestly — if it's smart, better odds; if it's still bad against ${ch}, narrate why.` : ""}

LOCALIZATION: User is from ${locale.country||"unknown country"}, language ${locale.language}. Write ALL copy in ${locale.language}.

SCENE: ${name||"The reader"} challenged ${ch} to an eating contest. ${ch} picks the venue — a spot in ${uni} that fits an eating contest (a great hall, a feast scene, a kitchen, a tavern, a county fair, wherever feels right). Write "environment" from ${ch}'s POV as the host. Frame it as an INTENTIONAL challenge.

RUN THE CONTEST. Five plate-by-plate beats covering: opening minutes, mid-contest stomach wall, when one pulls away, comeback attempt, the final plate. Decide the winner honestly based on capacity + canon ability + strategy + persona.

WRITING STYLE: Cosmo magazine voice. Stylized, punchy, third-person using ${name||"the reader"}'s name and ${ch}'s name. Channel ${ch}'s voice in any quoted lines. Funny when honest.

MIRROR THE READER'S PERSONA in the play-by-play (entry, signature move, archetype voice in their quotes, aftermath in the final beat).

VARIETY: This is a replayable game. Vary your output between runs.
- time_seconds: contest duration. Range from ~90s blowout up to 30+ minutes drawn-out grind. Use odd numbers, avoid clustering on 300/600/900.
- winner_quote & loser_quote: vary tone — sometimes a burp, sometimes deadpan, sometimes a desperate plea, sometimes triumphant, sometimes a canon catchphrase. Avoid generic 'I underestimated you' or 'Better luck next time'.
- Vary the food-coma / condition_of_loser image each run.

CONTENT RATING: PG. No vomit detail, no gross-out. Bloated, mustard-stained, food coma — all fine.

{"character":"Canonical name of ${ch} (fix typos)","universe":"Canonical title of ${uni} (fix typos)","location_pick":"Short name of the specific venue ${ch} picked — e.g. 'Great Hall, Hogwarts'","environment":"1-2 sentence scene-set from ${ch}'s POV as the host","play_by_play":["five short beats — opening, mid-contest, lead change, comeback, final plate"],"winner_role":"the literal string 'reader' or 'opponent'","time_seconds":<integer total contest duration in seconds — see VARIETY>,"time_narrative":"One short line for color, varied phrasing.","final_count":"Scoreboard at the end — e.g. 'Joey: 22 hot dogs · Matt: 4.5 hot dogs'","winner_quote":"One short quotable line the WINNER says, leaning back from the table.","loser_quote":"One short line the LOSER mutters or yells.","condition_of_loser":"One funny sentence about the loser's state after — food coma, mustard-stained, etc.","rematch_chance":<integer 0–100, realistic odds the reader would WIN a rematch with a better strategy>,"rematch_strategies":["three specific tactical suggestions — e.g. 'dip in water and swallow whole', 'pace the first five plates', 'no liquids in the last minute'"]}`;

    try {
      const text = await callClaude(feastPrompt);
      const parsed = parseJSON(text);
      if (!parsed.play_by_play || !parsed.winner_role) throw new Error("Missing fields");
      const canonCh  = (parsed.character && parsed.character.trim()) || ch;
      const canonUni = (parsed.universe && parsed.universe.trim()) || uni;
      const finalResult = { ...parsed, character: canonCh, universe: canonUni, food, strategy: strategyForRematch };
      setArenaResult(finalResult);
      setArenaShowRematch(false);
      setArenaRematchInput("");
      setArenaCustomStrategy(false);
      setScreen("results");
      (async () => {
        const img = (await fetchTMDBImage(canonCh))
                 || (await fetchTMDBImage(`${canonCh} ${canonUni}`))
                 || (await fetchTMDBImage(canonUni));
        setTmdbImages(prev => ({ ...prev, arena: img || null }));
      })();
      if (!strategyForRematch) {
        getArenaStreaming(finalResult).then(rec => { if (rec) setStreamingRec(rec); });
      }
    } catch (e) { setError(e.message); setScreen("survey"); }
  };

  // Fantasy Duel scenario resolver — given a scenario type, fills the next missing slot and
  // routes to the right sub-screen. After each sub-task finishes it calls this again to keep going.
  const SCENARIO_PAIRS = {
    "you-fantasy":    { a: "you",     b: "fantasy" },
    "you-oc":         { a: "you",     b: "oc"      },
    "oc-fantasy":     { a: "oc",      b: "fantasy" },
    "oc-oc":          { a: "oc",      b: "oc"      },
    "fantasy-fantasy":{ a: "fantasy-a", b: "fantasy" },
  };
  // overrides.slotA / .slotB let callers pass freshly-set values that React state hasn't committed yet.
  const advanceScenario = (type, overrides = {}) => {
    const want = SCENARIO_PAIRS[type];
    if (!want) { setScreen("oc-slots"); return; }
    // For OC vs OC: skip the auto-routing entirely. The combined picker handles BOTH slots in one view.
    if (type === "oc-oc" && !(overrides.slotA && overrides.slotB)) {
      setScreen("oc-oc-pick"); return;
    }
    // Use mutable locals so updates inside this function propagate to the slot B check below.
    let curA = overrides.slotA !== undefined ? overrides.slotA : slotA;
    let curB = overrides.slotB !== undefined ? overrides.slotB : slotB;
    const pickOcForSlot = (which) => {
      if (ocRoster.length === 0) {
        setOcBuildTarget(which === "A" ? "slotA" : "slotB");
        if (which === "A") { setSlotA(null); setSlotAType(null); }
        else               { setSlotB(null); setSlotBType(null); }
        setScreen("oc-create"); return;
      }
      if (ocRoster.length === 1) {
        const oc = ocRoster[0];
        if (which === "A") { setSlotA(oc); setSlotAType("oc"); }
        else               { setSlotB(oc); setSlotBType("oc"); }
        // Pass through BOTH current slot values so the recursive call doesn't re-walk slot A from a stale closure.
        const newOverrides = which === "A"
          ? { slotA: oc,   slotB: curB }
          : { slotA: curA, slotB: oc   };
        advanceScenario(type, newOverrides);
        return;
      }
      setOcRosterPickFor(which);
      setScreen("oc-roster-pick"); return;
    };
    // Slot A
    if (!curA) {
      if (want.a === "you") {
        if (!youChar) { setOcBuildTarget("you"); setScreen("oc-create"); return; }
        setSlotA(youChar); setSlotAType("you");
        curA = youChar;
      } else if (want.a === "oc") {
        pickOcForSlot("A"); return;
      } else if (want.a === "fantasy-a") {
        setOcFantasySlot("A"); setOcFantasyChar(""); setOcFantasyUni("");
        setScreen("oc-fantasy-input"); return;
      }
    }
    // Slot B
    if (!curB) {
      if (want.b === "you") {
        if (!youChar) { setOcBuildTarget("you"); setScreen("oc-create"); return; }
        setSlotB(youChar); setSlotBType("you");
        curB = youChar;
      } else if (want.b === "oc") {
        pickOcForSlot("B"); return;
      } else if (want.b === "fantasy") {
        setOcFantasySlot("B"); setOcFantasyChar(""); setOcFantasyUni("");
        setScreen("oc-fantasy-input"); return;
      }
    }
    // Both filled — fight.
    setOcBattleSim(null); setOcBattleNarr(null);
    setScreen("oc-prefight");
  };
  const startScenario = (type) => {
    setOcScenario(type);
    setSlotA(null); setSlotAType(null);
    setSlotB(null); setSlotBType(null);
    setOcBattleSim(null); setOcBattleNarr(null);
    setOcArmedA(false); setOcArmedB(false);
    setOcWeaponA(null); setOcWeaponB(null);
    setOcChosenVenue(""); setOcVenueCustom("");
    setOcFightImage(null);
    setError(null);
    advanceScenario(type);
  };

  const runAnalysis = async () => {
    if (experience === "arena") { return runArenaAnalysis(); }
    if (experience === "feast") { return runFeastAnalysis(); }
    setScreen("loading"); setError(null);
    const locale = detectLocale();
    const profile = computeProfile(answers);
    const coreValuesWarning = (profile.values/7) < 0.65;
    const dealbreakerFlags = ALL_QUESTIONS.filter(q => q.dealbreaker && answers[q.id] !== undefined && (answers[q.id] <= 2 || answers[q.id] >= 6)).map(q => q.text.split(".")[0]);
    const pct = k => Math.round((profile[k]/7)*100);

    const wantsKids       = answers.q1>=6  ? "wants kids"          : answers.q1<=2  ? "doesn't want kids"             : "is open about kids";
    const isAmbitious     = answers.q3>=6  ? "very career-driven"  : answers.q3<=2  ? "not career-focused"            : "moderately ambitious";
    const needsAlone      = answers.q10>=6 ? "needs lots of alone time" : answers.q10<=2 ? "thrives on togetherness" : "balanced on alone time";
    const lovesTravel     = answers.q12>=6 ? "loves adventure and travel" : answers.q12<=2 ? "prefers staying home"   : "sometimes adventurous";
    const conflictStyle   = answers.q19>=5 ? "addresses conflict head-on" : answers.q20>=5 ? "needs space to process" : "handles conflict carefully";
    const needsReassurance= answers.q23>=6 ? "craves reassurance and closeness" : answers.q23<=2 ? "very independent emotionally" : "moderately needs reassurance";
    const looksImportance = answers.q26>=6 ? "considers physical attraction very important" : answers.q26<=2 ? "prioritizes personality and connection over looks" : "considers looks moderately important";
    const isIntellectual  = answers.q18>=6 ? "deeply intellectual and curious" : answers.q18<=2 ? "more practical than intellectual" : "intellectually curious";
    const isOrganized     = answers.q16>=6 ? "highly organized and structured" : answers.q16<=2 ? "spontaneous and flexible" : "moderately organized";

    const profilePrompt = `Write a 2-3 sentence romantic personality profile in second person for someone with these traits: ${wantsKids}, ${isAmbitious}, ${needsAlone}, ${lovesTravel}, ${conflictStyle}, ${needsReassurance}, ${isIntellectual}, ${isOrganized}. Write like a Cosmo editor — warm, direct, insightful. Start with "You're someone who..." No labels, no scores, just a flowing personality read that feels like a horoscope crossed with therapy. Make it specific and resonant. Write in ${locale.language}.`;

    if (experience === "fc") {
      // ─── Fantasy Crush flow ────────────────────────────────────────────
      const ch = crushCharacter.trim();
      const uni = crushUniverse.trim();
      if (!ch || !uni) { setError("Pick a character and a universe."); setScreen("intro"); return; }

      const fcPrompt = `You are writing for a Cosmopolitan magazine piece — a humorous, opinionated reveal of whether ${ch} from ${uni} would actually love ${name||"this person"} back.

THE READER'S PROFILE: ${gender||"person"}, attracted to ${attraction||"anyone"}. CoreValues ${pct("values")}%, Lifestyle ${pct("lifestyle")}%, Conflict ${pct("conflict")}%, EmotionalNeeds ${pct("emotional")}%. They ${wantsKids}, ${isAmbitious}, ${needsAlone}, ${lovesTravel}, ${conflictStyle}, ${needsReassurance}, ${isIntellectual}, ${isOrganized}, ${looksImportance}.

THE CRUSH: ${ch} from ${uni}.

SPELLING: If the character name or universe above appears misspelled, mistyped, or wrongly capitalized, identify who the user clearly meant and use the canonical spelling everywhere in your response — including the "character" and "universe" fields in the JSON below. If the spelling is already correct, return it unchanged. Do not invent a different character; only fix obvious typos of the intended one.

Write entirely from ${ch}'s point of view — what would they ACTUALLY think of this person, given who ${ch} is and the world they live in? Be specific to their personality, voice, and circumstances. Reference particulars of ${uni} where it fits naturally.

LOCALIZATION: User is from ${locale.country||"unknown country"}, language ${locale.language}. Write ALL copy in ${locale.language}.

WRITING STYLE: Cosmo magazine voice, but channel ${ch}'s actual voice and worldview — a brutal character should be brutally funny, a sweet character should be earnest, a chaotic character should be chaotic. Funny, dramatic, over-the-top — calibrate to the score AND to who ${ch} is. Mismatches should be giggle-worthy, not gentle; if the score is low, go for the line that makes the reader laugh out loud. ${ch} is the comedian; you're giving them the stage. NO numbers, NO variable names, NO percentages.

CONTENT RATING: PG. No profanity, no explicit sexual or romantic content, no graphic violence, no slurs. Spicy, savage, swoony, melodramatic — all great. Crude is not. Keep it the kind of thing you could read aloud at brunch.

SCORING: The "score" is how interested ${ch} would actually be in ${name||"this person"} based on real personality fit between the reader's profile above and who ${ch} is. USE THE FULL 0–100 RANGE and DO NOT default to 87 — the example number below is illustrative only. The score must match the verdict:
  • 90–100 = SMITTEN (deep alignment, real spark, fundamentals click)
  • 70–89  = INTRIGUED (strong interest, undeniable energy, some friction)
  • 50–69  = CURIOUS (willing to give it a shot, not invested yet)
  • 30–49  = FRIEND-ZONED (warm regard, no romantic chemistry)
  • 10–29  = COMPLICATED (wrong frequency, ${ch} keeps distance)
  • 0–9    = NOT INTERESTED (fundamental clash, ${ch} wants nothing)
Weigh ${ch}'s actual personality and values from ${uni} against the reader's profile — career-driven readers + ambitious characters score higher together, chaos-averse readers + chaotic characters score lower, etc.

NAMING: Have ${ch} address ${name||"the reader"} by name (${name||""}) naturally in 2 or 3 of the responses below — sprinkle it in like a real person would (e.g. in the first impression, the opening line, somewhere in what they think). Don't force it into every sentence; mix with "you" and pronouns. It should feel personal, not robotic.

Return JSON only. No markdown.
{"character":"Canonical character name with correct spelling and capitalization — fix typos per SPELLING above","universe":"Canonical title of the show / film / book / game — fix typos per SPELLING above","verdict":"One word in caps describing how the character feels — choose from: SMITTEN / INTRIGUED / CURIOUS / FRIEND-ZONED / COMPLICATED / NOT INTERESTED","score":<integer 0–100, calibrated to the verdict and the actual fit — see SCORING above>,"pronoun":"she | he | they | it — the pronoun that fits the character","first_impression":"One sharp first-person sentence — ${ch}'s gut reaction the first time they see ${name||"this person"}.","what_they_think":"2-3 sentences in ${ch}'s voice — their honest take on this person. Specific, opinionated, in character.","relationship":"2-3 sentences describing what the relationship would actually look like. Specific to ${uni}'s setting.","they_love":"One first-person sentence from ${ch} about what they'd love most about this person.","they_dislike":"One first-person sentence from ${ch} about what would drive them up the wall.","opening_line":"The first thing ${ch} would say to ${name||"this person"} — in character, in their voice."}`;

      try {
        const text = await callClaude(fcPrompt);
        const parsed = parseJSON(text);
        if (!parsed.what_they_think || !parsed.relationship) throw new Error("Missing fields");
        const canonCh = (parsed.character && parsed.character.trim()) || ch;
        const canonUni = (parsed.universe && parsed.universe.trim()) || uni;
        const finalResult = { ...parsed, character: canonCh, universe: canonUni, profile: { ...profile, gender }, coreValuesWarning, dealbreakerFlags };
        setFcResult(finalResult);
        setScreen("results");
        (async () => {
          const charImg = await fetchTMDBCharacterImage(canonCh, canonUni);
          const img = charImg || await fetchTMDBImage(canonUni);
          if (img) setTmdbImages(prev => ({ ...prev, fc: img }));
        })();
        callClaude(profilePrompt).then(t => setRomanticProfile(t.trim())).catch(()=>{});
        getAdRecommendations({ marry: { character: canonCh, universe: canonUni } }).then(({recs, streaming}) => { setAdRecs(recs||[]); setStreamingRec(streaming||null); });
      } catch (e) { setError(e.message); setScreen("survey"); }
      return;
    }

    // ─── Marry / Kiss / Kill flow ──────────────────────────────────────
    const chosenUniverse = universe.trim();
    const activeCategoryLabel = chosenUniverse || "any fictional universe";
    const activeUniverses     = chosenUniverse
      ? `Focus specifically on: ${chosenUniverse}. Be creative within that universe — include obvious characters and lesser-known ones.`
      : "any fictional universe you know";

    const prompt = `You are writing for a Cosmopolitan magazine compatibility quiz. Match ${name||"this person"} with three fictional characters using Marry/Kiss/Kill.

THEIR PROFILE: ${gender||"person"}, attracted to ${attraction||"anyone"}. CoreValues ${pct("values")}%, Lifestyle ${pct("lifestyle")}%, Conflict ${pct("conflict")}%, EmotionalNeeds ${pct("emotional")}%. They ${wantsKids}, ${isAmbitious}, ${needsAlone}, ${lovesTravel}, ${conflictStyle}, ${needsReassurance}, ${isIntellectual}, ${isOrganized}, ${looksImportance}.

RULES: Only characters matching attraction (${attraction||"anyone"}). Be creative. CATEGORY: ${activeCategoryLabel}. Draw from: ${activeUniverses}.

LOCALIZATION: User is from ${locale.country||"unknown country"}, browser language is ${locale.language}. Write ALL copy in ${locale.language}. Prioritize characters from ${locale.country||"their region"} pop culture — local TV, films, books, dramas, anime. Include globally known characters only if they genuinely fit better than any local option.

WRITING STYLE: Like Cosmopolitan magazine in ${locale.language}. Second person throughout. Specific to the character. Funny, dramatic, over-the-top — Marry should feel swoony and aspirational, Kiss should feel electric and dangerous, Kill should be hilariously brutal (lean into why this would be a disaster, in each character's specific voice). Channel each character's actual personality — let them drive the tone of their own section. NO numbers, NO variable names, NO percentages.

CONTENT RATING: PG. No profanity, no explicit sexual or romantic content, no graphic violence, no slurs. Spicy, savage, swoony, melodramatic — all great. Crude is not. Keep it the kind of thing you could read aloud at brunch.

Return JSON only. No markdown.
{"marry":{"character":"Name","universe":"Show","emoji":"emoji","score":91,"why":"2-3 second-person Cosmo sentences — e.g. 'You need someone who can keep up with your ambition without dimming your shine — and that's exactly what she does.'","warning":"One sharp funny sentence","opening_line":"Charming opener — written as if YOU are approaching THEM, not them approaching you. First person, confident, specific to their world"},"screw":{"character":"Name","universe":"Show","emoji":"emoji","score":76,"why":"2-3 second-person sentences — e.g. 'The chemistry is undeniable, but you two would combust within a month.'","warning":"One funny sentence about how this crashes","opening_line":"A flirty line the USER would say TO this character — first-person, bold, a little dangerous"},"kill":{"character":"Name","universe":"Show","emoji":"emoji","score":8,"why":"2-3 second-person darkly funny sentences — e.g. 'You'd last exactly one conversation before someone storms out.'","warning":"One savage sentence","opening_line":"The line the USER would say TO this character that immediately causes conflict — first-person, provocative"}}`;

    try {
      const text = await callClaude(prompt);
      const parsed = parseJSON(text);
      if (!parsed.marry || !parsed.screw || !parsed.kill) throw new Error("Missing categories");
      const finalResults = { ...parsed, profile: { ...profile, gender }, coreValuesWarning, dealbreakerFlags };
      setResults(finalResults);
      setScreen("results");
      ["marry","screw","kill"].forEach(async (type) => {
        const m = parsed[type]; if (!m) return;
        const img = await fetchTMDBImage(m.universe);
        if (img) setTmdbImages(prev => ({ ...prev, [type]: img }));
      });
      callClaude(profilePrompt).then(t => setRomanticProfile(t.trim())).catch(()=>{});
      getAdRecommendations(parsed).then(({recs, streaming}) => { setAdRecs(recs||[]); setStreamingRec(streaming||null); });
    } catch (e) { setError(e.message); setScreen("survey"); }
  };

  const reset = () => {
    setScreen("home"); setExperience(null); setAnswers({}); setName(""); setAttraction(""); setGender("");
    setUniverse(""); setCrushCharacter(""); setCrushUniverse(""); setCurrentSection(0); setSecQIdx(0);
    setResults(null); setFcResult(null); setError(null); setShowShare(false);
    setTmdbImages({}); setAdRecs([]); setStreamingRec(null); setRomanticProfile(null);
    setArenaCharacter(""); setArenaUniverse(""); setArenaMode(""); setArenaWeapon("");
    setArenaResult(null); setArenaShowRematch(false); setArenaRematchInput(""); setArenaCustomStrategy(false);
  };

  const pickExperience = (exp) => {
    setExperience(exp);
    setScreen("intro");
  };
  const retryCategory = () => {
    setResults(null); setFcResult(null); setError(null); setShowShare(false);
    setTmdbImages({}); setAdRecs([]); setStreamingRec(null); setRomanticProfile(null);
    setScreen("pick");
  };

  // ─── Shared share text ────────────────────────────────────────────────────
  const shareTextMKK = results
    ? `THE TYPE IS REVEALED.\n\n💍 MARRY ${results.marry?.character} (${results.marry?.universe})\n💋 KISS ${results.screw?.character} (${results.screw?.universe})\n💀 KILL ${results.kill?.character} (${results.kill?.universe})\n\nFind yours →\nmarrykisskill.com`
    : "";
  // Pronoun helper for the active FC crush. Defaults to neutral "they" if Claude didn't return one.
  const fcPro = (() => {
    const p = (fcResult?.pronoun || "they").toLowerCase().trim();
    if (p === "she") return { subj:"SHE", obj:"HER", subjC:"She's",  thirdS:"S" };
    if (p === "he")  return { subj:"HE",  obj:"HIM", subjC:"He's",   thirdS:"S" };
    if (p === "it")  return { subj:"IT",  obj:"IT",  subjC:"It's",   thirdS:"S" };
    return             { subj:"THEY", obj:"THEM", subjC:"They're", thirdS:"" };
  })();

  const shareTextFC = fcResult
    ? (fcResult.opening_line
        ? `${fcResult.character}'s opening line to me:\n\n"${fcResult.opening_line}"\n\nWhat would yours be? →\nmarrykisskill.com`
        : `I love ${fcResult.character} from ${fcResult.universe}. ${fcPro.subjC} ${fcResult.score}% interested in me.\n\nFind out if YOUR crush is into you →\nmarrykisskill.com`)
    : "";
  const fmtFightTime = (s) => {
    const n = Number(s) || 0;
    if (n < 60) return `${n}s`;
    return `${Math.floor(n/60)}m ${n%60}s`;
  };
  const shareTextArena = arenaResult
    ? (() => {
        const youWon = (arenaResult.winner_role||"").toLowerCase().trim() === "reader";
        const headline = youWon
          ? `${(name||"I").toUpperCase()} TOOK DOWN ${(arenaResult.character||"").toUpperCase()}`
          : `${(arenaResult.character||"").toUpperCase()} BEAT ${(name||"ME").toUpperCase()}`;
        const time = arenaResult.time_seconds ? `· ${fmtFightTime(arenaResult.time_seconds)}` : "";
        const loc = arenaResult.location_pick ? `\n📍 ${arenaResult.location_pick}` : "";
        return `${headline} ${time}${loc}\n\nSee who'd win YOUR fight →\nmarrykisskill.com`;
      })()
    : "";
  const shareTextFeast = arenaResult
    ? (() => {
        const youWon = (arenaResult.winner_role||"").toLowerCase().trim() === "reader";
        const headline = youWon
          ? `${(name||"I").toUpperCase()} OUT-ATE ${(arenaResult.character||"").toUpperCase()}`
          : `${(arenaResult.character||"").toUpperCase()} OUT-ATE ${(name||"ME").toUpperCase()}`;
        const tally = arenaResult.final_count ? `\n🍴 ${arenaResult.final_count}` : "";
        const loc = arenaResult.location_pick ? `\n📍 ${arenaResult.location_pick}` : "";
        return `${headline}${tally}${loc}\n\nSee who'd out-eat YOU →\nmarrykisskill.com`;
      })()
    : "";
  const shareText = experience === "arena" ? shareTextArena
                  : experience === "feast" ? shareTextFeast
                  : experience === "fc"    ? shareTextFC
                                            : shareTextMKK;

  const headlineLines = results ? generateHeadline(name) : [];
  const lowAxes = results
    ? findLowAxes(results.profile)
    : fcResult
    ? findLowAxes(fcResult.profile)
    : new Set();
  const mastheadTitle =
    experience === "fc"    ? "FANTASY CRUSH." :
    experience === "arena" ? "FANTASY ARENA." :
    experience === "feast" ? "FANTASY FEAST." :
                             "MARRY. KISS. KILL.";
  // Roll fresh loading-screen flavor lines each time the user enters the loading state.
  const loadingLines = useMemo(() => {
    const pool1 = experience === "arena" ? ARENA_LOADING_LINES_1
                : experience === "feast" ? FEAST_LOADING_LINES_1
                : LOADING_LINES_1;
    const pool2 = experience === "arena" ? ARENA_LOADING_LINES_2
                : experience === "feast" ? FEAST_LOADING_LINES_2
                : LOADING_LINES_2;
    return {
      a: pool1[Math.floor(Math.random() * pool1.length)],
      b: pool2[Math.floor(Math.random() * pool2.length)],
    };
  }, [screen, experience]);

  return (
    <div style={{ minHeight:"100vh", background:C.paper, color:C.ink, fontFamily:FONT.display, padding:"16px 14px 32px" }}>
      <div style={{ maxWidth:"480px", margin:"0 auto" }}>

        {/* ── HOME / EXPERIENCE PICKER ──────────────────────────────── */}
        {screen === "home" && (
          <div>
            <div style={{ textAlign:"center", padding:"56px 0 32px" }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"4px", textTransform:"uppercase", color:C.inkSoft, marginBottom:"8px" }}>★ THE FANTASY EDITION ★</div>
              <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"17px", lineHeight:1.45, color:C.inkSoft, margin:"0 12px" }}>Pick your story.</p>
            </div>

            {/* OC Manager — front door for managing your roster */}
            <button onClick={()=>{ setError(null); setScreen("oc-manager"); }} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`2px solid ${C.ink}`, padding:"14px 16px",
              background:C.paperSoft, color:C.ink, marginBottom:"22px",
              fontFamily:FONT.display,
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:"10px" }}>
                <div>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2.5px", textTransform:"uppercase", color:C.red, marginBottom:"4px" }}>★ YOUR ROSTER ★</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, letterSpacing:"-0.5px" }}>OC MANAGER.</div>
                  <p style={{ margin:"4px 0 0", fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", color:C.inkSoft, lineHeight:1.3 }}>Edit fighters · take surveys · review profiles</p>
                </div>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", color:C.ink, fontWeight:700, whiteSpace:"nowrap" }}>{(youChar ? 1 : 0) + ocRoster.length} on file →</span>
              </div>
            </button>

            {/* Product 1 — MKK */}
            <button onClick={()=>pickExperience("mkk")} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.paper, color:C.ink, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.ink}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"10px" }}>★ EXPERIENCE ONE ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>MARRY. KISS. KILL.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:C.inkSoft, lineHeight:1.4 }}>Who's your match?</p>
              <div style={{ borderTop:`1px solid ${C.ink}`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.ink, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            {/* Product 2 — FC */}
            <button onClick={()=>pickExperience("fc")} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.ink, color:C.paper, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.red}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"10px" }}>★ EXPERIENCE TWO ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>FANTASY CRUSH.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:"rgba(241,232,210,0.7)", lineHeight:1.4 }}>Do they love you back?</p>
              <div style={{ borderTop:`1px solid rgba(241,232,210,0.25)`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            {/* Product 3 — Fantasy Arena */}
            <button onClick={()=>pickExperience("arena")} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.red, color:C.paper, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.ink}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.paper, marginBottom:"10px", opacity:0.85 }}>★ EXPERIENCE THREE ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>FANTASY ARENA.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:"rgba(241,232,210,0.85)", lineHeight:1.4 }}>Pick a fight. See who wins.</p>
              <div style={{ borderTop:`1px solid rgba(241,232,210,0.35)`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.paper, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            {/* Product 4 — Fantasy Feast */}
            <button onClick={()=>pickExperience("feast")} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.paperSoft, color:C.ink, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.red}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"10px" }}>★ EXPERIENCE FOUR ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>FANTASY FEAST.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:C.inkSoft, lineHeight:1.4 }}>Eating contest. Loser unbuckles first.</p>
              <div style={{ borderTop:`1px solid ${C.ink}`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.ink, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            {/* Product 5 — Fantasy Duel */}
            <button onClick={()=>setScreen("oc-slots")} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.paper, color:C.ink, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.ink}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"10px" }}>★ EXPERIENCE FIVE ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>OC PLAYGROUND.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:C.inkSoft, lineHeight:1.4 }}>Build your own fighter. Pick an opponent. Watch them duel.</p>
              <div style={{ borderTop:`1px solid ${C.ink}`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.ink, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            {/* Product 6 — OC: Marry. Kiss. Kill. */}
            <button onClick={()=>{
              setOcMkkPlayer(null); setOcMkkAttraction(""); setOcMkkUniverse("");
              setOcMkkResult(null); setError(null); setScreen("oc-mkk-pick");
            }} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.ink, color:C.paper, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.red}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"10px" }}>★ EXPERIENCE SIX ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>OC: MARRY. KISS. KILL.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:"rgba(241,232,210,0.75)", lineHeight:1.4 }}>Play MKK as you. Or as your OC. Or as Toad.</p>
              <div style={{ borderTop:`1px solid rgba(241,232,210,0.25)`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            {/* Product 7 — OC: Matchmaker */}
            <button onClick={()=>{
              setOcMmPlayer(null); setOcMmTarget("free"); setOcMmCrushName(""); setOcMmCrushUniverse("");
              setOcMmResult(null); setError(null); setScreen("oc-mm-pick");
            }} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.red, color:C.paper, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.ink}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.paper, marginBottom:"10px", opacity:0.85 }}>★ EXPERIENCE SEVEN ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>OC: MATCHMAKER.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:"rgba(241,232,210,0.9)", lineHeight:1.4 }}>Your OC has a crush. Are they into them back?</p>
              <div style={{ borderTop:`1px solid rgba(241,232,210,0.35)`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.paper, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            {/* Product 8 — OC: Feast */}
            <button onClick={()=>{
              setOcFeastPlayer(null); setOcFeastTarget("free"); setOcFeastOppName(""); setOcFeastOppUniverse("");
              setOcFeastFood("Hot dogs"); setOcFeastResult(null); setError(null); setScreen("oc-feast-pick");
            }} style={{
              display:"block", width:"100%", textAlign:"left", cursor:"pointer",
              border:`3px solid ${C.ink}`, padding:"22px 22px 20px",
              background:C.paperSoft, color:C.ink, marginBottom:"22px",
              boxShadow:`6px 6px 0 ${C.red}`,
              fontFamily:FONT.display,
            }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"10px" }}>★ EXPERIENCE EIGHT ★</div>
              <h2 style={{ margin:"0 0 8px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px" }}>OC: FEAST.</h2>
              <p style={{ margin:"0 0 14px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", color:C.inkSoft, lineHeight:1.4 }}>Eating contest. Loser unbuckles first.</p>
              <div style={{ borderTop:`1px solid ${C.ink}`, paddingTop:"10px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.ink, fontWeight:700 }}>Begin →</span>
              </div>
            </button>

            <PageFooter />
          </div>
        )}

        {/* ── INTRO ─────────────────────────────────────────────────── */}
        {screen === "intro" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
              <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>
                {experience === "fc" ? "FANTASY CRUSH" : experience === "arena" ? "FANTASY ARENA" : experience === "feast" ? "FANTASY FEAST" : "MARRY · KISS · KILL"}
              </div>
              <span style={{ width:"60px" }}/>
            </div>
            <Masthead title={mastheadTitle} subtitle="STRICTLY FICTIONAL" />

            <div style={{ textAlign:"center", marginBottom:"24px", padding:"16px 0" }}>
              <div style={{
                display:"inline-block",
                border:`4px solid ${C.red}`,
                color:C.red,
                padding:"14px 20px 12px",
                transform:"rotate(-3deg)",
                fontFamily:FONT.display, fontWeight:900,
                fontSize: experience === "fc" ? "44px" : experience === "arena" ? "46px" : experience === "feast" ? "46px" : "52px",
                lineHeight:0.95, letterSpacing:"-1px",
                textTransform:"uppercase",
              }}>
                {experience === "fc" ? <>DO THEY LOVE<br/>YOU BACK?</> : experience === "arena" ? <>CAN YOU<br/>BEAT THEM?</> : experience === "feast" ? <>CAN YOU<br/>OUT-EAT THEM?</> : <>WHO WILL<br/>IT BE?</>}
              </div>
              <p style={{ margin:"22px 8px 6px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", lineHeight:1.45, color:C.inkSoft }}>
                {experience === "fc"
                  ? "Pick a fictional crush. Answer twenty-five questions. We'll tell you exactly what they think of you."
                  : experience === "arena"
                  ? "Pick a fictional opponent. Step into the ring. We'll write the fight, the winner, and the post-game."
                  : experience === "feast"
                  ? "Pick a fictional opponent. Pick a food. We'll run the eating contest, plate by plate."
                  : "Three fictional matches. One reveal your group chat isn't ready for."}
              </p>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", marginTop:"6px", color:C.inkSoft }}>{experience === "arena" ? "By Our Ringside Reporter" : experience === "feast" ? "By Our Resident Glutton" : "By Our Resident Matchmaker"}</div>
            </div>

            <RuleHr double mb="18px" />

            {/* Form */}
            <div style={{ border:`2px solid ${C.ink}`, padding:"18px 18px 20px", background:C.paperSoft }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", textAlign:"center", color:C.inkMute, marginBottom:"14px" }}>— TELL US ABOUT YOU —</div>

              <Label>Your Name</Label>
              <TextInput value={name} onChange={setName} placeholder="e.g. Jamie" />

              <div style={{ height:"16px" }}/>
              <Label>You Are…</Label>
              <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
                {["Man","Woman","Non-binary"].map(opt => (
                  <Pill key={opt} selected={gender === opt} onClick={()=>setGender(opt)} size="sm">{opt}</Pill>
                ))}
              </div>

              {experience === "mkk" && (
                <>
                  <div style={{ height:"16px" }}/>
                  <Label>Into…</Label>
                  <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                    {["Men","Women","Both"].map(opt =>
                      <Pill key={opt} selected={attraction === opt} onClick={()=>setAttraction(opt)}>{opt}</Pill>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Hero box — Pick Your World (MKK), Pick Your Crush (FC), or Pick Your Opponent (Arena) */}
            <div style={{ height:"20px" }}/>
            {experience === "fc" ? (
              <div style={{ border:`3px solid ${C.ink}`, padding:"22px 20px 22px", background:C.paperSoft, boxShadow:`6px 6px 0 ${C.ink}` }}>
                <h3 style={{ margin:"0 0 16px", fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase", textAlign:"center" }}>
                  Pick Your Crush
                </h3>
                <Label>Character</Label>
                <TextInput value={crushCharacter} onChange={setCrushCharacter} placeholder="e.g. Hermione Granger" />
                <div style={{ height:"14px" }}/>
                <Label>From</Label>
                <TextInput value={crushUniverse} onChange={setCrushUniverse} placeholder="e.g. Harry Potter" />
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginTop:"10px", textAlign:"center" }}>
                  Any fictional character. We won't judge.
                </div>
              </div>
            ) : experience === "arena" ? (
              <div style={{ border:`3px solid ${C.ink}`, padding:"22px 20px 22px", background:C.paperSoft, boxShadow:`6px 6px 0 ${C.ink}` }}>
                <h3 style={{ margin:"0 0 16px", fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase", textAlign:"center" }}>
                  Pick Your Opponent
                </h3>
                <Label>Character</Label>
                <TextInput value={arenaCharacter} onChange={setArenaCharacter} placeholder="e.g. Geralt of Rivia" />
                <div style={{ height:"14px" }}/>
                <Label>From</Label>
                <TextInput value={arenaUniverse} onChange={setArenaUniverse} placeholder="e.g. The Witcher" />

                <div style={{ height:"18px" }}/>
                <Label>Fight Mode</Label>
                <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
                  {[{k:"unarmed",l:"Unarmed"},{k:"armed",l:"Armed"}].map(opt => (
                    <Pill key={opt.k} selected={arenaMode === opt.k} onClick={()=>{ setArenaMode(opt.k); if (opt.k === "unarmed") setArenaWeapon(""); }} size="sm">{opt.l}</Pill>
                  ))}
                </div>

                {arenaMode === "armed" && (
                  <>
                    <div style={{ height:"16px" }}/>
                    <Label>Your Weapon</Label>
                    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                      {ARENA_WEAPONS.map(w => (
                        <Pill key={w} selected={arenaWeapon === w} onClick={()=>setArenaWeapon(w)}>{w}</Pill>
                      ))}
                    </div>
                  </>
                )}

                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginTop:"12px", textAlign:"center" }}>
                  Your opponent fights with whatever they canonically have.
                </div>
              </div>
            ) : experience === "feast" ? (
              <div style={{ border:`3px solid ${C.ink}`, padding:"22px 20px 22px", background:C.paperSoft, boxShadow:`6px 6px 0 ${C.ink}` }}>
                <h3 style={{ margin:"0 0 16px", fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase", textAlign:"center" }}>
                  Pick Your Opponent
                </h3>
                <Label>Character</Label>
                <TextInput value={arenaCharacter} onChange={setArenaCharacter} placeholder="e.g. Joey Tribbiani" />
                <div style={{ height:"14px" }}/>
                <Label>From</Label>
                <TextInput value={arenaUniverse} onChange={setArenaUniverse} placeholder="e.g. Friends" />

                <div style={{ height:"18px" }}/>
                <Label>The Food</Label>
                <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                  {FEAST_FOODS.map(f => (
                    <Pill key={f} selected={arenaWeapon === f} onClick={()=>setArenaWeapon(f)}>{f}</Pill>
                  ))}
                </div>

                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginTop:"12px", textAlign:"center" }}>
                  Standard contest rules. First to tap out loses.
                </div>
              </div>
            ) : (
              <div style={{ border:`3px solid ${C.ink}`, padding:"22px 20px 22px", background:C.paperSoft, boxShadow:`6px 6px 0 ${C.ink}` }}>
                <h3 style={{ margin:"0 0 16px", fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase", textAlign:"center" }}>
                  Pick Your World
                </h3>
                <TextInput value={universe} onChange={setUniverse} placeholder="e.g. Harry Potter, Star Wars, Marvel…" />
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginTop:"8px", textAlign:"center" }}>
                  Leave blank for a wild card
                </div>
              </div>
            )}

            <div style={{ height:"22px" }}/>
            {experience === "fc" ? (
              <TabloidButton
                onClick={() => { if (name.trim() && gender && crushCharacter.trim() && crushUniverse.trim()) setScreen("survey"); }}
                disabled={!(name.trim() && gender && crushCharacter.trim() && crushUniverse.trim())}
              >
                ✦ Find Out →
              </TabloidButton>
            ) : experience === "arena" ? (
              <TabloidButton
                onClick={() => { if (name.trim() && gender && arenaCharacter.trim() && arenaUniverse.trim() && arenaMode && (arenaMode === "unarmed" || arenaWeapon)) setScreen("survey"); }}
                disabled={!(name.trim() && gender && arenaCharacter.trim() && arenaUniverse.trim() && arenaMode && (arenaMode === "unarmed" || arenaWeapon))}
              >
                ✦ Step Into The Ring →
              </TabloidButton>
            ) : experience === "feast" ? (
              <TabloidButton
                onClick={() => { if (name.trim() && gender && arenaCharacter.trim() && arenaUniverse.trim() && arenaWeapon) setScreen("survey"); }}
                disabled={!(name.trim() && gender && arenaCharacter.trim() && arenaUniverse.trim() && arenaWeapon)}
              >
                ✦ Take A Seat →
              </TabloidButton>
            ) : (
              <TabloidButton
                onClick={() => { if (name.trim() && attraction && gender) setScreen("survey"); }}
                disabled={!(name.trim() && attraction && gender)}
              >
                ✦ Reveal My Destiny →
              </TabloidButton>
            )}

            {typeof window !== "undefined" && window.location.hostname === "localhost" && (
              <div style={{ textAlign:"center", marginTop:"4px" }}>
                <TextLink onClick={testMode}>⚡ Skip Ahead (test mode)</TextLink>
              </div>
            )}

            <PageFooter />
          </div>
        )}

        {/* ── SURVEY ────────────────────────────────────────────────── */}
        {screen === "survey" && currentQ && (
          <div>
            <Masthead title={mastheadTitle} no={String(globalIdx+1)} subtitle="SPILL THE TEA" compact />

            <MetaRow
              left={`ROUND ${currentSection+1} · ${sec.title.toUpperCase()}`}
              right={`QUESTION ${globalIdx+1} OF ${activeTotal}`}
              border={false}
            />
            <RuleHr mb="10px" />

            {/* Progress bar */}
            <div style={{ height:"10px", border:`1px solid ${C.ink}`, background:C.paperSoft, marginBottom:"18px", position:"relative" }}>
              <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${progress}%`, background:hatched(C.ink) }}/>
            </div>

            {error && (
              <div style={{ background:C.black, color:C.paper, padding:"10px 14px", border:`2px solid ${C.red}`, marginBottom:"14px", fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1px" }}>
                ⚠ {error}
              </div>
            )}

            {/* Question card */}
            <div style={{ border:`2px solid ${C.ink}`, padding:"20px 18px", background:C.paperSoft, marginBottom:"16px" }}>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginBottom:"8px" }}>#{globalIdx+1} · {sec.subtitle}</div>
              <p style={{ fontFamily:FONT.display, fontSize:"21px", fontWeight:700, lineHeight:1.3, margin:"0 0 16px" }}>{currentQ.text}</p>

              {currentQ.type === "number" || currentQ.type === "text" ? (
                <>
                  <input
                    type="text"
                    inputMode={currentQ.type === "number" ? "decimal" : "text"}
                    value={answers[currentQ.id] ?? ""}
                    onChange={e => setAnswer(currentQ.id, currentQ.type === "number" ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value)}
                    placeholder={currentQ.placeholder}
                    style={{
                      width:"100%", padding:"14px 14px", fontSize:"18px",
                      fontFamily:FONT.display, fontWeight:700,
                      border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
                      outline:"none", borderRadius:0,
                    }}
                  />
                  {currentQ.unit && (
                    <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginTop:"8px", textAlign:"right" }}>
                      {currentQ.unit}
                    </div>
                  )}
                </>
              ) : currentQ.type === "choice" ? (
                <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                  {currentQ.choices.map(opt => (
                    <Pill key={opt} selected={answers[currentQ.id] === opt} onClick={()=>setAnswer(currentQ.id, opt)}>{opt}</Pill>
                  ))}
                </div>
              ) : (
                <>
                  <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginBottom:"8px" }}>
                    <span>Strongly Disagree</span><span>Strongly Agree</span>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:"4px" }}>
                    {[1,2,3,4,5,6,7].map(val => {
                      const sel = answers[currentQ.id] === val;
                      return (
                        <button key={val} onClick={()=>setAnswer(currentQ.id, val)} style={{
                          aspectRatio:"1/1",
                          background: sel ? C.ink : C.paper,
                          color:      sel ? C.paper : C.ink,
                          border:`1.5px solid ${C.ink}`,
                          fontFamily:FONT.display, fontWeight:900, fontSize:"18px",
                          cursor:"pointer",
                        }}>{val}</button>
                      );
                    })}
                  </div>

                  <div style={{ minHeight:"18px", marginTop:"10px", textAlign:"center" }}>
                    {answers[currentQ.id] && (
                      <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red }}>
                        {SCALE_LABELS[answers[currentQ.id]]}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            <TabloidButton onClick={goNext} disabled={!canAdvance}>
              {globalIdx < activeTotal-1 ? "Next →" : experience === "arena" ? "Ring The Bell ✦" : experience === "feast" ? "Bring The Food ✦" : experience === "fc" ? "Find Out ✦" : "Reveal My Three ✦"}
            </TabloidButton>

            {globalIdx > 0 && (
              <div style={{ textAlign:"center", marginTop:"4px" }}>
                <TextLink onClick={goBack}>← Go Back</TextLink>
              </div>
            )}

            <PageFooter />
          </div>
        )}

        {/* ── OC SHEET PREVIEW (WIP) ────────────────────────────────── */}
        {screen === "oc-preview" && (() => {
          // Two sample characters to lock in the visual feel before we build the engine.
          const SAMPLE_A = {
            name: "Sparkle",
            tagline: "Glitter tornado, somehow",
            habitat: "Forest",
            habitatIcon: "🌲",
            size: "Medium",
            age: 19,
            personality: { warmth:7, humor:9, energy:9, confidence:8, optimism:7, earnestness:3 },
            stats: { STR: 8, SPD: 6, DUR: 5, INT: 3, CUN: 10, WIL: 7 },
            signature: { name: "Glitter Bomb", effect: "Blinds opponent. Stains everything for weeks." },
            catchphrase: "You're gonna look fabulous on the way down.",
            buildNo: "047.12",
          };
          const SAMPLE_B = {
            name: "Boris",
            tagline: "Sad mountain in human form",
            habitat: "Mountain",
            habitatIcon: "⛰",
            size: "Large",
            age: 34,
            personality: { warmth:4, humor:2, energy:2, confidence:7, optimism:2, earnestness:9 },
            stats: { STR: 9, SPD: 3, DUR: 8, INT: 6, CUN: 4, WIL: 8 },
            signature: { name: "The Wait", effect: "Holds opponent in a bear hug until they apologize." },
            catchphrase: "Hit me. I have nowhere to be.",
            buildNo: "047.13",
          };

          // Quick HP derivation: DUR * 8 + SIZE bonus.
          const sizeBonus = { Tiny: -10, Small: -5, Medium: 0, Large: +10, Huge: +20 };
          const hp = (c) => c.stats.DUR * 8 + (sizeBonus[c.size] || 0) + 30;

          // Battle-relevant total — weighted: STR, CUN, WIL count more than INT for a fight.
          const fightScore = (c) => c.stats.STR * 1.2 + c.stats.SPD * 1.1 + c.stats.DUR * 1.1
                                  + c.stats.CUN * 1.0 + c.stats.WIL * 0.9 + c.stats.INT * 0.6
                                  + (sizeBonus[c.size] || 0) * 0.05;
          const a = fightScore(SAMPLE_A), b = fightScore(SAMPLE_B);
          // Soften odds so weaker side always has a real shot.
          const raw = a / (a + b);
          const softened = 0.5 + (raw - 0.5) * 0.7; // pulls toward 50/50
          const oddsA = Math.round(softened * 100);
          const oddsB = 100 - oddsA;

          // Per-stat advantage breakdown — used in the explanation text.
          const advantages = [];
          ["STR","SPD","DUR","INT","CUN","WIL"].forEach(k => {
            const d = SAMPLE_A.stats[k] - SAMPLE_B.stats[k];
            if (Math.abs(d) >= 2) advantages.push({ k, d, leader: d > 0 ? SAMPLE_A.name : SAMPLE_B.name });
          });

          const CharacterCard = ({ c, accent }) => (
            <div style={{ border:`3px solid ${C.ink}`, background:C.paper, marginBottom:"16px", boxShadow:`6px 6px 0 ${accent || C.ink}` }}>
              {/* Header */}
              <div style={{ borderBottom:`2px solid ${C.ink}`, padding:"12px 14px 10px", background:accent || C.paperSoft }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color: accent ? C.paper : C.inkMute, opacity: accent ? 0.85 : 1 }}>
                    CHARACTER FILE · BUILD #{c.buildNo}
                  </div>
                </div>
                <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"36px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase", color: accent ? C.paper : C.ink }}>
                  {c.name}
                </div>
                {c.tagline && (
                  <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color: accent ? "rgba(241,232,210,0.85)" : C.inkSoft, marginTop:"2px" }}>
                    — {c.tagline} —
                  </div>
                )}
              </div>

              {/* Habitat / Size / Age strip */}
              <div style={{ borderBottom:`1.5px solid ${C.ink}`, padding:"8px 14px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.ink, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:"8px", background:C.paperSoft }}>
                <span>{c.habitatIcon} {c.habitat}</span>
                <span>· {c.size} ·</span>
                <span>Age {c.age}</span>
              </div>

              {/* Stat bars */}
              <div style={{ padding:"14px" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"10px" }}>★ STATS ★</div>
                {Object.entries(c.stats).map(([k, v]) => (
                  <div key={k} style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"6px" }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", fontWeight:700, color:C.ink, width:"32px" }}>{k}</div>
                    <div style={{ flex:1, height:"12px", border:`1.5px solid ${C.ink}`, background:C.paperSoft, position:"relative" }}>
                      <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${v*10}%`, background:C.ink }}/>
                    </div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"14px", color:C.ink, width:"22px", textAlign:"right" }}>{v}</div>
                  </div>
                ))}

                {/* HP bar — fatter */}
                <div style={{ marginTop:"14px", display:"flex", alignItems:"center", gap:"10px" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", fontWeight:700, color:C.red, width:"32px" }}>HP</div>
                  <div style={{ flex:1, height:"18px", border:`2px solid ${C.ink}`, background:C.paperSoft, position:"relative" }}>
                    <div style={{ position:"absolute", top:0, left:0, bottom:0, width:"100%", background:C.red }}/>
                  </div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px", color:C.red, width:"40px", textAlign:"right" }}>{hp(c)}</div>
                </div>
              </div>

              {/* Signature move */}
              <div style={{ borderTop:`1.5px dashed ${C.ink}`, padding:"12px 14px", background:C.paperSoft }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ SIGNATURE MOVE ★</div>
                <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"17px", lineHeight:1.2, color:C.ink }}>"{c.signature.name}"</div>
                <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", color:C.inkSoft, marginTop:"2px" }}>{c.signature.effect}</div>
              </div>

              {/* Personality + catchphrase */}
              <div style={{ borderTop:`1.5px solid ${C.ink}`, padding:"12px 14px" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"8px" }}>PERSONALITY</div>
                {c.personality && OC_PERSONALITY_AXES.map(axis => {
                  const v = c.personality[axis.key] ?? 5;
                  return (
                    <div key={axis.key} style={{ marginBottom:"6px", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.ink }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"2px" }}>
                        <span style={{ fontWeight: v <= 4 ? 700 : 400, opacity: v <= 4 ? 1 : 0.55 }}>{axis.low.toUpperCase()}</span>
                        <span style={{ fontWeight: v >= 7 ? 700 : 400, opacity: v >= 7 ? 1 : 0.55 }}>{axis.high.toUpperCase()}</span>
                      </div>
                      <div style={{ height:"6px", border:`1px solid ${C.ink}`, background:C.paperSoft, position:"relative" }}>
                        <div style={{ position:"absolute", top:"-3px", left:`calc(${(v-1)/9*100}% - 5px)`, width:"10px", height:"10px", background:C.red, border:`1.5px solid ${C.ink}` }}/>
                      </div>
                    </div>
                  );
                })}
                <div style={{ color:C.red, fontFamily:FONT.display, fontSize:"16px", lineHeight:1, marginTop:"12px" }}>❝</div>
                <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", lineHeight:1.3 }}>{c.catchphrase}</p>
              </div>

              {/* Footer */}
              <div style={{ borderTop:`2px solid ${C.ink}`, padding:"6px 14px", textAlign:"center", fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, background:C.paperSoft }}>
                Built for Battle · marrykisskill.com
              </div>
            </div>
          );

          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC PLAYGROUND." subtitle="SHEET DRAFT · LOCK THE LOOK" compact />

              {/* Draft note */}
              <div style={{ border:`2px dashed ${C.red}`, padding:"12px 14px", marginBottom:"16px", background:C.paperSoft }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ DRAFT · LOCK THE LOOK ★</div>
                <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                  Sample fighters, no fight yet. Tap "Build Your Own" to make a real one.
                </p>
              </div>

              <SectionHeader title="FIGHTER A" suffix="the challenger" />
              <CharacterCard c={SAMPLE_A} accent={C.red} />

              <div style={{ textAlign:"center", margin:"6px 0 14px" }}>
                <div style={{ display:"inline-block", border:`2px solid ${C.ink}`, padding:"4px 12px", fontFamily:FONT.display, fontWeight:900, fontSize:"22px", letterSpacing:"3px", background:C.ink, color:C.paper }}>
                  VS
                </div>
              </div>

              <SectionHeader title="FIGHTER B" suffix="the challenged" />
              <CharacterCard c={SAMPLE_B} accent={C.ink} />

              {/* Pre-fight odds */}
              <div style={{ height:"6px" }}/>
              <SectionHeader title="THE ODDS" suffix="before a punch is thrown" />
              <div style={{ border:`3px solid ${C.ink}`, padding:"14px 14px 16px", marginBottom:"14px", background:C.paperSoft }}>
                {/* Odds bar */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"6px" }}>
                  <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.red }}>{SAMPLE_A.name} {oddsA}%</span>
                  <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.ink }}>{oddsB}% {SAMPLE_B.name}</span>
                </div>
                <div style={{ height:"20px", border:`2px solid ${C.ink}`, display:"flex" }}>
                  <div style={{ width:`${oddsA}%`, background:C.red }}/>
                  <div style={{ width:`${oddsB}%`, background:C.ink }}/>
                </div>

                {/* Explanation */}
                <div style={{ marginTop:"14px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"6px" }}>★ THE TALE OF THE TAPE ★</div>
                <ul style={{ margin:0, paddingLeft:"18px", display:"flex", flexDirection:"column", gap:"6px" }}>
                  {advantages.length === 0 && (
                    <li style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>Stats are dead even. This one's a coin toss with attitude.</li>
                  )}
                  <li style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    <strong>Sparkle</strong> has +6 cunning. She's seen things.
                  </li>
                  <li style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    <strong>Boris</strong> has +3 strength and +3 durability. He is, frankly, built like a fridge with a grudge.
                  </li>
                  <li style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    The Glitter Bomb vs The Wait. Whoever lands their signature first probably walks out clean.
                  </li>
                  <li style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.inkSoft }}>
                    Anything can happen. Stranger things have.
                  </li>
                </ul>
              </div>

              {/* Sample of HP draining in a beat */}
              <SectionHeader title="MID-FIGHT PREVIEW" suffix="how the play-by-play feels" />
              <div style={{ border:`2px dashed ${C.ink}`, padding:"14px", marginBottom:"14px" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"8px" }}>BEAT 3 OF 5</div>
                <p style={{ margin:"0 0 12px", fontFamily:FONT.display, fontSize:"15px", lineHeight:1.45 }}>Sparkle ducks, flings a handful of forest litter — Boris swings through the cloud, off-balance and visibly annoyed. She clips his jaw on the way past. Glitter, somehow, everywhere.</p>
                {/* HP bars after the beat */}
                <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", fontWeight:700, color:C.red, width:"56px" }}>SPARKLE</div>
                    <div style={{ flex:1, height:"14px", border:`1.5px solid ${C.ink}`, background:C.paperSoft, position:"relative" }}>
                      <div style={{ position:"absolute", top:0, left:0, bottom:0, width:"72%", background:C.red }}/>
                    </div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"13px", color:C.red, width:"56px", textAlign:"right" }}>50/{hp(SAMPLE_A)}</div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", fontWeight:700, color:C.ink, width:"56px" }}>BORIS</div>
                    <div style={{ flex:1, height:"14px", border:`1.5px solid ${C.ink}`, background:C.paperSoft, position:"relative" }}>
                      <div style={{ position:"absolute", top:0, left:0, bottom:0, width:"55%", background:C.ink }}/>
                    </div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"13px", color:C.ink, width:"56px", textAlign:"right" }}>56/{hp(SAMPLE_B)}</div>
                  </div>
                </div>
              </div>

              <TabloidButton onClick={()=>setScreen("oc-create")}>✦ Build Your Own →</TabloidButton>
              <div style={{ height:"8px" }}/>
              <TabloidButton variant="secondary" onClick={()=>setScreen("home")}>← Back To Home</TabloidButton>

              <PageFooter />
            </div>
          );
        })()}

        {/* ── OC ROSTER PICKER (scenario needs an OC and roster has 2+) ─ */}
        {/* ── OC vs OC combined matchup picker ─────────────────────── */}
        {screen === "oc-oc-pick" && (() => {
          // Pick + deselect for both slots in a single view, with a VS scorecard at the top.
          // Tap a roster card to fill the next empty slot. Tap a filled slot in the scorecard to clear it.
          // Tap a roster card that's already selected to deselect it from whichever slot it occupies.
          const pickFor = (oc) => {
            // If this OC is already in a slot, deselect.
            if (slotA && slotA.id === oc.id) { setSlotA(null); setSlotAType(null); return; }
            if (slotB && slotB.id === oc.id) { setSlotB(null); setSlotBType(null); return; }
            // Fill the first empty slot.
            if (!slotA) { setSlotA(oc); setSlotAType("oc"); return; }
            if (!slotB) { setSlotB(oc); setSlotBType("oc"); return; }
            // Both filled — replace slot A (most-recent-pick goes to A so user can iterate B).
            setSlotA(oc); setSlotAType("oc");
          };
          const slotChip = (slot, slotType, label, onClear, color) => (
            <div style={{ flex:1, padding:"12px 10px", background: slot ? color : C.paperSoft, color: slot ? C.paper : C.inkMute, border:`2px solid ${C.ink}`, minHeight:"96px", display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", textAlign:"center", cursor: slot ? "pointer" : "default" }}
                 onClick={slot ? onClear : undefined}>
              <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.85, marginBottom:"4px" }}>{label}</div>
              {slot ? (
                <>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>{slot.name}</div>
                  {slot.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"11px", opacity:0.85, marginTop:"3px" }}>— {slot.tagline} —</div>}
                  <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", opacity:0.7, marginTop:"6px", textDecoration:"underline" }}>tap to clear</div>
                </>
              ) : (
                <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.inkSoft }}>Tap an OC below</div>
              )}
            </div>
          );
          const canFight = slotA && slotB;
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>{ setSlotA(null); setSlotB(null); setSlotAType(null); setSlotBType(null); setOcScenario(null); setScreen("oc-slots"); }}>← Back</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC PLAYGROUND." subtitle="PICK TWO FIGHTERS" compact />

              {/* VS scorecard */}
              <div style={{ display:"flex", gap:"8px", alignItems:"stretch", marginBottom:"6px" }}>
                {slotChip(slotA, slotAType, "FIGHTER A", () => { setSlotA(null); setSlotAType(null); }, C.red)}
                {slotChip(slotB, slotBType, "FIGHTER B", () => { setSlotB(null); setSlotBType(null); }, C.ink)}
              </div>
              <div style={{ textAlign:"center", margin:"-6px 0 12px", position:"relative", zIndex:1 }}>
                <span style={{ display:"inline-block", border:`2px solid ${C.ink}`, padding:"2px 10px", fontFamily:FONT.display, fontWeight:900, fontSize:"16px", letterSpacing:"3px", background:C.ink, color:C.paper }}>VS</span>
              </div>

              <div style={{ minHeight:"70px" }}>
                {canFight && (
                  <div style={{ animation:"ringBellPop 360ms cubic-bezier(0.34, 1.56, 0.64, 1) both", transformOrigin:"center" }}>
                    <TabloidButton onClick={()=>{
                      if (!canFight) return;
                      setOcBattleSim(null); setOcBattleNarr(null);
                      setScreen("oc-prefight");
                    }}>
                      ✦ Ring The Bell →
                    </TabloidButton>
                    <style>{`@keyframes ringBellPop{0%{transform:scale(0.6);opacity:0}55%{transform:scale(1.06);opacity:1}80%{transform:scale(0.98)}100%{transform:scale(1);opacity:1}}`}</style>
                  </div>
                )}
              </div>

              <div style={{ height:"22px" }}/>
              <SectionHeader title="YOUR OCS" suffix="tap to select / deselect" />
              <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"20px" }}>
                {ocRoster.map(oc => {
                  const inA = slotA && slotA.id === oc.id;
                  const inB = slotB && slotB.id === oc.id;
                  const selected = inA || inB;
                  return (
                    <button key={oc.id} onClick={()=>pickFor(oc)} style={{
                      textAlign:"left", padding:"12px 14px",
                      background: selected ? (inA ? C.red : C.ink) : C.paper,
                      color: selected ? C.paper : C.ink,
                      border:`2px solid ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                      boxShadow:`4px 4px 0 ${C.ink}`,
                    }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                        <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"20px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>{oc.name}</div>
                        {selected && <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", fontWeight:700 }}>★ {inA ? "FIGHTER A" : "FIGHTER B"}</span>}
                      </div>
                      {oc.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", opacity:0.85, marginTop:"4px" }}>— {oc.tagline} —</div>}
                      <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", opacity:0.75, marginTop:"6px" }}>
                        HP {oc.hp ?? "?"} · {oc.abilities?.length || 0} abilities · {oc.features?.length || 0} features
                      </div>
                    </button>
                  );
                })}
                <button type="button" onClick={()=>{
                  // Build a fresh transient OC for whichever slot is empty next.
                  const whichSlot = !slotA ? "slotA" : !slotB ? "slotB" : "slotA";
                  setOcBuildTarget(whichSlot);
                  setScreen("oc-create");
                }} style={{
                  textAlign:"center", padding:"14px 16px", background:"transparent", color:C.ink,
                  border:`2px dashed ${C.ink}`, cursor:"pointer",
                  fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
                }}>✦ Or Build A Fresh One</button>
              </div>

              <PageFooter />
            </div>
          );
        })()}

        {screen === "oc-roster-pick" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
              <TextLink onClick={()=>{ setOcRosterPickFor(null); setScreen("oc-slots"); }}>← Back</TextLink>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
              <span style={{ width:"60px" }}/>
            </div>
            <Masthead title="OC PLAYGROUND." subtitle={`PICK OC FOR FIGHTER ${ocRosterPickFor || "A"}`} compact />

            <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"20px" }}>
              {ocRoster.filter(oc => {
                // For OC vs OC: hide the OC that's already filling the other slot so the user can't accidentally clone-fight.
                if (ocScenario !== "oc-oc") return true;
                const otherSlotChar = ocRosterPickFor === "A" ? slotB : slotA;
                return !otherSlotChar || otherSlotChar.id !== oc.id;
              }).map((oc, idx) => (
                <button key={oc.id} onClick={()=>{
                  const which = ocRosterPickFor;
                  if (which === "A") { setSlotA(oc); setSlotAType("oc"); }
                  else               { setSlotB(oc); setSlotBType("oc"); }
                  setOcRosterPickFor(null);
                  if (ocScenario) {
                    const o = which === "A" ? { slotA: oc } : { slotB: oc };
                    advanceScenario(ocScenario, o);
                  } else {
                    setScreen("oc-slots");
                  }
                }} style={{
                  textAlign:"left", padding:"14px 16px", background:C.paper, color:C.ink,
                  border:`2px solid ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                  boxShadow:`4px 4px 0 ${C.ink}`,
                }}>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>{oc.name}</div>
                  {oc.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", color:C.inkSoft, marginTop:"4px" }}>— {oc.tagline} —</div>}
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.inkMute, marginTop:"6px" }}>
                    HP {oc.hp ?? "?"} · {oc.abilities?.length || 0} abilities · {oc.features?.length || 0} features
                  </div>
                </button>
              ))}
              <button type="button" onClick={()=>{
                // Build a fresh transient OC for this slot
                const which = ocRosterPickFor;
                setOcBuildTarget(which === "A" ? "slotA" : "slotB");
                setOcRosterPickFor(null);
                setScreen("oc-create");
              }} style={{
                textAlign:"center", padding:"14px 16px", background:"transparent", color:C.ink,
                border:`2px dashed ${C.ink}`, cursor:"pointer",
                fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
              }}>✦ Or Build A Fresh One</button>
            </div>

            <PageFooter />
          </div>
        )}

        {/* ── OC CREATOR ─────────────────────────────────────────────── */}
        {screen === "oc-create" && (() => {
          // Creator binds to one of four targets:
          //   'oc'   — saved OC profile
          //   'you'  — saved You profile
          //   'slotA'/'slotB' — transient fresh build going straight into a battle slot
          const target = ocBuildTarget || "oc";
          const isYou    = target === "you";
          const isSlotA  = target === "slotA";
          const isSlotB  = target === "slotB";
          const isTransient = isSlotA || isSlotB;
          // For OC mode: editing an existing roster entry (ocEditIdx >= 0) writes straight to that slot.
          // Building new (ocEditIdx === -1) writes to ocDraft, which gets appended to the roster on Lock It In.
          const isNewOc = !isYou && !isTransient && ocEditIdx === -1;
          const targetChar  = isYou ? youChar
                            : isSlotA ? slotA
                            : isSlotB ? slotB
                            : isNewOc ? ocDraft
                            : (ocEditIdx >= 0 && ocEditIdx < ocRoster.length ? ocRoster[ocEditIdx] : null);
          const setTarget   = isYou ? setYouChar
                            : isSlotA ? setSlotA
                            : isSlotB ? setSlotB
                            : isNewOc ? setOcDraft
                            : (next) => updateOcAt(ocEditIdx, next);
          // Empty state defaults — randomize fills these.
          const draft = targetChar || {
            name: "", tagline: "",
            hp: 200, height: 175, weight: 70, gender: "",
            habitat: "Forest", habitatIcon: "🌲",
            abilities: [], features: [],
            personality: { ...OC_PERSONALITY_DEFAULT },
            catchphrase: "",
            age: 25,
          };
          // Migration: old characters had size/stats/signatureName — drop them, derive HP from old stats if needed.
          if (!draft.abilities) draft.abilities = [];
          if (!draft.features)  draft.features  = [];
          if (typeof draft.hp !== "number") draft.hp = draft.stats?.DUR ? Math.round(draft.stats.DUR * 30) : 200;
          if (typeof draft.height !== "number") draft.height = ({ Tiny:140, Small:160, Medium:175, Large:200, Huge:240 })[draft.size] || 175;
          if (typeof draft.weight !== "number") draft.weight = ({ Tiny:40, Small:55, Medium:75, Large:120, Huge:250 })[draft.size] || 75;
          if (!draft.gender) draft.gender = "";
          const update = (patch) => setTarget({ ...draft, ...patch });
          const updatePersonality = (k, v) => setTarget({ ...draft, personality: { ...draft.personality, [k]: Math.max(1, Math.min(10, Number(v)||5)) } });
          const toggleAbility = (a) => {
            const has = draft.abilities.includes(a);
            setTarget({ ...draft, abilities: has ? draft.abilities.filter(x => x !== a) : [...draft.abilities, a] });
          };
          const toggleFeature = (f) => {
            const has = draft.features.includes(f);
            setTarget({ ...draft, features: has ? draft.features.filter(x => x !== f) : [...draft.features, f] });
          };
          const randomize = () => {
            const hab = OC_HABITATS[Math.floor(Math.random()*OC_HABITATS.length)];
            const rolledPersonality = {};
            OC_PERSONALITY_AXES.forEach(a => {
              rolledPersonality[a.key] = 2 + Math.floor(Math.random()*8);
            });
            // Roll 2-5 random abilities and 1-4 features.
            const abilityCount = 2 + Math.floor(Math.random()*4);
            const featureCount = 1 + Math.floor(Math.random()*4);
            const abilities = [...OC_ABILITIES].sort(() => Math.random()-0.5).slice(0, abilityCount);
            const features  = [...OC_FEATURES ].sort(() => Math.random()-0.5).slice(0, featureCount);
            setTarget({
              name: OC_RANDOM_NAMES[Math.floor(Math.random()*OC_RANDOM_NAMES.length)],
              tagline: "",
              hp: 100 + Math.floor(Math.random()*350),    // 100-450
              height: 140 + Math.floor(Math.random()*100), // 140-240 cm
              weight: 40 + Math.floor(Math.random()*160),  // 40-200 kg
              gender: OC_GENDERS[Math.floor(Math.random()*OC_GENDERS.length)],
              habitat: hab.name, habitatIcon: hab.icon,
              abilities, features,
              personality: rolledPersonality,
              catchphrase: OC_RANDOM_CATCHPHRASES[Math.floor(Math.random()*OC_RANDOM_CATCHPHRASES.length)],
              age: 8 + Math.floor(Math.random()*60),
            });
          };
          // Tagline is optional; catchphrase required (so the fight has a quotable line).
          const isReady = draft.name.trim() && draft.catchphrase.trim();
          const requestSuggestions = async (field) => {
            setOcSuggesting(s => ({ ...s, [field]: true }));
            const list = await getOcSuggestions(field, draft);
            setOcSuggestions(s => ({ ...s, [field]: list }));
            setOcSuggesting(s => ({ ...s, [field]: false }));
          };
          const applySuggestion = (field, value) => {
            update({ [field]: value });
            setOcSuggestions(s => ({ ...s, [field]: [] }));
          };
          const SuggestRow = ({ field, label }) => {
            const list = ocSuggestions[field] || [];
            const loading = ocSuggesting[field];
            return (
              <div>
                <button
                  type="button"
                  onClick={()=>requestSuggestions(field)}
                  disabled={loading}
                  style={{
                    marginTop:"6px", background:"transparent", color:C.red,
                    border:`1.5px solid ${C.red}`, padding:"4px 10px", cursor:loading?"wait":"pointer",
                    fontFamily:FONT.mono, fontSize:"10px", fontWeight:700, letterSpacing:"2px", textTransform:"uppercase",
                    opacity: loading ? 0.6 : 1,
                  }}
                >{loading ? "✦ Thinking…" : (list.length > 0 ? `✦ ${label} again` : `✨ ${label}`)}</button>
                {list.length > 0 && (
                  <div style={{ marginTop:"8px", display:"flex", flexDirection:"column", gap:"6px" }}>
                    {list.map((s, i) => (
                      <button
                        key={i} type="button" onClick={()=>applySuggestion(field, s)}
                        style={{
                          textAlign:"left", padding:"10px 12px", background:C.paperSoft, color:C.ink,
                          border:`1.5px dashed ${C.ink}`, cursor:"pointer",
                          fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.35,
                        }}
                      >"{s}"</button>
                    ))}
                  </div>
                )}
              </div>
            );
          };

          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen(isTransient || isYou ? "oc-slots" : "oc-slots")}>← Cancel</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC PLAYGROUND." subtitle={
                isYou ? "BUILD BATTLE YOU"
                  : isTransient ? `BUILD FIGHTER ${isSlotA ? "A" : "B"}`
                  : "BUILD YOUR OC"
              } compact />
              {isYou && (
                <div style={{ border:`2px dashed ${C.red}`, padding:"10px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ THIS IS YOU ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    Same scale as any other fighter. Be honest about your stats — the math doesn't care if you're cocky.
                  </p>
                </div>
              )}
              {isTransient && (
                <div style={{ border:`2px dashed ${C.ink}`, padding:"10px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ FIGHTER {isSlotA ? "A" : "B"} ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    Building a fresh OC just for this fight. It won't be saved to your roster.
                  </p>
                </div>
              )}

              {/* Top: shuffle button */}
              <div style={{ display:"flex", gap:"8px", marginBottom:"16px" }}>
                <button onClick={randomize} style={{
                  flex:1, background:C.red, color:C.paper, border:`2px solid ${C.ink}`,
                  padding:"12px", fontFamily:FONT.mono, fontSize:"11px", fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", cursor:"pointer",
                }}>🎲 Roll Me A Fighter</button>
              </div>

              {/* Identity */}
              <SectionHeader title="IDENTITY" suffix="who's this?" />
              <Label>Fighter Name</Label>
              <TextInput value={draft.name} onChange={(v)=>update({name:v})} placeholder="e.g. Sparkle" />

              <div style={{ height:"14px" }}/>
              <Label>Tagline <span style={{ fontWeight:400, opacity:0.6 }}>(optional)</span></Label>
              <TextInput value={draft.tagline} onChange={(v)=>update({tagline:v})} placeholder="e.g. Sad clown with a sword" />
              <SuggestRow field="tagline" label="Suggest some" />

              <div style={{ height:"14px" }}/>
              <Label>Age</Label>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"10px", color:C.inkMute, letterSpacing:"1.5px" }}>0</span>
                <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"24px", color:C.red, lineHeight:1 }}>{draft.age || 0}</span>
                <span style={{ fontFamily:FONT.mono, fontSize:"10px", color:C.inkMute, letterSpacing:"1.5px" }}>150</span>
              </div>
              <input
                type="range" min="0" max="150" value={draft.age || 25}
                onChange={(e)=>update({age: Number(e.target.value)})}
                style={{ width:"100%", accentColor:C.red, cursor:"pointer" }}
              />

              <div style={{ height:"14px" }}/>
              <Label>Gender</Label>
              <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
                {OC_GENDERS.map(g => (
                  <Pill key={g} selected={draft.gender === g} onClick={()=>update({gender:g})} size="sm">{g}</Pill>
                ))}
              </div>

              <div style={{ height:"14px" }}/>
              <Label>Where they're from</Label>
              <input
                type="text"
                value={draft.hometown || ""}
                onChange={(e)=>update({hometown: e.target.value})}
                placeholder="e.g. Brooklyn · a small town in Ireland · Mushroom Kingdom"
                style={{
                  width:"100%", padding:"10px 12px", fontSize:"14px",
                  fontFamily:FONT.display, fontWeight:600,
                  border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
                  outline:"none", borderRadius:0, boxSizing:"border-box",
                }}
              />

              <div style={{ height:"14px" }}/>
              <Label>Habitat</Label>
              <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
                {OC_HABITATS.map(h => (
                  <Pill key={h.name} selected={draft.habitat === h.name} onClick={()=>update({habitat:h.name, habitatIcon:h.icon})} size="sm">{h.icon} {h.name}</Pill>
                ))}
              </div>

              {/* Body */}
              <div style={{ height:"22px" }}/>
              <SectionHeader title="BODY" suffix="raw measurements" />
              <div style={{ border:`2px solid ${C.ink}`, padding:"14px", background:C.paperSoft, marginBottom:"18px" }}>
                <div style={{ marginBottom:"14px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                    <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", fontWeight:700, color:C.red }}>HP · MAX HEALTH</span>
                    <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.red, lineHeight:1 }}>{draft.hp}</span>
                  </div>
                  <input type="range" min="20" max="500" value={draft.hp}
                    onChange={(e)=>update({hp: Number(e.target.value)})}
                    style={{ width:"100%", accentColor:C.red, cursor:"pointer" }}/>
                  <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"9px", color:C.inkMute, letterSpacing:"1.5px" }}>
                    <span>20</span><span>500</span>
                  </div>
                </div>
                {(() => {
                  const heightIn = cmToIn(draft.height);
                  const weightLb = kgToLb(draft.weight);
                  return (
                    <>
                      <div style={{ marginBottom:"14px" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                          <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", fontWeight:700, color:C.ink }}>HEIGHT</span>
                          <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", color:C.red, lineHeight:1 }}>{inToFtIn(heightIn)} <span style={{ fontFamily:FONT.mono, fontSize:"11px", color:C.inkMute, fontWeight:700 }}>· {heightIn} in</span></span>
                        </div>
                        <input type="range" min="20" max="240" value={heightIn}
                          onChange={(e)=>update({height: inToCm(e.target.value)})}
                          style={{ width:"100%", accentColor:C.red, cursor:"pointer" }}/>
                        <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"9px", color:C.inkMute, letterSpacing:"1.5px" }}>
                          <span>1'8"</span><span>20'0"</span>
                        </div>
                      </div>
                      <div>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                          <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", fontWeight:700, color:C.ink }}>WEIGHT</span>
                          <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", color:C.red, lineHeight:1 }}>{weightLb} <span style={{ fontFamily:FONT.mono, fontSize:"11px", color:C.inkMute, fontWeight:700 }}>lb</span></span>
                        </div>
                        <input type="range" min="20" max="5000" value={weightLb}
                          onChange={(e)=>update({weight: lbToKg(e.target.value)})}
                          style={{ width:"100%", accentColor:C.red, cursor:"pointer" }}/>
                        <div style={{ display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"9px", color:C.inkMute, letterSpacing:"1.5px" }}>
                          <span>20 lb</span><span>5000 lb</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Abilities */}
              <SectionHeader title="ABILITIES" suffix={`${draft.abilities.length} picked`} />
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.inkMute, marginBottom:"8px" }}>
                Each ability adds real bite to the math. Pick what fits.
              </div>
              <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"18px" }}>
                {OC_ABILITIES.map(a => (
                  <Pill key={a} selected={draft.abilities.includes(a)} onClick={()=>toggleAbility(a)} size="sm">{a}</Pill>
                ))}
              </div>

              {/* Physical features */}
              <SectionHeader title="PHYSICAL FEATURES" suffix={`${draft.features.length} picked`} />
              <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"18px" }}>
                {OC_FEATURES.map(f => (
                  <Pill key={f} selected={draft.features.includes(f)} onClick={()=>toggleFeature(f)} size="sm">{f}</Pill>
                ))}
              </div>

              {/* Personality sliders + catchphrase */}
              <div style={{ height:"22px" }}/>
              <SectionHeader title="PERSONALITY" suffix="how do they read?" />
              <div style={{ border:`2px solid ${C.ink}`, padding:"14px", background:C.paperSoft, marginBottom:"18px" }}>
                {OC_PERSONALITY_AXES.map(axis => (
                  <div key={axis.key} style={{ marginBottom:"14px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", fontWeight:700 }}>
                      <span style={{ color: draft.personality[axis.key] <= 4 ? C.ink : C.inkMute }}>{axis.low.toUpperCase()}</span>
                      <span style={{ color: draft.personality[axis.key] >= 7 ? C.ink : C.inkMute }}>{axis.high.toUpperCase()}</span>
                    </div>
                    <input
                      type="range" min="1" max="10" value={draft.personality[axis.key]}
                      onChange={(e)=>updatePersonality(axis.key, e.target.value)}
                      style={{ width:"100%", accentColor:C.red, cursor:"pointer" }}
                    />
                  </div>
                ))}
              </div>
              <Label>Catchphrase</Label>
              <TextInput value={draft.catchphrase} onChange={(v)=>update({catchphrase:v})} placeholder="e.g. You're gonna look fabulous on the way down." />
              <SuggestRow field="catchphrase" label="Suggest some" />

              <div style={{ height:"14px" }}/>
              <Label>Backstory <span style={{ fontWeight:400, opacity:0.6 }}>(optional)</span></Label>
              <textarea
                value={draft.backstory || ""}
                onChange={(e)=>update({backstory: e.target.value})}
                placeholder="A few lines about where they came from. Origin, motivation, what made them like this."
                rows={4}
                style={{
                  width:"100%", padding:"10px 12px", fontFamily:FONT.display, fontSize:"14px",
                  border:`1.5px solid ${C.ink}`, background:C.paper, color:C.ink,
                  resize:"vertical", lineHeight:1.4, outline:"none",
                }}
              />

              {/* Submit */}
              <div style={{ height:"22px" }}/>
              <TabloidButton
                onClick={()=>{
                  if (!isReady) return;
                  const builtChar = draft;
                  if (isNewOc && ocDraft) { addOcToRoster(ocDraft); setOcDraft(null); }
                  if (isSlotA) { setSlotAType("oc-transient"); setOcBuildTarget("oc"); }
                  else if (isSlotB) { setSlotBType("oc-transient"); setOcBuildTarget("oc"); }
                  else { setOcBuildTarget("oc"); setOcEditIdx(-1); }
                  // OC-vs-OC: after a transient build, return to the combined picker so the user can confirm both slots.
                  if (ocScenario === "oc-oc" && isTransient) {
                    setScreen("oc-oc-pick");
                    return;
                  }
                  if (ocScenario) {
                    const overrides = isSlotA ? { slotA: builtChar } : isSlotB ? { slotB: builtChar } : {};
                    advanceScenario(ocScenario, overrides);
                  } else {
                    setScreen("oc-slots");
                  }
                }}
                disabled={!isReady}
              >
                ✦ {isTransient ? "Lock In →" : (isNewOc ? "Save To Roster →" : "Save Changes →")}
              </TabloidButton>
              <div style={{ height:"8px" }}/>
              <TabloidButton variant="secondary" onClick={()=>{
                if (isSlotA) setSlotA(null);
                if (isSlotB) setSlotB(null);
                setOcBuildTarget("oc");
                setOcDraft(null);
                setOcEditIdx(-1);
                // OC-vs-OC: cancel back to the matchup picker instead of the home screen.
                if (ocScenario === "oc-oc" && isTransient) setScreen("oc-oc-pick");
                else setScreen("oc-slots");
              }}>← Cancel</TabloidButton>

              <PageFooter />
            </div>
          );
        })()}

        {/* ── OC SLOTS (scenario picker — pick the matchup type, then guide through setup) ─ */}
        {screen === "oc-slots" && (() => {
          // List of scenarios. Each is a tappable card that kicks off a guided setup flow.
          const SCENARIOS = [
            { key:"you-fantasy",     a:"You",     b:"Fantasy" },
            { key:"you-oc",          a:"You",     b:"OC"      },
            { key:"oc-fantasy",      a:"OC",      b:"Fantasy" },
            { key:"oc-oc",           a:"OC",      b:"OC"      },
            { key:"fantasy-fantasy", a:"Fantasy", b:"Fantasy" },
          ];
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC PLAYGROUND." subtitle="PICK YOUR MATCHUP" compact />

              {error && (
                <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ HEADS UP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>{error}</p>
                </div>
              )}

              {/* Scenario cards — center aligned, plenty of breathing room */}
              <div style={{ display:"flex", flexDirection:"column", gap:"20px", marginBottom:"30px" }}>
                {SCENARIOS.map(s => (
                  <button key={s.key} onClick={()=>startScenario(s.key)} style={{
                    display:"block", width:"100%", textAlign:"center", cursor:"pointer",
                    border:`3px solid ${C.ink}`, padding:"28px 18px",
                    background:C.paper, color:C.ink,
                    boxShadow:`5px 5px 0 ${C.ink}`,
                    fontFamily:FONT.display,
                  }}>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1.15, letterSpacing:"-0.5px", textTransform:"uppercase" }}>
                      {s.a} <span style={{ color:C.red, fontWeight:900 }}>vs</span> {s.b}
                    </div>
                  </button>
                ))}
              </div>

              <RuleHr double mb="18px" />

              {/* Roster — quick access to edit/build saved fighters */}
              <SectionHeader title="YOUR ROSTER" suffix={`${ocRoster.length} OC${ocRoster.length === 1 ? "" : "s"} saved`} />
              <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"16px" }}>
                {/* Battle You — single slot, single profile */}
                <div style={{ border:`2px solid ${C.ink}`, background:C.paperSoft }}>
                  <div style={{ display:"flex", padding:"12px 14px", justifyContent:"space-between", alignItems:"center", gap:"10px" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"2px" }}>BATTLE YOU</div>
                      {youChar ? (
                        <>
                          <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase" }}>{youChar.name}</div>
                          {youChar.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", color:C.inkSoft, marginTop:"2px" }}>— {youChar.tagline} —</div>}
                        </>
                      ) : (
                        <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.inkSoft }}>Not built yet.</div>
                      )}
                    </div>
                    <div style={{ display:"flex", gap:"6px", flexShrink:0 }}>
                      {youChar && (
                        <button type="button" onClick={()=>{
                          setOcProfileFor("youChar"); setOcProfileGenStatus({ loading:false, error:null }); setError(null); setScreen("oc-profile");
                        }} style={{
                          padding:"8px 10px", background:C.paper, color:C.ink,
                          border:`1.5px solid ${C.ink}`, cursor:"pointer",
                          fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                        }}>📖 Profile</button>
                      )}
                      <button type="button" onClick={()=>{
                        setOcScenario(null); setOcBuildTarget("you"); setError(null); setScreen("oc-create");
                      }} style={{
                        padding:"8px 12px", background:C.paper, color:C.ink,
                        border:`1.5px solid ${C.ink}`, cursor:"pointer",
                        fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                      }}>{youChar ? "✎ Edit" : "✦ Build"}</button>
                    </div>
                  </div>
                </div>

                {/* OC roster — multiple slots */}
                {ocRoster.map((oc, idx) => {
                  return (
                  <div key={oc.id} style={{ border:`2px solid ${C.ink}`, background:C.paperSoft }}>
                    <div style={{ display:"flex", padding:"12px 14px", justifyContent:"space-between", alignItems:"center", gap:"10px" }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"2px" }}>OC</div>
                        <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase" }}>{oc.name}</div>
                        {oc.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", color:C.inkSoft, marginTop:"2px" }}>— {oc.tagline} —</div>}
                      </div>
                      <div style={{ display:"flex", gap:"6px", flexShrink:0 }}>
                        <button type="button" onClick={()=>{
                          setOcProfileFor(oc.id); setOcProfileGenStatus({ loading:false, error:null }); setError(null); setScreen("oc-profile");
                        }} style={{
                          padding:"8px 10px", background:C.paper, color:C.ink,
                          border:`1.5px solid ${C.ink}`, cursor:"pointer",
                          fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                        }}>📖 Profile</button>
                        <button type="button" onClick={()=>{
                          setOcScenario(null); setOcBuildTarget("oc"); setOcEditIdx(idx); setOcDraft(null); setError(null); setScreen("oc-create");
                        }} style={{
                          padding:"8px 10px", background:C.paper, color:C.ink,
                          border:`1.5px solid ${C.ink}`, cursor:"pointer",
                          fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                        }}>✎ Edit</button>
                        <button type="button" onClick={()=>{
                          if (confirm(`Delete ${oc.name}?`)) deleteOcAt(idx);
                        }} style={{
                          padding:"8px 10px", background:"transparent", color:C.red,
                          border:`1.5px solid ${C.red}`, cursor:"pointer",
                          fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                        }}>✕</button>
                      </div>
                    </div>
                  </div>
                  );
                })}

                {/* Build new OC */}
                <button type="button" onClick={()=>{
                  setOcScenario(null); setOcBuildTarget("oc"); setOcEditIdx(-1); setOcDraft(null); setError(null); setScreen("oc-create");
                }} style={{
                  textAlign:"center", padding:"14px", background:"transparent", color:C.ink,
                  border:`2px dashed ${C.ink}`, cursor:"pointer",
                  fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
                }}>✦ Build {ocRoster.length > 0 ? "Another" : "An"} OC</button>
              </div>

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC PROFILE (character bio + quote board) ─────────────── */}
        {screen === "oc-profile" && (() => {
          const isYou = ocProfileFor === "youChar";
          const ocIdx = isYou ? -1 : ocRoster.findIndex(o => o.id === ocProfileFor);
          const c = isYou ? youChar : (ocIdx >= 0 ? ocRoster[ocIdx] : null);
          if (!c) {
            return (
              <div>
                <TextLink onClick={()=>{ setOcProfileFor(null); setScreen("oc-slots"); }}>← Back</TextLink>
                <div style={{ padding:"32px 0", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft }}>
                  This fighter is gone.
                </div>
              </div>
            );
          }
          const saveProfile = (profileText) => {
            if (isYou) setYouChar({ ...c, profile: profileText });
            else updateOcAt(ocIdx, { ...c, profile: profileText });
          };
          const handleGenerate = async () => {
            setOcProfileGenStatus({ loading:true, error:null });
            const res = await getOcProfile(c);
            if (res.ok) {
              saveProfile(res.profile);
              setOcProfileGenStatus({ loading:false, error:null });
            } else {
              setOcProfileGenStatus({ loading:false, error: res.error || "Profile generation failed." });
            }
          };
          const quotes = flattenArenaQuotes(c);
          const roleAccent = (role) => role === "winner" ? C.red : role === "loser" ? C.ink : C.inkSoft;
          const roleLabel = (role) => role === "winner" ? "WINNER" : role === "loser" ? "LOSER" : "PRE-FIGHT";
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>{ setOcProfileFor(null); setScreen("oc-slots"); }}>← Roster</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>CHARACTER FILE</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title={(c.name || "—").toUpperCase() + "."} subtitle={c.tagline ? `— ${c.tagline} —` : (isYou ? "BATTLE YOU" : "YOUR OC")} compact />

              {/* Vitals */}
              <div style={{ border:`3px solid ${C.ink}`, padding:"14px 16px", background:C.paperSoft, marginBottom:"18px" }}>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px 18px", fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1.5px", color:C.ink }}>
                  <span><span style={{ color:C.inkMute }}>HP</span> {c.hp ?? 200}</span>
                  {c.age != null && c.age !== 0 && <span><span style={{ color:C.inkMute }}>AGE</span> {c.age}</span>}
                  {c.gender && c.gender !== "Other / skip" && <span><span style={{ color:C.inkMute }}>GENDER</span> {c.gender}</span>}
                  {c.height && <span><span style={{ color:C.inkMute }}>HT</span> {inToFtIn(cmToIn(c.height))}</span>}
                  {c.weight && <span><span style={{ color:C.inkMute }}>WT</span> {kgToLb(c.weight)} lb</span>}
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px 18px", marginTop:"6px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.ink }}>
                  {c.hometown && <span><span style={{ color:C.inkMute }}>FROM</span> {c.hometown}</span>}
                  {c.habitat && <span><span style={{ color:C.inkMute }}>HABITAT</span> {c.habitat}</span>}
                </div>
                {Array.isArray(c.abilities) && c.abilities.length > 0 && (
                  <div style={{ marginTop:"8px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.ink }}>
                    <span style={{ color:C.inkMute }}>ABILITIES</span> {c.abilities.join(" · ")}
                  </div>
                )}
                {Array.isArray(c.features) && c.features.length > 0 && (
                  <div style={{ marginTop:"4px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.ink }}>
                    <span style={{ color:C.inkMute }}>FEATURES</span> {c.features.join(" · ")}
                  </div>
                )}
                {c.catchphrase && (
                  <div style={{ marginTop:"10px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.ink, borderTop:`1px dashed ${C.ink}`, paddingTop:"8px" }}>
                    "{c.catchphrase}"
                  </div>
                )}
              </div>

              {/* PSYCH ANALYSIS — Cosmo-voice romantic personality read from the survey */}
              {(() => {
                const hasSurvey = !!(c.mkkSurvey && c.mkkSurvey.answers);
                const psych = c.psychAnalysis;
                if (!hasSurvey && !psych) return null;
                const regenerate = async () => {
                  if (!hasSurvey) return;
                  setOcProfileGenStatus({ loading: true, error: null });
                  const res = await runOcPsychAnalysis(c);
                  if (res.ok) {
                    const today = new Date().toISOString().slice(0, 10);
                    const withAnalysis = { ...c, psychAnalysis: { headline: res.headline, analysis: res.analysis, generatedAt: today } };
                    if (isYou) setYouChar(withAnalysis);
                    else updateOcAt(ocIdx, withAnalysis);
                    setOcProfileGenStatus({ loading: false, error: null });
                  } else {
                    setOcProfileGenStatus({ loading: false, error: res.error || "Analysis failed." });
                  }
                };
                return (
                  <>
                    <SectionHeader title="THE PSYCH READ" suffix={psych?.generatedAt ? `from the survey · ${psych.generatedAt}` : "from the compatibility survey"} />
                    <div style={{ border:`3px solid ${C.ink}`, padding:"16px 18px", background:C.ink, color:C.paper, marginBottom:"18px" }}>
                      {psych?.headline && (
                        <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", color:C.red, fontWeight:700, marginBottom:"8px" }}>★ {psych.headline.toUpperCase()} ★</div>
                      )}
                      {psych?.analysis ? (
                        <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", lineHeight:1.55, color:C.paper }}>{psych.analysis}</p>
                      ) : (
                        <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:"rgba(241,232,210,0.7)" }}>
                          {hasSurvey ? "No analysis yet — generate one to read their romantic profile." : "Take the compatibility survey to unlock the psych read."}
                        </p>
                      )}
                      {hasSurvey && (
                        <div style={{ marginTop:"12px" }}>
                          <button type="button" onClick={regenerate} style={{
                            padding:"7px 12px", background:C.paper, color:C.ink,
                            border:`1.5px solid ${C.paper}`, cursor:"pointer",
                            fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                          }}>{psych ? "✦ Regenerate" : "✨ Generate Analysis"}</button>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* THE BIO */}
              <SectionHeader title="THE BIO" suffix={c.profile ? "the magazine treatment" : "press generate"} />
              <div style={{ border:`2px solid ${C.ink}`, padding:"16px 18px", background:C.paper, marginBottom:"18px" }}>
                {ocProfileGenStatus.loading ? (
                  <div style={{ padding:"22px 0", textAlign:"center" }}>
                    <LoadingDots />
                    <div style={{ marginTop:"10px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", color:C.inkSoft }}>WRITING THE PROFILE…</div>
                  </div>
                ) : c.profile ? (
                  c.profile.split("\n\n").map((para, i) => (
                    <p key={i} style={{ margin:i===0?"0 0 10px":"0 0 10px", fontFamily:FONT.display, fontSize:"15px", lineHeight:1.5, color:C.ink }}>{para}</p>
                  ))
                ) : (
                  <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.inkSoft }}>
                    No profile yet. Generate one to see {c.name || "this fighter"} written up tabloid-style.
                  </p>
                )}
                {ocProfileGenStatus.error && (
                  <div style={{ marginTop:"10px", padding:"8px 10px", border:`1.5px solid ${C.red}`, background:C.paperSoft, fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1px", color:C.red }}>
                    {ocProfileGenStatus.error}
                  </div>
                )}
                {!ocProfileGenStatus.loading && (
                  <div style={{ marginTop:"12px", display:"flex", gap:"8px" }}>
                    <button type="button" onClick={handleGenerate} style={{
                      padding:"8px 12px", background:C.ink, color:C.paper,
                      border:`1.5px solid ${C.ink}`, cursor:"pointer",
                      fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                    }}>{c.profile ? "✦ Regenerate" : "✨ Generate Profile"}</button>
                    {c.profile && (
                      <button type="button" onClick={()=>{ if (confirm("Clear the profile?")) saveProfile(""); }} style={{
                        padding:"8px 12px", background:"transparent", color:C.ink,
                        border:`1.5px dashed ${C.ink}`, cursor:"pointer",
                        fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                      }}>Clear</button>
                    )}
                  </div>
                )}
              </div>

              {/* THE LOVE LIFE — MKK + Matchmaker history + cross-OC cameos */}
              {(() => {
                const mkkHist = Array.isArray(c?.history?.mkk) ? c.history.mkk : [];
                const cameos = Array.isArray(c?.history?.mkkCameos) ? c.history.mkkCameos : [];
                const mmHist = Array.isArray(c?.history?.matchmaker) ? c.history.matchmaker : [];
                const mmCameos = Array.isArray(c?.history?.matchmakerCameos) ? c.history.matchmakerCameos : [];
                if (mkkHist.length === 0 && cameos.length === 0 && mmHist.length === 0 && mmCameos.length === 0) return null;
                const verdictEmoji = (v) => v === "marry" ? "💍" : v === "kiss" || v === "screw" ? "💋" : v === "kill" ? "💀" : "★";
                const verdictLabel = (v) => v === "screw" ? "KISS" : (v || "").toUpperCase();
                const totalCount = mkkHist.length + mmHist.length;
                const totalCameos = cameos.length + mmCameos.length;
                return (
                  <>
                    <SectionHeader title="THE LOVE LIFE" suffix={`${totalCount} session${totalCount === 1 ? "" : "s"}${totalCameos ? ` · ${totalCameos} cameo${totalCameos === 1 ? "" : "s"}` : ""}`} />
                    <div style={{ marginBottom:"18px", display:"flex", flexDirection:"column", gap:"10px" }}>
                      {mkkHist.slice().reverse().map((sess, i) => (
                        <div key={`m${i}`} style={{ border:`2px solid ${C.ink}`, padding:"10px 12px", background:C.paperSoft }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"6px" }}>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700 }}>MKK SESSION</span>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.inkMute }}>{sess.attraction || "anyone"}{sess.universe ? ` · ${sess.universe}` : ""}{sess.date ? ` · ${sess.date}` : ""}</span>
                          </div>
                          {(sess.results || []).map((r, j) => (
                            <div key={j} style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                              <span style={{ marginRight:"6px" }}>{verdictEmoji(r.verdict)}</span>
                              <span style={{ fontWeight:700 }}>{verdictLabel(r.verdict)}</span> {r.celeb}{r.universe ? <span style={{ color:C.inkSoft, fontFamily:FONT.mono, fontSize:"10px", marginLeft:"6px" }}>({r.universe})</span> : null}
                            </div>
                          ))}
                        </div>
                      ))}
                      {mmHist.slice().reverse().map((sess, i) => (
                        <div key={`mm${i}`} style={{ border:`2px solid ${C.ink}`, padding:"10px 12px", background:C.paperSoft }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"6px" }}>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700 }}>MATCHMAKER · {sess.verdict || "TBD"}</span>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.inkMute }}>{sess.score != null ? `${sess.score}%` : ""}{sess.date ? ` · ${sess.date}` : ""}</span>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                            Had a crush on <span style={{ fontWeight:700 }}>{sess.crush}</span>{sess.crushUniverse ? <span style={{ color:C.inkSoft, fontFamily:FONT.mono, fontSize:"10px", marginLeft:"6px" }}>({sess.crushUniverse})</span> : null}{sess.crushOcId ? <span style={{ color:C.red, fontFamily:FONT.mono, fontSize:"10px", marginLeft:"6px", letterSpacing:"1.5px" }}>· ROSTER</span> : null}
                          </div>
                        </div>
                      ))}
                      {cameos.slice().reverse().map((cam, i) => (
                        <div key={`c${i}`} style={{ border:`2px solid ${C.red}`, padding:"10px 12px", background:C.paperSoft }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700 }}>★ MKK CAMEO ★</span>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.inkMute }}>{cam.date || ""}</span>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                            <span style={{ fontWeight:700 }}>{cam.byOcName}</span> picked {c.name || "them"} as their <span style={{ marginLeft:"4px" }}>{verdictEmoji(cam.verdict)}</span>{" "}
                            <span style={{ fontWeight:700 }}>{verdictLabel(cam.verdict)}</span>.
                          </div>
                        </div>
                      ))}
                      {mmCameos.slice().reverse().map((cam, i) => (
                        <div key={`mc${i}`} style={{ border:`2px solid ${C.red}`, padding:"10px 12px", background:C.paperSoft }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700 }}>★ HAS A CRUSH ★</span>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.inkMute }}>{cam.score != null ? `verdict: ${cam.verdict || "?"} · ${cam.score}%` : (cam.verdict || "")}{cam.date ? ` · ${cam.date}` : ""}</span>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                            <span style={{ fontWeight:700 }}>{cam.byOcName}</span> has a crush on {c.name || "them"}.
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}

              {/* THE FEAST TABLE — eating contests + cameos */}
              {(() => {
                const feastHist = Array.isArray(c?.history?.feast) ? c.history.feast : [];
                const feastCameos = Array.isArray(c?.history?.feastCameos) ? c.history.feastCameos : [];
                if (feastHist.length === 0 && feastCameos.length === 0) return null;
                const wins = feastHist.filter(s => s.result === "win").length;
                const losses = feastHist.filter(s => s.result === "loss").length;
                return (
                  <>
                    <SectionHeader title="THE FEAST TABLE" suffix={`${wins}W · ${losses}L${feastCameos.length ? ` · ${feastCameos.length} cameo${feastCameos.length === 1 ? "" : "s"}` : ""}`} />
                    <div style={{ marginBottom:"18px", display:"flex", flexDirection:"column", gap:"10px" }}>
                      {feastHist.slice().reverse().map((sess, i) => (
                        <div key={`f${i}`} style={{ border:`2px solid ${C.ink}`, borderLeft:`6px solid ${sess.result === "win" ? C.red : C.ink}`, padding:"10px 12px", background:C.paperSoft }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700 }}>{sess.result === "win" ? "OUT-ATE" : "OUT-EATEN"}</span>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.inkMute }}>{sess.food || ""}{sess.date ? ` · ${sess.date}` : ""}</span>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                            vs <span style={{ fontWeight:700 }}>{sess.opponent}</span>{sess.opponentId ? <span style={{ color:C.red, fontFamily:FONT.mono, fontSize:"10px", marginLeft:"6px", letterSpacing:"1.5px" }}>· ROSTER</span> : null}
                          </div>
                          {sess.finalCount && <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1px", color:C.inkSoft, marginTop:"4px" }}>{sess.finalCount}</div>}
                        </div>
                      ))}
                      {feastCameos.slice().reverse().map((cam, i) => (
                        <div key={`fc${i}`} style={{ border:`2px solid ${C.red}`, padding:"10px 12px", background:C.paperSoft }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700 }}>★ {cam.result === "win" ? "OUT-ATE THEM" : "OUT-EATEN"} ★</span>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.inkMute }}>{cam.food || ""}{cam.date ? ` · ${cam.date}` : ""}</span>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                            <span style={{ fontWeight:700 }}>{cam.byOcName}</span> challenged {c.name || "them"} to {cam.food || "an eating contest"}.
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}

              {/* THE QUOTE BOARD */}
              <SectionHeader title="THE QUOTE BOARD" suffix={quotes.length ? `${quotes.length} on record` : "fight quotes save here"} />
              <div style={{ marginBottom:"18px" }}>
                {quotes.length === 0 ? (
                  <div style={{ border:`2px dashed ${C.ink}`, padding:"18px", background:C.paperSoft, textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft }}>
                    No quotes yet. Win, lose, or talk smack in a fight — they land here.
                  </div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                    {quotes.map((q, i) => {
                      const accent = roleAccent(q.role);
                      return (
                        <div key={i} style={{ border:`2px solid ${C.ink}`, borderLeft:`6px solid ${accent}`, padding:"10px 12px", background:C.paperSoft }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:"8px", marginBottom:"4px" }}>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color: accent, fontWeight:700 }}>{roleLabel(q.role)}</span>
                            <span style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.inkMute }}>vs {q.opponent || "?"}{q.date ? ` · ${q.date}` : ""}</span>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", color:C.ink, lineHeight:1.4 }}>"{q.text}"</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {quotes.length > 0 && (
                  <div style={{ marginTop:"10px", textAlign:"right" }}>
                    <button type="button" onClick={()=>{
                      if (!confirm("Clear all quotes for this fighter?")) return;
                      const base = ensureOcHistory(c);
                      const cleared = {
                        ...base,
                        history: {
                          ...base.history,
                          arena: base.history.arena.map(f => ({ ...f, quotes: [] })),
                        },
                      };
                      if (isYou) setYouChar(cleared);
                      else updateOcAt(ocIdx, cleared);
                    }} style={{
                      padding:"6px 10px", background:"transparent", color:C.red,
                      border:`1.5px dashed ${C.red}`, cursor:"pointer",
                      fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", fontWeight:700,
                    }}>Clear quotes</button>
                  </div>
                )}
              </div>

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC MANAGER — roster index w/ per-OC controls ─────────── */}
        {screen === "oc-manager" && (() => {
          // Completion check — what's missing? Used for at-a-glance status chips.
          const completion = (c) => {
            const missing = [];
            if (!c.name) missing.push("name");
            if (!c.gender || c.gender === "Other / skip") missing.push("gender");
            if (c.age == null || c.age === 0) missing.push("age");
            if (!c.hometown) missing.push("hometown");
            if (!c.backstory) missing.push("backstory");
            if (!c.mkkSurvey?.answers) missing.push("survey");
            if (!c.profile) missing.push("bio");
            return missing;
          };
          const renderCard = (c, isYou, idx) => {
            const missing = completion(c);
            const total = 7;
            const filled = total - missing.length;
            const pct = Math.round((filled / total) * 100);
            const ocId = isYou ? "youChar" : c.id;
            return (
              <div key={ocId} style={{ border:`2px solid ${C.ink}`, padding:"14px 16px", background:C.paperSoft, marginBottom:"12px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"10px", marginBottom:"10px" }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"2px" }}>{isYou ? "👤 BATTLE YOU" : "🎨 OC"}</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, textTransform:"uppercase" }}>{c.name || "Unnamed"}</div>
                    {c.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", color:C.inkSoft, marginTop:"4px" }}>— {c.tagline} —</div>}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:pct === 100 ? C.red : C.ink, lineHeight:1 }}>{pct}%</div>
                    <div style={{ fontFamily:FONT.mono, fontSize:"8px", letterSpacing:"1.5px", color:C.inkMute, marginTop:"2px" }}>COMPLETE</div>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ height:"6px", border:`1px solid ${C.ink}`, background:C.paper, marginBottom:"10px", position:"relative" }}>
                  <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${pct}%`, background:pct === 100 ? C.red : C.ink }}/>
                </div>

                {/* Missing chips */}
                {missing.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"4px", marginBottom:"10px" }}>
                    {missing.map(m => (
                      <span key={m} style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.red, border:`1px solid ${C.red}`, padding:"2px 6px" }}>+ {m}</span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                  <button type="button" onClick={()=>{
                    setOcProfileFor(ocId); setOcProfileGenStatus({ loading:false, error:null }); setError(null); setScreen("oc-profile");
                  }} style={{
                    padding:"7px 10px", background:C.paper, color:C.ink,
                    border:`1.5px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                  }}>📖 Profile</button>
                  <button type="button" onClick={()=>{
                    setOcScenario(null); setOcBuildTarget(isYou ? "you" : "oc");
                    setOcEditIdx(isYou ? -1 : idx); setOcDraft(null); setError(null);
                    setScreen("oc-create");
                  }} style={{
                    padding:"7px 10px", background:C.paper, color:C.ink,
                    border:`1.5px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                  }}>✎ Edit</button>
                  <button type="button" onClick={()=>openOcSurvey(ocId, "oc-manager")} style={{
                    padding:"7px 10px", background:C.paper, color:c.mkkSurvey ? C.ink : C.red,
                    border:`1.5px solid ${c.mkkSurvey ? C.ink : C.red}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                  }}>📋 {c.mkkSurvey ? "Update Survey" : "Take Survey"}</button>
                  {!isYou && (
                    <button type="button" onClick={()=>{
                      if (confirm(`Delete ${c.name || "this OC"}?`)) deleteOcAt(idx);
                    }} style={{
                      padding:"7px 10px", background:"transparent", color:C.red,
                      border:`1.5px solid ${C.red}`, cursor:"pointer",
                      fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700, marginLeft:"auto",
                    }}>✕ Delete</button>
                  )}
                </div>
              </div>
            );
          };
          const empty = !youChar && ocRoster.length === 0;
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC MANAGER</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC MANAGER." subtitle={`${(youChar ? 1 : 0) + ocRoster.length} ON THE ROSTER`} compact />

              <div style={{ border:`2px solid ${C.ink}`, padding:"10px 14px", marginBottom:"18px", background:C.paperSoft }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.ink }}>
                  Each fighter has a profile that fills out as you play. Complete the basics, take the compatibility survey, generate the bio — then matchmaker / mkk get smarter.
                </div>
              </div>

              {empty ? (
                <div style={{ border:`2px dashed ${C.ink}`, padding:"22px 18px", textAlign:"center", background:C.paperSoft, marginBottom:"18px" }}>
                  <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", color:C.inkSoft, marginBottom:"12px" }}>No fighters yet.</div>
                  <button type="button" onClick={()=>{
                    setOcScenario(null); setOcBuildTarget("oc"); setOcEditIdx(-1); setOcDraft(null);
                    setError(null); setScreen("oc-create");
                  }} style={{
                    padding:"10px 14px", background:C.ink, color:C.paper,
                    border:`2px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
                  }}>✦ Build the First One</button>
                </div>
              ) : (
                <>
                  {youChar && renderCard(youChar, true, -1)}
                  {ocRoster.map((oc, idx) => renderCard(oc, false, idx))}
                </>
              )}

              <button type="button" onClick={()=>{
                setOcScenario(null); setOcBuildTarget("oc"); setOcEditIdx(-1); setOcDraft(null);
                setError(null); setScreen("oc-create");
              }} style={{
                width:"100%", padding:"14px", background:"transparent", color:C.ink,
                border:`2px dashed ${C.ink}`, cursor:"pointer",
                fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
                marginBottom:"18px",
              }}>✦ Build {ocRoster.length > 0 || youChar ? "Another" : "A"} OC</button>

              {!youChar && (
                <button type="button" onClick={()=>{
                  setOcScenario(null); setOcBuildTarget("you"); setOcEditIdx(-1); setOcDraft(null);
                  setError(null); setScreen("oc-create");
                }} style={{
                  width:"100%", padding:"12px", background:"transparent", color:C.ink,
                  border:`1.5px dashed ${C.ink}`, cursor:"pointer",
                  fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
                  marginBottom:"18px",
                }}>👤 Build "Battle You"</button>
              )}

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC MKK — PICK PLAYER + SETUP ─────────────────────────── */}
        {screen === "oc-mkk-pick" && (() => {
          const ATTRACTIONS = ["anyone", "women", "men", "non-binary"];
          const playerChar =
            ocMkkPlayer === "youChar" ? youChar :
            ocMkkPlayer ? ocRoster.find(o => o.id === ocMkkPlayer) :
            null;
          const hasSurvey = !!(playerChar && playerChar.mkkSurvey && playerChar.mkkSurvey.answers);
          const needsSurvey = !!playerChar && !hasSurvey;
          const canStart = !!playerChar && !!ocMkkAttraction && hasSurvey;
          const startSession = async () => {
            if (!canStart) return;
            setError(null);
            setOcMkkLoading(true);
            setScreen("oc-mkk-loading");
            // Roster guest injection: 1-in-3 chance if there are 2+ other OCs.
            const candidates = ocRoster.filter(o => o.id !== playerChar.id && o.name);
            const injectOc = (candidates.length >= 2 && Math.random() < 0.34)
              ? candidates[Math.floor(Math.random() * candidates.length)]
              : null;
            const res = await runOcMkkSession(playerChar, {
              attraction: ocMkkAttraction,
              universe: ocMkkUniverse,
              injectOc,
            });
            setOcMkkLoading(false);
            if (!res.ok) {
              setError(res.error || "Couldn't generate. Try again.");
              setScreen("oc-mkk-pick");
              return;
            }
            // Save to history.mkk on the player.
            const today = new Date().toISOString().slice(0, 10);
            const record = {
              date: today,
              attraction: ocMkkAttraction,
              universe: ocMkkUniverse || null,
              results: [
                { celeb: res.results.marry?.character, universe: res.results.marry?.universe, verdict: "marry" },
                { celeb: res.results.screw?.character, universe: res.results.screw?.universe, verdict: "kiss" },
                { celeb: res.results.kill?.character, universe: res.results.kill?.universe, verdict: "kill" },
              ],
              injectedOcId: res.results.injectedOcId || null,
            };
            const appendMkk = (c) => {
              const base = ensureOcHistory(c);
              return { ...base, attraction: ocMkkAttraction, history: { ...base.history, mkk: [...base.history.mkk, record] } };
            };
            if (ocMkkPlayer === "youChar") setYouChar(appendMkk(playerChar));
            else {
              const idx = ocRoster.findIndex(o => o.id === playerChar.id);
              if (idx >= 0) updateOcAt(idx, appendMkk(playerChar));
            }
            // ── 3a: cross-OC cameo. If a roster OC was injected, figure out which
            // verdict (marry/kiss/kill) matches their name in the result and add a
            // cameo record to that OC's history.mkkCameos.
            if (res.results.injectedOcId) {
              const injectedOc = ocRoster.find(o => o.id === res.results.injectedOcId);
              if (injectedOc) {
                const verdictForName = ["marry", "screw", "kill"].reduce((acc, key) => {
                  if (acc) return acc;
                  const ch = res.results[key]?.character || "";
                  return ch.trim().toLowerCase() === injectedOc.name.trim().toLowerCase() ? key : null;
                }, null);
                const cameo = {
                  date: today,
                  byOcId: playerChar.id || (ocMkkPlayer === "youChar" ? "youChar" : null),
                  byOcName: playerChar.name || "Someone",
                  verdict: verdictForName === "screw" ? "kiss" : (verdictForName || "cameo"),
                  attraction: ocMkkAttraction,
                };
                const injIdx = ocRoster.findIndex(o => o.id === res.results.injectedOcId);
                if (injIdx >= 0) {
                  const base = ensureOcHistory(ocRoster[injIdx]);
                  updateOcAt(injIdx, { ...base, history: { ...base.history, mkkCameos: [...base.history.mkkCameos, cameo] } });
                }
              }
            }
            setOcMkkResult(res.results);
            setScreen("oc-mkk-result");
          };
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>{ setError(null); setScreen("home"); }}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC: MARRY. KISS. KILL.</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC MKK." subtitle="WHO'S PLAYING TODAY" compact />

              {error && (
                <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ HEADS UP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>{error}</p>
                </div>
              )}

              {/* Player slot */}
              <SectionHeader title="PLAYING AS" suffix="pick a fighter from your roster" />
              <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"18px" }}>
                {youChar && (
                  <button type="button" onClick={()=>{
                    setOcMkkPlayer("youChar");
                    if (!ocMkkAttraction && youChar.attraction) setOcMkkAttraction(youChar.attraction);
                  }} style={{
                    textAlign:"left", padding:"12px 14px",
                    background: ocMkkPlayer === "youChar" ? C.ink : C.paperSoft,
                    color: ocMkkPlayer === "youChar" ? C.paper : C.ink,
                    border:`2px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.display,
                  }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.7 }}>👤 BATTLE YOU</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase", marginTop:"2px" }}>{youChar.name}</div>
                    {youChar.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", marginTop:"2px", opacity:0.85 }}>— {youChar.tagline} —</div>}
                  </button>
                )}
                {ocRoster.map(oc => (
                  <button key={oc.id} type="button" onClick={()=>{
                    setOcMkkPlayer(oc.id);
                    if (!ocMkkAttraction && oc.attraction) setOcMkkAttraction(oc.attraction);
                  }} style={{
                    textAlign:"left", padding:"12px 14px",
                    background: ocMkkPlayer === oc.id ? C.ink : C.paperSoft,
                    color: ocMkkPlayer === oc.id ? C.paper : C.ink,
                    border:`2px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.display,
                  }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.7 }}>🎨 OC</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase", marginTop:"2px" }}>{oc.name}</div>
                    {oc.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", marginTop:"2px", opacity:0.85 }}>— {oc.tagline} —</div>}
                  </button>
                ))}
                {!youChar && ocRoster.length === 0 && (
                  <div style={{ border:`2px dashed ${C.ink}`, padding:"14px", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft, background:C.paperSoft }}>
                    No fighters yet. Build one first.
                  </div>
                )}
                <button type="button" onClick={()=>{
                  setOcBuildTarget("oc"); setOcEditIdx(-1); setOcDraft(null); setOcScenario(null);
                  setError(null); setScreen("oc-create");
                }} style={{
                  textAlign:"center", padding:"12px 14px", background:"transparent", color:C.ink,
                  border:`2px dashed ${C.ink}`, cursor:"pointer",
                  fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
                }}>✦ Build {ocRoster.length > 0 || youChar ? "Another" : "A"} Fighter</button>
              </div>

              {/* Attraction */}
              <SectionHeader title="ATTRACTED TO" suffix="who's in the dating pool" />
              <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginBottom:"18px" }}>
                {ATTRACTIONS.map(opt => (
                  <button key={opt} type="button" onClick={()=>setOcMkkAttraction(opt)} style={{
                    padding:"10px 14px",
                    background: ocMkkAttraction === opt ? C.ink : C.paper,
                    color: ocMkkAttraction === opt ? C.paper : C.ink,
                    border:`1.5px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1.5px", fontWeight:700, textTransform:"uppercase",
                  }}>{opt}</button>
                ))}
              </div>

              {/* Universe (optional) */}
              <SectionHeader title="UNIVERSE" suffix="optional — leave blank for anywhere" />
              <input
                type="text"
                value={ocMkkUniverse}
                onChange={e => setOcMkkUniverse(e.target.value)}
                placeholder="e.g. Marvel, Disney, anime, Harry Potter…"
                style={{
                  width:"100%", padding:"12px 14px", fontSize:"15px",
                  fontFamily:FONT.display, fontWeight:700,
                  border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
                  outline:"none", borderRadius:0, marginBottom:"22px", boxSizing:"border-box",
                }}
              />

              {needsSurvey ? (
                <TabloidButton onClick={()=>openOcSurvey(ocMkkPlayer, "oc-mkk-pick")}>
                  ✦ Take the Compatibility Survey →
                </TabloidButton>
              ) : (
                <TabloidButton onClick={startSession} disabled={!canStart}>
                  ✦ {canStart ? "Reveal My Three →" : "Pick a fighter + attraction first"}
                </TabloidButton>
              )}
              {playerChar && hasSurvey && (
                <div style={{ textAlign:"center", marginTop:"6px" }}>
                  <TextLink onClick={()=>openOcSurvey(ocMkkPlayer, "oc-mkk-pick")}>✎ Update {playerChar.name || "their"} survey answers</TextLink>
                </div>
              )}

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC MKK — LOADING ──────────────────────────────────────── */}
        {screen === "oc-mkk-loading" && (
          <div>
            <div style={{ height:"40px" }}/>
            <Masthead title="OC MKK." subtitle="CONSULTING THE STARS" compact />
            <div style={{ padding:"40px 0", textAlign:"center" }}>
              <LoadingDots />
              <div style={{ marginTop:"14px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", color:C.inkSoft }}>FINDING THEIR THREE…</div>
            </div>
            <PageFooter />
          </div>
        )}


        {/* ── OC SURVEY — 26-question compatibility form for a fighter ── */}
        {screen === "oc-survey" && (() => {
          const subjectChar =
            ocSurveyFor === "youChar" ? youChar :
            ocSurveyFor ? ocRoster.find(o => o.id === ocSurveyFor) :
            null;
          if (!subjectChar) {
            return (
              <div>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ padding:"30px 0", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft }}>This fighter is gone.</div>
              </div>
            );
          }
          const totalCount = ALL_QUESTIONS.length;
          const answeredCount = ALL_QUESTIONS.filter(q => ocSurveyAnswers[q.id] != null).length;
          const canSave = answeredCount === totalCount;
          const setSurveyAnswer = (qid, val) => setOcSurveyAnswers(prev => ({ ...prev, [qid]: val }));
          const saveSurvey = async () => {
            if (!canSave) return;
            const today = new Date().toISOString().slice(0, 10);
            const updated = {
              ...subjectChar,
              mkkSurvey: { answers: ocSurveyAnswers, completedAt: today },
            };
            if (ocSurveyFor === "youChar") setYouChar(updated);
            else {
              const idx = ocRoster.findIndex(o => o.id === ocSurveyFor);
              if (idx >= 0) updateOcAt(idx, updated);
            }
            // Fire-and-forget: generate the romantic analysis in the background.
            // The user can keep going; the analysis lands on their profile when ready.
            (async () => {
              const res = await runOcPsychAnalysis(updated);
              if (!res.ok) return;
              const withAnalysis = {
                ...updated,
                psychAnalysis: { headline: res.headline, analysis: res.analysis, generatedAt: today },
              };
              if (ocSurveyFor === "youChar") setYouChar(withAnalysis);
              else {
                const idx = ocRoster.findIndex(o => o.id === ocSurveyFor);
                if (idx >= 0) updateOcAt(idx, withAnalysis);
              }
            })();
            setScreen(ocSurveyReturnScreen || "home");
          };
          const subjectName = (subjectChar.name || "Fighter").toUpperCase();
          const hasPrior = !!subjectChar.mkkSurvey;
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen(ocSurveyReturnScreen || "home")}>← Back</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>COMPATIBILITY SURVEY</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title={`${subjectName}'S SURVEY.`} subtitle={hasPrior ? "UPDATE THE ANSWERS" : "26 QUESTIONS · ONE TIME"} compact />

              <div style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", marginBottom:"18px", background:C.paperSoft }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", color:C.red, fontWeight:700, marginBottom:"6px" }}>WHY THIS</div>
                <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.45, color:C.ink }}>
                  Answer once for {subjectChar.name || "this fighter"} — these answers feed every Marry/Kiss/Kill and Matchmaker session they play, and live on their profile.
                </p>
                <div style={{ marginTop:"8px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.inkMute }}>
                  ANSWERED {answeredCount} / {totalCount}
                </div>
                <div style={{ height:"6px", border:`1px solid ${C.ink}`, background:C.paper, marginTop:"6px", position:"relative" }}>
                  <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${(answeredCount/totalCount)*100}%`, background:C.red }}/>
                </div>
              </div>

              {SECTIONS.map(sec => (
                <div key={sec.id} style={{ marginBottom:"22px" }}>
                  <SectionHeader title={sec.title.toUpperCase()} suffix={sec.subtitle.toLowerCase()} />
                  <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
                    {sec.questions.map(q => {
                      const v = ocSurveyAnswers[q.id];
                      return (
                        <div key={q.id} style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", background:C.paperSoft }}>
                          <p style={{ margin:"0 0 10px", fontFamily:FONT.display, fontSize:"15px", fontWeight:700, lineHeight:1.35, color:C.ink }}>{q.text}</p>
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:"4px" }}>
                            {[1,2,3,4,5,6,7].map(val => {
                              const sel = v === val;
                              return (
                                <button key={val} type="button" onClick={()=>setSurveyAnswer(q.id, val)} style={{
                                  aspectRatio:"1/1",
                                  background: sel ? C.ink : C.paper,
                                  color: sel ? C.paper : C.ink,
                                  border:`1.5px solid ${C.ink}`,
                                  fontFamily:FONT.display, fontWeight:900, fontSize:"16px",
                                  cursor:"pointer",
                                }}>{val}</button>
                              );
                            })}
                          </div>
                          <div style={{ display:"flex", justifyContent:"space-between", marginTop:"6px", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute }}>
                            <span>Disagree</span><span>Agree</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <TabloidButton onClick={saveSurvey} disabled={!canSave}>
                ✦ {canSave ? "Save & Continue →" : `Answer ${totalCount - answeredCount} more`}
              </TabloidButton>

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC MKK — RESULT ───────────────────────────────────────── */}
        {screen === "oc-mkk-result" && ocMkkResult && (() => {
          const playerChar =
            ocMkkPlayer === "youChar" ? youChar :
            ocRoster.find(o => o.id === ocMkkPlayer);
          if (!playerChar) {
            return (
              <div>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ padding:"30px 0", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft }}>This fighter is gone.</div>
              </div>
            );
          }
          const cards = [
            { key: "marry", data: ocMkkResult.marry, label: "MARRY", emoji: "💍", accent: C.red },
            { key: "screw", data: ocMkkResult.screw, label: "KISS",  emoji: "💋", accent: C.ink },
            { key: "kill",  data: ocMkkResult.kill,  label: "KILL",  emoji: "💀", accent: C.ink },
          ];
          const playerName = (playerChar.name || "Fighter").toUpperCase();
          const injectedName = ocMkkResult.injectedOcId
            ? (ocRoster.find(o => o.id === ocMkkResult.injectedOcId)?.name || null)
            : null;
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>THE VERDICT</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title={`${playerName}'S THREE.`} subtitle="MARRY · KISS · KILL" compact />

              {injectedName && (
                <div style={{ border:`2px solid ${C.red}`, padding:"10px 14px", marginBottom:"16px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ ROSTER CAMEO ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    {injectedName} made the panel. Their profile will remember this.
                  </p>
                </div>
              )}

              <div style={{ display:"flex", flexDirection:"column", gap:"14px", marginBottom:"22px" }}>
                {cards.map(({ key, data, label, emoji, accent }) => {
                  if (!data) return null;
                  return (
                    <div key={key} style={{ border:`3px solid ${C.ink}`, background: key === "marry" ? accent : C.paper, color: key === "marry" ? C.paper : C.ink }}>
                      <div style={{ padding:"10px 14px 6px", borderBottom:`2px solid ${key === "marry" ? "rgba(241,232,210,0.4)" : C.ink}`, display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                        <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", fontWeight:700 }}>{emoji} {label}</span>
                        {typeof data.score === "number" && (
                          <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px" }}>{data.score}</span>
                        )}
                      </div>
                      <div style={{ padding:"12px 14px 14px" }}>
                        <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1.1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>{data.character}</div>
                        {data.universe && <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", marginTop:"4px", opacity:0.8 }}>{data.universe}</div>}
                        {data.why && <p style={{ margin:"10px 0 0", fontFamily:FONT.display, fontSize:"14px", lineHeight:1.45 }}>{data.why}</p>}
                        {data.warning && <p style={{ margin:"8px 0 0", fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", opacity:0.85 }}>{data.warning}</p>}
                        {data.opening_line && (
                          <div style={{ marginTop:"10px", padding:"8px 10px", borderLeft:`3px solid ${key === "marry" ? C.paper : accent}`, background: key === "marry" ? "rgba(241,232,210,0.08)" : C.paperSoft }}>
                            <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.7, marginBottom:"2px" }}>OPENING LINE</div>
                            <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px" }}>"{data.opening_line}"</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <TabloidButton onClick={()=>{
                setOcMkkResult(null);
                setScreen("oc-mkk-pick");
              }}>✦ Play Again →</TabloidButton>
              <div style={{ height:"10px" }}/>
              <button type="button" onClick={()=>{
                setOcProfileFor(ocMkkPlayer); setOcProfileGenStatus({ loading:false, error:null });
                setScreen("oc-profile");
              }} style={{
                width:"100%", padding:"12px", background:"transparent", color:C.ink,
                border:`1.5px dashed ${C.ink}`, cursor:"pointer",
                fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
              }}>📖 View {playerName}'s Profile</button>

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC MATCHMAKER — PICK PLAYER + CRUSH ──────────────────── */}
        {screen === "oc-mm-pick" && (() => {
          const playerChar =
            ocMmPlayer === "youChar" ? youChar :
            ocMmPlayer ? ocRoster.find(o => o.id === ocMmPlayer) :
            null;
          const otherOcs = ocRoster.filter(o => !playerChar || o.id !== playerChar.id);
          const targetOc = ocMmTarget !== "free" ? otherOcs.find(o => o.id === ocMmTarget) : null;
          const hasSurvey = !!(playerChar && playerChar.mkkSurvey && playerChar.mkkSurvey.answers);
          const needsSurvey = !!playerChar && !hasSurvey;
          const canStart = !!playerChar && hasSurvey && (
            ocMmTarget === "free"
              ? !!ocMmCrushName.trim()
              : !!targetOc
          );
          const startSession = async () => {
            if (!canStart) return;
            setError(null);
            setScreen("oc-mm-loading");
            const target = targetOc
              ? { ocId: targetOc.id, name: targetOc.name, tagline: targetOc.tagline, personality: targetOc.personality, backstory: targetOc.backstory, gender: targetOc.gender }
              : { name: ocMmCrushName.trim(), universe: ocMmCrushUniverse.trim() };
            const res = await runOcMatchmaker(playerChar, target);
            if (!res.ok) {
              setError(res.error || "Couldn't generate. Try again.");
              setScreen("oc-mm-pick");
              return;
            }
            const today = new Date().toISOString().slice(0, 10);
            const record = {
              date: today,
              crush: res.result.character || target.name,
              crushUniverse: res.result.universe || target.universe || null,
              crushOcId: target.ocId || null,
              verdict: res.result.verdict || null,
              score: typeof res.result.score === "number" ? res.result.score : null,
            };
            const appendMm = (c) => {
              const base = ensureOcHistory(c);
              return { ...base, history: { ...base.history, matchmaker: [...base.history.matchmaker, record] } };
            };
            if (ocMmPlayer === "youChar") setYouChar(appendMm(playerChar));
            else {
              const idx = ocRoster.findIndex(o => o.id === playerChar.id);
              if (idx >= 0) updateOcAt(idx, appendMm(playerChar));
            }
            // Cross-OC cameo: if the crush is another roster OC, record it on them too.
            if (target.ocId) {
              const cameo = {
                date: today,
                byOcId: playerChar.id || (ocMmPlayer === "youChar" ? "youChar" : null),
                byOcName: playerChar.name || "Someone",
                verdict: res.result.verdict || null,
                score: typeof res.result.score === "number" ? res.result.score : null,
              };
              const cIdx = ocRoster.findIndex(o => o.id === target.ocId);
              if (cIdx >= 0) {
                const base = ensureOcHistory(ocRoster[cIdx]);
                updateOcAt(cIdx, { ...base, history: { ...base.history, matchmakerCameos: [...base.history.matchmakerCameos, cameo] } });
              }
            }
            setOcMmResult(res.result);
            setScreen("oc-mm-result");
          };
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>{ setError(null); setScreen("home"); }}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC: MATCHMAKER</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC MATCHMAKER." subtitle="DO THEY LOVE THEM BACK?" compact />

              {error && (
                <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ HEADS UP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>{error}</p>
                </div>
              )}

              {/* Player */}
              <SectionHeader title="WHO'S CRUSHING" suffix="pick a fighter from your roster" />
              <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"18px" }}>
                {youChar && (
                  <button type="button" onClick={()=>setOcMmPlayer("youChar")} style={{
                    textAlign:"left", padding:"12px 14px",
                    background: ocMmPlayer === "youChar" ? C.ink : C.paperSoft,
                    color: ocMmPlayer === "youChar" ? C.paper : C.ink,
                    border:`2px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.display,
                  }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.7 }}>👤 BATTLE YOU</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase", marginTop:"2px" }}>{youChar.name}</div>
                  </button>
                )}
                {ocRoster.map(oc => (
                  <button key={oc.id} type="button" onClick={()=>{
                    setOcMmPlayer(oc.id);
                    if (ocMmTarget !== "free" && ocMmTarget === oc.id) setOcMmTarget("free");
                  }} style={{
                    textAlign:"left", padding:"12px 14px",
                    background: ocMmPlayer === oc.id ? C.ink : C.paperSoft,
                    color: ocMmPlayer === oc.id ? C.paper : C.ink,
                    border:`2px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.display,
                  }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.7 }}>🎨 OC</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase", marginTop:"2px" }}>{oc.name}</div>
                  </button>
                ))}
                {!youChar && ocRoster.length === 0 && (
                  <div style={{ border:`2px dashed ${C.ink}`, padding:"14px", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft, background:C.paperSoft }}>
                    No fighters yet. Build one first.
                  </div>
                )}
              </div>

              {/* Crush picker */}
              <SectionHeader title="THE CRUSH" suffix={otherOcs.length > 0 ? "fictional character or another OC" : "fictional character"} />
              <div style={{ display:"flex", gap:"6px", marginBottom:"10px" }}>
                <button type="button" onClick={()=>setOcMmTarget("free")} style={{
                  flex:1, padding:"10px 14px",
                  background: ocMmTarget === "free" ? C.ink : C.paper,
                  color: ocMmTarget === "free" ? C.paper : C.ink,
                  border:`1.5px solid ${C.ink}`, cursor:"pointer",
                  fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1.5px", fontWeight:700, textTransform:"uppercase",
                }}>Fictional</button>
                {otherOcs.length > 0 && (
                  <button type="button" onClick={()=>setOcMmTarget(otherOcs[0].id)} style={{
                    flex:1, padding:"10px 14px",
                    background: ocMmTarget !== "free" ? C.ink : C.paper,
                    color: ocMmTarget !== "free" ? C.paper : C.ink,
                    border:`1.5px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1.5px", fontWeight:700, textTransform:"uppercase",
                  }}>Another OC</button>
                )}
              </div>

              {ocMmTarget === "free" ? (
                <div style={{ marginBottom:"22px" }}>
                  <Label>Character name</Label>
                  <input
                    type="text"
                    value={ocMmCrushName}
                    onChange={e => setOcMmCrushName(e.target.value)}
                    placeholder="e.g. Wonder Woman, Spider-Man, Hermione…"
                    style={{
                      width:"100%", padding:"12px 14px", fontSize:"15px",
                      fontFamily:FONT.display, fontWeight:700,
                      border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
                      outline:"none", borderRadius:0, marginBottom:"10px", boxSizing:"border-box",
                    }}
                  />
                  <Label>Universe (optional)</Label>
                  <input
                    type="text"
                    value={ocMmCrushUniverse}
                    onChange={e => setOcMmCrushUniverse(e.target.value)}
                    placeholder="e.g. Marvel, DC, Harry Potter…"
                    style={{
                      width:"100%", padding:"12px 14px", fontSize:"15px",
                      fontFamily:FONT.display, fontWeight:700,
                      border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
                      outline:"none", borderRadius:0, boxSizing:"border-box",
                    }}
                  />
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:"6px", marginBottom:"22px" }}>
                  {otherOcs.map(oc => (
                    <button key={oc.id} type="button" onClick={()=>setOcMmTarget(oc.id)} style={{
                      textAlign:"left", padding:"10px 12px",
                      background: ocMmTarget === oc.id ? C.red : C.paperSoft,
                      color: ocMmTarget === oc.id ? C.paper : C.ink,
                      border:`2px solid ${ocMmTarget === oc.id ? C.red : C.ink}`, cursor:"pointer",
                      fontFamily:FONT.display,
                    }}>
                      <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px", textTransform:"uppercase" }}>{oc.name}</div>
                      {oc.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", opacity:0.85, marginTop:"2px" }}>— {oc.tagline} —</div>}
                    </button>
                  ))}
                </div>
              )}

              {needsSurvey ? (
                <TabloidButton onClick={()=>openOcSurvey(ocMmPlayer, "oc-mm-pick")}>
                  ✦ Take the Compatibility Survey →
                </TabloidButton>
              ) : (
                <TabloidButton onClick={startSession} disabled={!canStart}>
                  ✦ {canStart ? "Find Out →" : "Pick a fighter + a crush first"}
                </TabloidButton>
              )}
              {playerChar && hasSurvey && (
                <div style={{ textAlign:"center", marginTop:"6px" }}>
                  <TextLink onClick={()=>openOcSurvey(ocMmPlayer, "oc-mm-pick")}>✎ Update {playerChar.name || "their"} survey answers</TextLink>
                </div>
              )}

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC MATCHMAKER — LOADING ──────────────────────────────── */}
        {screen === "oc-mm-loading" && (
          <div>
            <div style={{ height:"40px" }}/>
            <Masthead title="OC MATCHMAKER." subtitle="READING THEIR MIND" compact />
            <div style={{ padding:"40px 0", textAlign:"center" }}>
              <LoadingDots />
              <div style={{ marginTop:"14px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", color:C.inkSoft }}>SEEING IF THEY'RE INTO YOU…</div>
            </div>
            <PageFooter />
          </div>
        )}


        {/* ── OC MATCHMAKER — RESULT ───────────────────────────────── */}
        {screen === "oc-mm-result" && ocMmResult && (() => {
          const playerChar =
            ocMmPlayer === "youChar" ? youChar :
            ocRoster.find(o => o.id === ocMmPlayer);
          if (!playerChar) {
            return (
              <div>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ padding:"30px 0", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft }}>This fighter is gone.</div>
              </div>
            );
          }
          const r = ocMmResult;
          const score = typeof r.score === "number" ? Math.max(0, Math.min(100, r.score)) : 50;
          const isCross = !!r.crushOcId;
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>THE VERDICT</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title={`${(r.character || "—").toUpperCase()}.`} subtitle={`ON ${(playerChar.name || "FIGHTER").toUpperCase()}`} compact />

              {isCross && (
                <div style={{ border:`2px solid ${C.red}`, padding:"10px 14px", marginBottom:"16px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ ROSTER MATCHUP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    Both fighters will remember this. Check {r.character}'s profile to see it from their side.
                  </p>
                </div>
              )}

              <div style={{ border:`3px solid ${C.ink}`, padding:"16px 18px", background:C.paper, marginBottom:"18px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"8px" }}>
                  <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", color:C.red, fontWeight:700 }}>{r.verdict || "TBD"}</span>
                  <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.ink }}>{score}%</span>
                </div>
                <div style={{ height:"10px", border:`1px solid ${C.ink}`, background:C.paperSoft, marginBottom:"14px", position:"relative" }}>
                  <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${score}%`, background:C.red }}/>
                </div>
                {r.first_impression && (
                  <p style={{ margin:"0 0 10px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", lineHeight:1.4, color:C.ink }}>
                    "{r.first_impression}"
                  </p>
                )}
                {r.what_they_think && (
                  <div style={{ marginBottom:"10px" }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.inkMute, marginBottom:"4px" }}>WHAT THEY THINK</div>
                    <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.45, color:C.ink }}>{r.what_they_think}</p>
                  </div>
                )}
                {r.relationship && (
                  <div style={{ marginBottom:"10px" }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.inkMute, marginBottom:"4px" }}>THE RELATIONSHIP</div>
                    <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.45, color:C.ink }}>{r.relationship}</p>
                  </div>
                )}
                {r.they_love && (
                  <p style={{ margin:"0 0 6px", fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    <span style={{ fontWeight:900 }}>They'd love:</span> {r.they_love}
                  </p>
                )}
                {r.they_dislike && (
                  <p style={{ margin:"0 0 10px", fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    <span style={{ fontWeight:900 }}>What'd grate:</span> {r.they_dislike}
                  </p>
                )}
                {r.opening_line && (
                  <div style={{ marginTop:"10px", padding:"10px 12px", borderLeft:`3px solid ${C.red}`, background:C.paperSoft }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.inkMute, marginBottom:"2px" }}>OPENING LINE</div>
                    <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.ink }}>"{r.opening_line}"</div>
                  </div>
                )}
              </div>

              <TabloidButton onClick={()=>{
                setOcMmResult(null);
                setScreen("oc-mm-pick");
              }}>✦ Try Another Crush →</TabloidButton>
              <div style={{ height:"10px" }}/>
              <button type="button" onClick={()=>{
                setOcProfileFor(ocMmPlayer); setOcProfileGenStatus({ loading:false, error:null });
                setScreen("oc-profile");
              }} style={{
                width:"100%", padding:"12px", background:"transparent", color:C.ink,
                border:`1.5px dashed ${C.ink}`, cursor:"pointer",
                fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
              }}>📖 View {(playerChar.name || "").toUpperCase()}'s Profile</button>

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC FEAST — PICK PLAYER + OPPONENT + FOOD ─────────────── */}
        {screen === "oc-feast-pick" && (() => {
          const playerChar =
            ocFeastPlayer === "youChar" ? youChar :
            ocFeastPlayer ? ocRoster.find(o => o.id === ocFeastPlayer) :
            null;
          const otherOcs = ocRoster.filter(o => !playerChar || o.id !== playerChar.id);
          const targetOc = ocFeastTarget !== "free" ? otherOcs.find(o => o.id === ocFeastTarget) : null;
          const canStart = !!playerChar && !!ocFeastFood && (
            ocFeastTarget === "free" ? !!ocFeastOppName.trim() : !!targetOc
          );
          const startSession = async () => {
            if (!canStart) return;
            setError(null);
            setScreen("oc-feast-loading");
            const opponent = targetOc
              ? { ocId: targetOc.id, name: targetOc.name, tagline: targetOc.tagline, personality: targetOc.personality, backstory: targetOc.backstory, habitat: targetOc.habitat }
              : { name: ocFeastOppName.trim(), universe: ocFeastOppUniverse.trim() };
            const res = await runOcFeast(playerChar, opponent, ocFeastFood);
            if (!res.ok) {
              setError(res.error || "Couldn't run the contest. Try again.");
              setScreen("oc-feast-pick");
              return;
            }
            const today = new Date().toISOString().slice(0, 10);
            const playerWon = (res.result.winner_role || "").trim().toLowerCase() === "player";
            const record = {
              date: today,
              opponent: res.result.opponent || opponent.name,
              opponentId: opponent.ocId || null,
              food: res.result.food || ocFeastFood,
              result: playerWon ? "win" : "loss",
              finalCount: res.result.final_count || null,
            };
            const appendFeast = (c) => {
              const base = ensureOcHistory(c);
              return { ...base, history: { ...base.history, feast: [...base.history.feast, record] } };
            };
            if (ocFeastPlayer === "youChar") setYouChar(appendFeast(playerChar));
            else {
              const idx = ocRoster.findIndex(o => o.id === playerChar.id);
              if (idx >= 0) updateOcAt(idx, appendFeast(playerChar));
            }
            // Cross-OC cameo.
            if (opponent.ocId) {
              const cameo = {
                date: today,
                byOcId: playerChar.id || (ocFeastPlayer === "youChar" ? "youChar" : null),
                byOcName: playerChar.name || "Someone",
                result: playerWon ? "loss" : "win",
                food: ocFeastFood,
              };
              const cIdx = ocRoster.findIndex(o => o.id === opponent.ocId);
              if (cIdx >= 0) {
                const base = ensureOcHistory(ocRoster[cIdx]);
                updateOcAt(cIdx, { ...base, history: { ...base.history, feastCameos: [...base.history.feastCameos, cameo] } });
              }
            }
            setOcFeastResult(res.result);
            setScreen("oc-feast-result");
          };
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>{ setError(null); setScreen("home"); }}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC: FEAST</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC FEAST." subtitle="WHO OUT-EATS WHO" compact />

              {error && (
                <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ HEADS UP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>{error}</p>
                </div>
              )}

              {/* Player */}
              <SectionHeader title="WHO'S EATING" suffix="pick a fighter from your roster" />
              <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"18px" }}>
                {youChar && (
                  <button type="button" onClick={()=>setOcFeastPlayer("youChar")} style={{
                    textAlign:"left", padding:"12px 14px",
                    background: ocFeastPlayer === "youChar" ? C.ink : C.paperSoft,
                    color: ocFeastPlayer === "youChar" ? C.paper : C.ink,
                    border:`2px solid ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                  }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.7 }}>👤 BATTLE YOU</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase", marginTop:"2px" }}>{youChar.name}</div>
                  </button>
                )}
                {ocRoster.map(oc => (
                  <button key={oc.id} type="button" onClick={()=>{
                    setOcFeastPlayer(oc.id);
                    if (ocFeastTarget !== "free" && ocFeastTarget === oc.id) setOcFeastTarget("free");
                  }} style={{
                    textAlign:"left", padding:"12px 14px",
                    background: ocFeastPlayer === oc.id ? C.ink : C.paperSoft,
                    color: ocFeastPlayer === oc.id ? C.paper : C.ink,
                    border:`2px solid ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                  }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", opacity:0.7 }}>🎨 OC</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1, textTransform:"uppercase", marginTop:"2px" }}>{oc.name}</div>
                  </button>
                ))}
                {!youChar && ocRoster.length === 0 && (
                  <div style={{ border:`2px dashed ${C.ink}`, padding:"14px", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft, background:C.paperSoft }}>
                    No fighters yet. Build one first.
                  </div>
                )}
              </div>

              {/* Opponent */}
              <SectionHeader title="THE CHALLENGER" suffix={otherOcs.length > 0 ? "fictional character or another OC" : "fictional character"} />
              <div style={{ display:"flex", gap:"6px", marginBottom:"10px" }}>
                <button type="button" onClick={()=>setOcFeastTarget("free")} style={{
                  flex:1, padding:"10px 14px",
                  background: ocFeastTarget === "free" ? C.ink : C.paper,
                  color: ocFeastTarget === "free" ? C.paper : C.ink,
                  border:`1.5px solid ${C.ink}`, cursor:"pointer",
                  fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1.5px", fontWeight:700, textTransform:"uppercase",
                }}>Fictional</button>
                {otherOcs.length > 0 && (
                  <button type="button" onClick={()=>setOcFeastTarget(otherOcs[0].id)} style={{
                    flex:1, padding:"10px 14px",
                    background: ocFeastTarget !== "free" ? C.ink : C.paper,
                    color: ocFeastTarget !== "free" ? C.paper : C.ink,
                    border:`1.5px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"1.5px", fontWeight:700, textTransform:"uppercase",
                  }}>Another OC</button>
                )}
              </div>

              {ocFeastTarget === "free" ? (
                <div style={{ marginBottom:"18px" }}>
                  <Label>Character name</Label>
                  <input
                    type="text"
                    value={ocFeastOppName}
                    onChange={e => setOcFeastOppName(e.target.value)}
                    placeholder="e.g. Kirby, Joey Chestnut, Cookie Monster…"
                    style={{
                      width:"100%", padding:"12px 14px", fontSize:"15px",
                      fontFamily:FONT.display, fontWeight:700,
                      border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
                      outline:"none", borderRadius:0, marginBottom:"10px", boxSizing:"border-box",
                    }}
                  />
                  <Label>Universe (optional)</Label>
                  <input
                    type="text"
                    value={ocFeastOppUniverse}
                    onChange={e => setOcFeastOppUniverse(e.target.value)}
                    placeholder="e.g. Nintendo, Sesame Street…"
                    style={{
                      width:"100%", padding:"12px 14px", fontSize:"15px",
                      fontFamily:FONT.display, fontWeight:700,
                      border:`2px solid ${C.ink}`, background:C.paper, color:C.ink,
                      outline:"none", borderRadius:0, boxSizing:"border-box",
                    }}
                  />
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:"6px", marginBottom:"18px" }}>
                  {otherOcs.map(oc => (
                    <button key={oc.id} type="button" onClick={()=>setOcFeastTarget(oc.id)} style={{
                      textAlign:"left", padding:"10px 12px",
                      background: ocFeastTarget === oc.id ? C.red : C.paperSoft,
                      color: ocFeastTarget === oc.id ? C.paper : C.ink,
                      border:`2px solid ${ocFeastTarget === oc.id ? C.red : C.ink}`, cursor:"pointer",
                      fontFamily:FONT.display,
                    }}>
                      <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px", textTransform:"uppercase" }}>{oc.name}</div>
                      {oc.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"12px", opacity:0.85, marginTop:"2px" }}>— {oc.tagline} —</div>}
                    </button>
                  ))}
                </div>
              )}

              {/* Food */}
              <SectionHeader title="THE FOOD" suffix="pick the plate they're racing through" />
              <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginBottom:"22px" }}>
                {FEAST_FOODS.map(opt => (
                  <button key={opt} type="button" onClick={()=>setOcFeastFood(opt)} style={{
                    padding:"8px 12px",
                    background: ocFeastFood === opt ? C.ink : C.paper,
                    color: ocFeastFood === opt ? C.paper : C.ink,
                    border:`1.5px solid ${C.ink}`, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700, textTransform:"uppercase",
                  }}>{opt}</button>
                ))}
              </div>

              <TabloidButton onClick={startSession} disabled={!canStart}>
                ✦ {canStart ? "Bring The Food →" : "Pick player + challenger + food"}
              </TabloidButton>

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC FEAST — LOADING ───────────────────────────────────── */}
        {screen === "oc-feast-loading" && (
          <div>
            <div style={{ height:"40px" }}/>
            <Masthead title="OC FEAST." subtitle="LIGHTING THE TIMER" compact />
            <div style={{ padding:"40px 0", textAlign:"center" }}>
              <LoadingDots />
              <div style={{ marginTop:"14px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", color:C.inkSoft }}>STACKING THE PLATES…</div>
            </div>
            <PageFooter />
          </div>
        )}


        {/* ── OC FEAST — RESULT ────────────────────────────────────── */}
        {screen === "oc-feast-result" && ocFeastResult && (() => {
          const playerChar =
            ocFeastPlayer === "youChar" ? youChar :
            ocRoster.find(o => o.id === ocFeastPlayer);
          if (!playerChar) {
            return (
              <div>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ padding:"30px 0", textAlign:"center", fontFamily:FONT.display, fontStyle:"italic", color:C.inkSoft }}>This fighter is gone.</div>
              </div>
            );
          }
          const r = ocFeastResult;
          const playerWon = (r.winner_role || "").trim().toLowerCase() === "player";
          const playerName = (playerChar.name || "Player").toUpperCase();
          const oppName = (r.opponent || "Opponent").toUpperCase();
          const headline = playerWon ? `${playerName} OUT-ATE ${oppName}` : `${oppName} OUT-ATE ${playerName}`;
          const isCross = !!r.opponentOcId;
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("home")}>← Home</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>THE CONTEST</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title={headline + "."} subtitle="EATING CONTEST · THE TAPE" compact />

              {isCross && (
                <div style={{ border:`2px solid ${C.red}`, padding:"10px 14px", marginBottom:"16px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ ROSTER MATCHUP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                    Both fighters will remember this. Check {r.opponent}'s profile for the other side.
                  </p>
                </div>
              )}

              <div style={{ border:`3px solid ${C.ink}`, padding:"14px 16px", background:C.paperSoft, marginBottom:"16px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:"8px 14px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.ink }}>
                  <span><span style={{ color:C.inkMute }}>FOOD</span> {r.food || "—"}</span>
                  {r.location_pick && <span><span style={{ color:C.inkMute }}>VENUE</span> {r.location_pick}</span>}
                  {typeof r.time_seconds === "number" && <span><span style={{ color:C.inkMute }}>TIME</span> {r.time_seconds < 60 ? `${r.time_seconds}s` : `${Math.floor(r.time_seconds/60)}m ${r.time_seconds%60}s`}</span>}
                </div>
                {r.environment && (
                  <p style={{ margin:"10px 0 0", fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.ink }}>{r.environment}</p>
                )}
              </div>

              {Array.isArray(r.play_by_play) && r.play_by_play.length > 0 && (
                <div style={{ border:`2px solid ${C.ink}`, padding:"14px 16px", background:C.paper, marginBottom:"16px" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700, marginBottom:"8px" }}>★ PLAY BY PLAY ★</div>
                  <ol style={{ margin:0, paddingLeft:"18px", fontFamily:FONT.display, fontSize:"14px", lineHeight:1.5, color:C.ink }}>
                    {r.play_by_play.map((beat, i) => <li key={i} style={{ marginBottom:"4px" }}>{beat}</li>)}
                  </ol>
                </div>
              )}

              {r.final_count && (
                <div style={{ border:`3px solid ${C.red}`, padding:"12px 14px", background:C.paperSoft, marginBottom:"16px", textAlign:"center" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700, marginBottom:"4px" }}>FINAL COUNT</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1.2, color:C.ink }}>{r.final_count}</div>
                </div>
              )}

              {(r.winner_quote || r.loser_quote) && (
                <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"16px" }}>
                  {r.winner_quote && (
                    <div style={{ border:`2px solid ${C.ink}`, borderLeft:`6px solid ${C.red}`, padding:"10px 12px", background:C.paperSoft }}>
                      <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.red, fontWeight:700, marginBottom:"4px" }}>WINNER</div>
                      <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.ink }}>"{r.winner_quote}"</div>
                    </div>
                  )}
                  {r.loser_quote && (
                    <div style={{ border:`2px solid ${C.ink}`, borderLeft:`6px solid ${C.ink}`, padding:"10px 12px", background:C.paperSoft }}>
                      <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", color:C.ink, fontWeight:700, marginBottom:"4px" }}>LOSER</div>
                      <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:C.ink }}>"{r.loser_quote}"</div>
                    </div>
                  )}
                </div>
              )}

              {r.condition_of_loser && (
                <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.inkSoft, textAlign:"center", marginBottom:"22px" }}>
                  — {r.condition_of_loser} —
                </div>
              )}

              <TabloidButton onClick={()=>{
                setOcFeastResult(null);
                setScreen("oc-feast-pick");
              }}>✦ Eat Again →</TabloidButton>
              <div style={{ height:"10px" }}/>
              <button type="button" onClick={()=>{
                setOcProfileFor(ocFeastPlayer); setOcProfileGenStatus({ loading:false, error:null });
                setScreen("oc-profile");
              }} style={{
                width:"100%", padding:"12px", background:"transparent", color:C.ink,
                border:`1.5px dashed ${C.ink}`, cursor:"pointer",
                fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", fontWeight:700, textTransform:"uppercase",
              }}>📖 View {playerName}'s Profile</button>

              <PageFooter />
            </div>
          );
        })()}


        {/* ── OC FANTASY OPPONENT INPUT ─────────────────────────────── */}
        {screen === "oc-fantasy-input" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
              <TextLink onClick={()=>{ setError(null); setScreen("oc-slots"); }}>← Back</TextLink>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
              <span style={{ width:"60px" }}/>
            </div>
            <Masthead title="OC PLAYGROUND." subtitle="PICK A FANTASY OPPONENT" compact />

            {error && (
              <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ HEADS UP ★</div>
                <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>{error}</p>
              </div>
            )}

            <div style={{ border:`3px solid ${C.ink}`, padding:"22px 20px", background:C.paperSoft, boxShadow:`6px 6px 0 ${C.ink}`, marginBottom:"22px" }}>
              <h3 style={{ margin:"0 0 16px", fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase", textAlign:"center" }}>
                Who Are They?
              </h3>
              <Label>Character</Label>
              <TextInput value={ocFantasyChar} onChange={setOcFantasyChar} placeholder="e.g. Hermione Granger" />
              <div style={{ height:"14px" }}/>
              <Label>From</Label>
              <TextInput value={ocFantasyUni} onChange={setOcFantasyUni} placeholder="e.g. Harry Potter" />
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginTop:"10px", textAlign:"center" }}>
                Any fictional character. We won't judge.
              </div>
            </div>

            <TabloidButton
              onClick={async () => {
                if (!ocFantasyChar.trim() || !ocFantasyUni.trim()) return;
                setError(null);
                setScreen("oc-loading");
                let res = await getOcFantasyOpponent(ocFantasyChar.trim(), ocFantasyUni.trim());
                if (!res.ok) res = await getOcFantasyOpponent(ocFantasyChar.trim(), ocFantasyUni.trim());
                if (!res.ok) {
                  setError(`Couldn't pull a stat block. (${res.error}) Try a different character or check the spelling.`);
                  setScreen("oc-fantasy-input");
                  return;
                }
                const opp = res.data;
                if (!opp.habitatIcon) opp.habitatIcon = (OC_HABITATS.find(h => h.name === opp.habitat)?.icon) || "✨";
                const isSlotA = ocFantasySlot === "A";
                if (isSlotA) { setSlotA(opp); setSlotAType("fantasy"); }
                else         { setSlotB(opp); setSlotBType("fantasy"); }
                // Resume scenario flow if active; else return to slot picker. Pass the fresh value
                // so advanceScenario doesn't re-trigger this same fantasy input due to state batching.
                if (ocScenario) advanceScenario(ocScenario, isSlotA ? { slotA: opp } : { slotB: opp });
                else setScreen("oc-slots");
              }}
              disabled={!ocFantasyChar.trim() || !ocFantasyUni.trim()}
            >✦ Bring 'Em On →</TabloidButton>
            <div style={{ height:"8px" }}/>
            <TabloidButton variant="secondary" onClick={()=>{ setError(null); setScreen("oc-slots"); }}>← Cancel</TabloidButton>

            <PageFooter />
          </div>
        )}

        {/* ── OC LOADING (opponent generation) ──────────────────────── */}
        {screen === "oc-loading" && (
          <OcOpponentLoadingScreen
            opponentName={ocFantasyChar}
            opponentUni={ocFantasyUni}
            challengerName={
              // Whoever's already in the OTHER slot is the challenger. If both are still empty (fantasy vs fantasy),
              // skip the "you" framing entirely by using the opponent's universe as context.
              (ocFantasySlot === "B" ? slotA?.name : slotB?.name) || ""
            }
          />
        )}

        {/* ── OC PRE-FIGHT (both cards + odds + ring the bell) ──────── */}
        {/* ── OC VENUE PICKER (user won the coin flip) ──────────────── */}
        {screen === "oc-venue-pick" && slotA && slotB && (() => {
          const runFight = (venue) => {
            setOcChosenVenue(venue);
            const aIsHomeNow = slotAType === "fantasy";
            const bIsHomeNow = slotBType === "fantasy";
            const aWB = ocWeaponA ? OC_WEAPONS.find(w => w.name === ocWeaponA)?.bonus : undefined;
            const bWB = ocWeaponB ? OC_WEAPONS.find(w => w.name === ocWeaponB)?.bonus : undefined;
            const sim = simulateOcBattle(slotA, slotB, { aIsHome: aIsHomeNow, bIsHome: bIsHomeNow, aArmed: ocArmedA, bArmed: ocArmedB, aWeaponBonus: aWB, bWeaponBonus: bWB });
            setOcBattleSim(sim);
            setOcBattleNarr(null);
            setError(null);
            setScreen("oc-rumble");
            (async () => {
              let narr = await getOcBattleNarration(slotA, slotB, sim, { aArmed: ocArmedA, bArmed: ocArmedB, aWeapon: ocWeaponA, bWeapon: ocWeaponB, chosenVenue: venue });
              if (!narr || !narr.play_by_play) narr = await getOcBattleNarration(slotA, slotB, sim, { aArmed: ocArmedA, bArmed: ocArmedB, aWeapon: ocWeaponA, bWeapon: ocWeaponB, chosenVenue: venue });
              if (!narr || !narr.play_by_play) { setError("The reporter dropped their notes. Try Ring The Bell again."); setScreen("oc-prefight"); return; }
              setOcBattleNarr(narr);
              recordFightQuotes(narr, sim);
            })();
          };
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("oc-prefight")}>← Back</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC PLAYGROUND." subtitle="YOU CALL THE LOCATION" compact />

              <div style={{ border:`2px dashed ${C.red}`, padding:"12px 14px", marginBottom:"18px", background:C.paperSoft }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ YOU WON THE TOSS ★</div>
                <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                  Pick where {slotA.name} and {slotB.name} fight. Tap an option below or write your own.
                </p>
              </div>

              <SectionHeader title="PICK A VENUE" suffix="tap to set the scene" />
              <div style={{ display:"flex", flexDirection:"column", gap:"6px", marginBottom:"18px" }}>
                {OC_VENUES.map(v => (
                  <button key={v} type="button" onClick={()=>runFight(v)} style={{
                    textAlign:"left", padding:"12px 14px", background:C.paper, color:C.ink,
                    border:`1.5px solid ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                    fontSize:"14px", lineHeight:1.35,
                  }}>{v}</button>
                ))}
              </div>

              <SectionHeader title="OR WRITE YOUR OWN" suffix="anywhere goes" />
              <TextInput value={ocVenueCustom} onChange={setOcVenueCustom} placeholder="e.g. The principal's office on parent-teacher night" />
              <div style={{ height:"10px" }}/>
              <TabloidButton
                onClick={()=>{ if (ocVenueCustom.trim()) runFight(ocVenueCustom.trim()); }}
                disabled={!ocVenueCustom.trim()}
              >
                ✦ Use This Venue →
              </TabloidButton>
              <div style={{ height:"8px" }}/>
              <TabloidButton variant="secondary" onClick={()=>setScreen("oc-prefight")}>← Back</TabloidButton>

              <PageFooter />
            </div>
          );
        })()}

        {screen === "oc-prefight" && slotA && slotB && (() => {
          const a = slotA, b = slotB;
          const aIsHome = slotAType === "fantasy";
          const bIsHome = slotBType === "fantasy";
          const aWeaponObj = ocWeaponA ? OC_WEAPONS.find(w => w.name === ocWeaponA) : null;
          const bWeaponObj = ocWeaponB ? OC_WEAPONS.find(w => w.name === ocWeaponB) : null;
          const aWeaponBonus = aWeaponObj?.bonus;
          const bWeaponBonus = bWeaponObj?.bonus;
          const bdown = ocOddsBreakdown(a, b, { aIsHome, bIsHome, aArmed: ocArmedA, bArmed: ocArmedB, aWeaponBonus, bWeaponBonus });
          const oddsA = Math.round(bdown.oddsA * 100);
          const oddsB = 100 - oddsA;
          // Surface the real factors driving the odds — HP gap, ability count, weight, arms.
          const adv = [];
          const hpGap = ocHp(a) - ocHp(b);
          if (Math.abs(hpGap) >= 50) adv.push({ leader: hpGap > 0 ? a.name : b.name, line: `${hpGap > 0 ? a.name : b.name} has ${Math.abs(hpGap)} more HP.` });
          const abilGap = (a.abilities?.length || 0) - (b.abilities?.length || 0);
          if (Math.abs(abilGap) >= 2) adv.push({ leader: abilGap > 0 ? a.name : b.name, line: `${abilGap > 0 ? a.name : b.name} brings ${Math.abs(abilGap)} more abilities to the fight.` });
          const featGap = (a.features?.length || 0) - (b.features?.length || 0);
          if (Math.abs(featGap) >= 2) adv.push({ leader: featGap > 0 ? a.name : b.name, line: `${featGap > 0 ? a.name : b.name} has ${Math.abs(featGap)} more physical advantages.` });
          const weightGap = (a.weight || 70) - (b.weight || 70);
          if (Math.abs(weightGap) >= 30) adv.push({ leader: weightGap > 0 ? a.name : b.name, line: `${weightGap > 0 ? a.name : b.name} is +${Math.abs(weightGap)}kg heavier — more force, less speed.` });
          const heightGap = (a.height || 170) - (b.height || 170);
          if (Math.abs(heightGap) >= 25) adv.push({ leader: heightGap > 0 ? a.name : b.name, line: `${heightGap > 0 ? a.name : b.name} towers +${Math.abs(heightGap)}cm — more reach.` });
          if (ocArmedA && !ocArmedB) adv.push({ leader: a.name, line: `${a.name} is armed${ocWeaponA ? ` (${ocWeaponA})` : ""}. ${b.name} isn't.` });
          if (ocArmedB && !ocArmedA) adv.push({ leader: b.name, line: `${b.name} is armed${ocWeaponB ? ` (${ocWeaponB})` : ""}. ${a.name} isn't.` });
          if (ocArmedA && ocArmedB && ocWeaponA && ocWeaponB && ocWeaponA !== ocWeaponB) adv.push({ leader: null, line: `${a.name} brought a ${ocWeaponA}; ${b.name} brought a ${ocWeaponB}.` });
          const TinyCard = ({ c, accent }) => (
            <div style={{ border:`3px solid ${C.ink}`, background:C.paper, boxShadow:`6px 6px 0 ${accent || C.ink}`, marginBottom:"14px" }}>
              <div style={{ borderBottom:`2px solid ${C.ink}`, padding:"10px 12px", background: accent || C.paperSoft, color: accent ? C.paper : C.ink }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", opacity:0.85 }}>FIGHTER</div>
                <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"26px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>{c.name}</div>
                {c.tagline && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", opacity:0.85, marginTop:"2px" }}>— {c.tagline} —</div>}
              </div>
              <div style={{ padding:"10px 12px", display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.ink, background:C.paperSoft, borderBottom:`1.5px solid ${C.ink}`, flexWrap:"wrap", gap:"6px" }}>
                <span>{c.habitatIcon || "✨"} {c.habitat}</span>
                <span>{c.height ? `${inToFtIn(cmToIn(c.height))} · ${kgToLb(c.weight)}lb` : "?"}</span>
                <span style={{ color:C.red, fontWeight:700 }}>HP {ocHp(c)}</span>
              </div>
              {(c.abilities && c.abilities.length > 0) && (
                <div style={{ padding:"8px 12px", borderBottom:`1.5px dashed ${C.ink}` }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>ABILITIES</div>
                  <div style={{ fontFamily:FONT.display, fontSize:"13px", lineHeight:1.3, color:C.ink }}>{c.abilities.join(" · ")}</div>
                </div>
              )}
              {(c.features && c.features.length > 0) && (
                <div style={{ padding:"8px 12px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, fontWeight:700, marginBottom:"4px" }}>FEATURES</div>
                  <div style={{ fontFamily:FONT.display, fontSize:"13px", lineHeight:1.3, color:C.ink }}>{c.features.join(" · ")}</div>
                </div>
              )}
            </div>
          );
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("oc-slots")}>← Back</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC PLAYGROUND." subtitle="PRE-FIGHT" compact />

              {error && (
                <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ HEADS UP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>{error} Try Ring The Bell again.</p>
                </div>
              )}

              <SectionHeader title="FIGHTER A" suffix={a.universe || (slotAType === "you" ? "you" : slotAType === "oc" ? "your OC" : "")} />
              <TinyCard c={a} accent={C.red} />

              <div style={{ textAlign:"center", margin:"6px 0 14px" }}>
                <div style={{ display:"inline-block", border:`2px solid ${C.ink}`, padding:"4px 12px", fontFamily:FONT.display, fontWeight:900, fontSize:"22px", letterSpacing:"3px", background:C.ink, color:C.paper }}>VS</div>
              </div>

              <SectionHeader title="FIGHTER B" suffix={b.universe || (slotBType === "you" ? "you" : slotBType === "oc" ? "your OC" : "")} />
              <TinyCard c={b} accent={C.ink} />

              <SectionHeader title="THE ODDS" suffix="before a punch is thrown" />
              <div style={{ border:`3px solid ${C.ink}`, padding:"14px 14px 16px", marginBottom:"14px", background:C.paperSoft }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"6px" }}>
                  <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.red }}>{a.name} {oddsA}%</span>
                  <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.ink }}>{oddsB}% {b.name}</span>
                </div>
                <div style={{ height:"20px", border:`2px solid ${C.ink}`, display:"flex" }}>
                  <div style={{ width:`${oddsA}%`, background:C.red }}/>
                  <div style={{ width:`${oddsB}%`, background:C.ink }}/>
                </div>
                <div style={{ marginTop:"14px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"6px" }}>★ TALE OF THE TAPE ★</div>
                <ul style={{ margin:0, paddingLeft:"18px", display:"flex", flexDirection:"column", gap:"6px" }}>
                  {adv.length === 0 && bdown.aMods.length === 0 && bdown.bMods.length === 0 && (
                    <li style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>Stats are dead even. This one's a coin toss with attitude.</li>
                  )}
                  {adv.map((d, i) => (
                    <li key={`adv-${i}`} style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                      {d.line}
                    </li>
                  ))}
                  {bdown.aMods.map((m, i) => (
                    <li key={`am-${i}`} style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                      <strong>{a.name}</strong> {m.why} <span style={{ color:m.delta < 0 ? C.red : C.ink, fontWeight:700 }}>({m.delta > 0 ? "+" : ""}{m.delta}%)</span>.
                    </li>
                  ))}
                  {bdown.bMods.map((m, i) => (
                    <li key={`bm-${i}`} style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                      <strong>{b.name}</strong> {m.why} <span style={{ color:m.delta < 0 ? C.red : C.ink, fontWeight:700 }}>({m.delta > 0 ? "+" : ""}{m.delta}%)</span>.
                    </li>
                  ))}
                  {bdown.homeFieldA > 0 && (
                    <li style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                      ⚑ <strong>{a.name}</strong> on home turf — <span style={{ color:C.ink, fontWeight:700 }}>(+{bdown.homeFieldA}%)</span>.
                    </li>
                  )}
                  {bdown.homeFieldB > 0 && (
                    <li style={{ fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>
                      ⚑ <strong>{b.name}</strong> picks the venue — <span style={{ color:C.ink, fontWeight:700 }}>(+{bdown.homeFieldB}%)</span>.
                    </li>
                  )}
                  {bdown.homeFieldA > 0 && bdown.homeFieldB > 0 && (
                    <li style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", lineHeight:1.4, color:C.inkSoft }}>
                      Both fighters bring home-field — bonuses cancel out.
                    </li>
                  )}
                  <li style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.4, color:C.inkSoft }}>
                    Anything can happen. Stranger things have.
                  </li>
                </ul>
              </div>

              {/* Combat mode — armed/unarmed per fighter. Armed reveals a weapon picker that affects odds. */}
              <div style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"10px" }}>★ COMBAT MODE ★</div>
                {[
                  { who:"A", name:a.name, armed:ocArmedA, setArmed:setOcArmedA, weapon:ocWeaponA, setWeapon:setOcWeaponA },
                  { who:"B", name:b.name, armed:ocArmedB, setArmed:setOcArmedB, weapon:ocWeaponB, setWeapon:setOcWeaponB },
                ].map(({ who, name, armed, setArmed, weapon, setWeapon }) => (
                  <div key={who} style={{ marginBottom: who === "A" ? "12px" : 0 }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", color:C.ink, marginBottom:"6px", fontWeight:700 }}>{(name||who).toUpperCase()}</div>
                    <div style={{ display:"flex", gap:"4px", marginBottom:"6px" }}>
                      <Pill size="sm" selected={!armed} onClick={()=>{ setArmed(false); setWeapon(null); }}>Unarmed</Pill>
                      <Pill size="sm" selected={armed}  onClick={()=>{ setArmed(true); if (!weapon) setWeapon(OC_WEAPONS[0].name); }}>Armed</Pill>
                    </div>
                    {armed && (
                      <div style={{ display:"flex", gap:"4px", flexWrap:"wrap", paddingLeft:"6px", borderLeft:`2px solid ${C.red}` }}>
                        {OC_WEAPONS.map(w => (
                          <Pill key={w.name} size="sm" selected={weapon === w.name} onClick={()=>setWeapon(w.name)}>{w.name}</Pill>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <TabloidButton onClick={() => {
                setError(null);
                // Coin flip: 50/50 chance the user picks the venue. Otherwise Claude picks via the narration as usual.
                const userPicks = Math.random() < 0.5;
                if (userPicks && !ocChosenVenue) {
                  setOcVenueCustom("");
                  setScreen("oc-venue-pick");
                  return;
                }
                // Either Claude picks or the user already picked (e.g. on a rematch route — currently unused).
                const sim = simulateOcBattle(a, b, { aIsHome, bIsHome, aArmed: ocArmedA, bArmed: ocArmedB, aWeaponBonus, bWeaponBonus });
                setOcBattleSim(sim);
                setOcBattleNarr(null);
                setScreen("oc-rumble");
                (async () => {
                  let narr = await getOcBattleNarration(a, b, sim, { aArmed: ocArmedA, bArmed: ocArmedB, aWeapon: ocWeaponA, bWeapon: ocWeaponB, chosenVenue: ocChosenVenue });
                  if (!narr || !narr.play_by_play) narr = await getOcBattleNarration(a, b, sim, { aArmed: ocArmedA, bArmed: ocArmedB, aWeapon: ocWeaponA, bWeapon: ocWeaponB, chosenVenue: ocChosenVenue });
                  if (!narr || !narr.play_by_play) { setError("The reporter dropped their notes. Try Ring The Bell again."); setScreen("oc-prefight"); return; }
                  setOcBattleNarr(narr);
                  recordFightQuotes(narr, sim);
                })();
              }}>✦ Ring The Bell →</TabloidButton>
              <div style={{ height:"8px" }}/>
              <TabloidButton variant="secondary" onClick={()=>setScreen("oc-slots")}>← Change Fighters</TabloidButton>

              <PageFooter />
            </div>
          );
        })()}

        {/* ── OC READY TO RUMBLE ────────────────────────────────────── */}
        {screen === "oc-rumble" && slotA && slotB && (
          <OcRumbleScreen
            a={slotA}
            b={slotB}
            narrReady={!!ocBattleNarr}
            narrError={error}
            onAdvance={() => setScreen("oc-fight-result")}
            onCancel={() => setScreen("oc-prefight")}
          />
        )}

        {/* ── OC FIGHT RESULT ───────────────────────────────────────── */}
        {screen === "oc-fight-result" && slotA && slotB && ocBattleSim && ocBattleNarr && (() => {
          const a = slotA, b = slotB, sim = ocBattleSim, n = ocBattleNarr;
          const aIsHome = slotAType === "fantasy";
          const bIsHome = slotBType === "fantasy";
          const aWon = sim.winner === "a";
          const winnerName = aWon ? a.name : b.name;
          const loserName  = aWon ? b.name : a.name;
          const fmtT = (s) => { const x = Number(s)||0; return x < 60 ? `${x}s` : `${Math.floor(x/60)}m ${x%60}s`; };
          return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"6px" }}>
                <TextLink onClick={()=>setScreen("oc-prefight")}>← Pre-Fight</TextLink>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.inkSoft }}>OC PLAYGROUND</div>
                <span style={{ width:"60px" }}/>
              </div>
              <Masthead title="OC PLAYGROUND." subtitle="FIGHT REPORT" compact />
              {error && (
                <div style={{ border:`2px solid ${C.red}`, padding:"12px 14px", marginBottom:"14px", background:C.paperSoft }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ REMATCH HICCUP ★</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontSize:"14px", lineHeight:1.4, color:C.ink }}>{error}</p>
                </div>
              )}
              <div style={{ textAlign:"center", marginBottom:"14px" }}>
                <Stamp rotate={-3} size="11px">{aWon ? "★ YOU WON ★" : "★ YOU LOST ★"}</Stamp>
              </div>

              <div style={{ background:C.ink, color:C.paper, padding:"18px 16px", border:`3px solid ${C.ink}`, marginBottom:"14px" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"6px" }}>★ THE WINNER ★</div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:"10px" }}>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>{winnerName}</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, color:C.red }}>{fmtT(sim.durationSeconds)}</div>
                </div>
                {n.time_narrative && <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:"rgba(241,232,210,0.85)", marginTop:"8px" }}>{n.time_narrative}</div>}
              </div>

              {n.environment && (
                <>
                  <SectionHeader title="THE SCENE" suffix="where it went down" />
                  <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", lineHeight:1.5, color:C.ink, margin:"0 0 18px" }}>{n.environment}</p>
                </>
              )}

              {(n.pre_fight_remark || n.opening_words || n.pre_fight_grumble) && (() => {
                const speaker = n.pre_fight_remark_speaker || loserName;
                const text = n.pre_fight_remark || n.opening_words || n.pre_fight_grumble;
                return (
                  <>
                    <SectionHeader title="BEFORE THE BELL" suffix="last words" />
                    <div style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", marginBottom:"18px", background:C.paperSoft }}>
                      <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, fontWeight:700, marginBottom:"4px" }}>{speaker.toUpperCase()}, TO CAMERA</div>
                      <div style={{ color:C.red, fontFamily:FONT.display, fontSize:"16px", lineHeight:1 }}>❝</div>
                      <p style={{ margin:"0 0 0", fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", lineHeight:1.4 }}>{text}</p>
                    </div>
                  </>
                );
              })()}

              <SectionHeader title="BLOW BY BLOW" suffix="five beats, HP and all" />
              <div style={{ display:"flex", flexDirection:"column", gap:"12px", marginBottom:"18px" }}>
                {(n.play_by_play || []).map((beat, i) => {
                  const aHp = sim.aHp[i+1], bHp = sim.bHp[i+1];
                  return (
                    <div key={i} style={{ border:`2px solid ${C.ink}`, padding:"12px" }}>
                      <div style={{ display:"flex", gap:"10px", alignItems:"flex-start", marginBottom:"10px" }}>
                        <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.red, lineHeight:1, flexShrink:0, minWidth:"24px" }}>{i+1}</div>
                        <div style={{ fontFamily:FONT.display, fontSize:"15px", lineHeight:1.4, color:C.ink }}>{beat}</div>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:"4px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                          <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", fontWeight:700, color:C.red, width:"72px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={a.name}>{a.name.toUpperCase()}</div>
                          <div style={{ flex:1, height:"10px", border:`1.5px solid ${C.ink}`, background:C.paperSoft, position:"relative" }}>
                            <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${(aHp/sim.aHp[0])*100}%`, background:C.red }}/>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"12px", color:C.red, width:"60px", textAlign:"right" }}>{aHp}/{sim.aHp[0]}</div>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                          <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", fontWeight:700, color:C.ink, width:"72px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={b.name}>{b.name.toUpperCase()}</div>
                          <div style={{ flex:1, height:"10px", border:`1.5px solid ${C.ink}`, background:C.paperSoft, position:"relative" }}>
                            <div style={{ position:"absolute", top:0, left:0, bottom:0, width:`${(bHp/sim.bHp[0])*100}%`, background:C.ink }}/>
                          </div>
                          <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"12px", color:C.ink, width:"60px", textAlign:"right" }}>{bHp}/{sim.bHp[0]}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {(n.winner_quote || n.loser_quote) && (
                <>
                  <SectionHeader title="POST-FIGHT QUOTES" suffix="straight from the floor" />
                  <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"10px" }}>
                    {n.winner_quote && (
                      <div style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", background:C.paper }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>★ WINNER · {winnerName.toUpperCase()}</div>
                        <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"17px", lineHeight:1.4 }}>"{n.winner_quote}"</div>
                      </div>
                    )}
                    {n.loser_quote && (
                      <div style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", background:C.paperSoft }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, fontWeight:700, marginBottom:"4px" }}>LOSER · {loserName.toUpperCase()}</div>
                        <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"17px", lineHeight:1.4 }}>"{n.loser_quote}"</div>
                      </div>
                    )}
                  </div>
                  {/* Save confirmation — quotes auto-save to roster profiles via recordFightQuotes. */}
                  {(() => {
                    const aSaved = slotAType === "oc" || slotAType === "you";
                    const bSaved = slotBType === "oc" || slotBType === "you";
                    if (!aSaved && !bSaved) {
                      return (
                        <div style={{ border:`1.5px dashed ${C.inkMute}`, padding:"10px 12px", marginBottom:"18px", background:"transparent" }}>
                          <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", color:C.inkMute, textTransform:"uppercase" }}>Quotes won't save — neither fighter is on your roster.</div>
                        </div>
                      );
                    }
                    const aId = slotAType === "you" ? "youChar" : (slotA?.id || null);
                    const bId = slotBType === "you" ? "youChar" : (slotB?.id || null);
                    const labelA = slotA?.name || "Fighter A";
                    const labelB = slotB?.name || "Fighter B";
                    return (
                      <div style={{ border:`2px solid ${C.ink}`, padding:"10px 12px", marginBottom:"18px", background:C.paperSoft, display:"flex", flexDirection:"column", gap:"8px" }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", color:C.ink, fontWeight:700, textTransform:"uppercase" }}>
                          ✓ Saved to {[aSaved && labelA, bSaved && labelB].filter(Boolean).join(" & ")}'{[aSaved && labelA, bSaved && labelB].filter(Boolean).length > 1 ? "" : "s"} profile{[aSaved && labelA, bSaved && labelB].filter(Boolean).length > 1 ? "s" : ""}
                        </div>
                        <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
                          {aSaved && aId && (
                            <button type="button" onClick={()=>{ setOcProfileFor(aId); setOcProfileGenStatus({ loading:false, error:null }); setScreen("oc-profile"); }} style={{
                              padding:"7px 10px", background:C.paper, color:C.ink,
                              border:`1.5px solid ${C.ink}`, cursor:"pointer",
                              fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                            }}>📖 {labelA.toUpperCase()}'S PROFILE</button>
                          )}
                          {bSaved && bId && (
                            <button type="button" onClick={()=>{ setOcProfileFor(bId); setOcProfileGenStatus({ loading:false, error:null }); setScreen("oc-profile"); }} style={{
                              padding:"7px 10px", background:C.paper, color:C.ink,
                              border:`1.5px solid ${C.ink}`, cursor:"pointer",
                              fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", fontWeight:700,
                            }}>📖 {labelB.toUpperCase()}'S PROFILE</button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}

              {n.condition_of_loser && (
                <BlackCallout header={`POST-FIGHT · ${loserName.toUpperCase()}`} title="THE DAMAGE" body={n.condition_of_loser} />
              )}

              <div style={{ height:"18px" }}/>
              {(() => {
                // Rematch odds = base matchup odds from the same math used pre-fight. Honest to the math.
                const aWB = ocWeaponA ? OC_WEAPONS.find(w => w.name === ocWeaponA)?.bonus : undefined;
                const bWB = ocWeaponB ? OC_WEAPONS.find(w => w.name === ocWeaponB)?.bonus : undefined;
                const rematchPct = Math.round(ocOdds(a, b, { aIsHome, bIsHome, aArmed: ocArmedA, bArmed: ocArmedB, aWeaponBonus: aWB, bWeaponBonus: bWB }) * 100);
                return (
              <div style={{ border:`3px solid ${C.ink}`, padding:"14px 14px 16px", marginBottom:"14px", background:C.paperSoft }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, fontWeight:700 }}>★ REMATCH ODDS ★</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"36px", color:C.red, lineHeight:1 }}>{rematchPct}%</div>
                </div>
                <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute }}>chance {a.name} could take {b.name}, same stats, fresh roll</div>
              </div>
                );
              })()}

              {(() => {
                // Rematch picker: tap a strategy (or write your own) to run the rematch with it applied.
                // Engine gives +5% odds bump when a strategy is chosen, and the narration weaves it in.
                const runRematch = (strategy) => {
                  setError(null);
                  const trimmed = (strategy || "").trim();
                  const aWB2 = ocWeaponA ? OC_WEAPONS.find(w => w.name === ocWeaponA)?.bonus : undefined;
                  const bWB2 = ocWeaponB ? OC_WEAPONS.find(w => w.name === ocWeaponB)?.bonus : undefined;
                  const sim2 = simulateOcBattle(a, b, { aIsHome, bIsHome, aArmed: ocArmedA, bArmed: ocArmedB, aWeaponBonus: aWB2, bWeaponBonus: bWB2, strategyBump: trimmed ? 5 : 0 });
                  setOcBattleSim(sim2);
                  setOcBattleNarr(null);
                  setOcShowCustomStrategy(false);
                  setOcCustomStrategyInput("");
                  setScreen("oc-rumble");
                  (async () => {
                    let narr2 = await getOcBattleNarration(a, b, sim2, { strategy: trimmed, aArmed: ocArmedA, bArmed: ocArmedB, aWeapon: ocWeaponA, bWeapon: ocWeaponB, chosenVenue: ocChosenVenue });
                    if (!narr2 || !narr2.play_by_play) narr2 = await getOcBattleNarration(a, b, sim2, { strategy: trimmed, aArmed: ocArmedA, bArmed: ocArmedB, aWeapon: ocWeaponA, bWeapon: ocWeaponB, chosenVenue: ocChosenVenue });
                    if (!narr2 || !narr2.play_by_play) { setError("Reporter dropped their notes on the rematch. Try again."); setScreen("oc-fight-result"); return; }
                    setOcBattleNarr(narr2);
                    recordFightQuotes(narr2, sim2);
                  })();
                };
                return (
                  <>
                    <SectionHeader title="REMATCH" suffix="pick a strategy" />
                    <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"14px" }}>
                      {(n.rematch_strategies || []).map((s, i) => (
                        <button
                          key={i} type="button" onClick={()=>runRematch(s)}
                          style={{
                            textAlign:"left", padding:"12px 14px", background:C.paper, color:C.ink,
                            border:`2px solid ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                            fontSize:"15px", lineHeight:1.4, fontWeight:600,
                            display:"flex", gap:"10px", alignItems:"flex-start",
                          }}
                        >
                          <span style={{ color:C.red, fontWeight:900, flexShrink:0 }}>★</span>
                          <span>{s}</span>
                        </button>
                      ))}
                      {!ocShowCustomStrategy ? (
                        <button
                          type="button" onClick={()=>setOcShowCustomStrategy(true)}
                          style={{
                            textAlign:"left", padding:"12px 14px", background:C.paper, color:C.ink,
                            border:`2px dashed ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                            fontSize:"15px", lineHeight:1.4, fontWeight:600,
                            display:"flex", gap:"10px", alignItems:"flex-start",
                          }}
                        >
                          <span style={{ color:C.red, fontWeight:900, flexShrink:0 }}>✎</span>
                          <span>Choose your own strategy…</span>
                        </button>
                      ) : (
                        <div style={{ border:`2px dashed ${C.ink}`, padding:"12px", background:C.paperSoft }}>
                          <Label>Your Strategy</Label>
                          <TextInput value={ocCustomStrategyInput} onChange={setOcCustomStrategyInput} placeholder="e.g. Open with The Old Switcheroo to throw them off-balance." />
                          <div style={{ height:"10px" }}/>
                          <TabloidButton onClick={()=>{ if (ocCustomStrategyInput.trim()) runRematch(ocCustomStrategyInput); }} disabled={!ocCustomStrategyInput.trim()}>
                            ✦ Run It →
                          </TabloidButton>
                          <div style={{ textAlign:"center", marginTop:"6px" }}>
                            <TextLink onClick={()=>{ setOcShowCustomStrategy(false); setOcCustomStrategyInput(""); }}>← Cancel</TextLink>
                          </div>
                        </div>
                      )}
                      <button
                        type="button" onClick={()=>runRematch("")}
                        style={{
                          textAlign:"center", padding:"10px 14px", background:"transparent", color:C.inkMute,
                          border:`1px dashed ${C.inkMute}`, cursor:"pointer", fontFamily:FONT.mono,
                          fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase",
                        }}
                      >↻ Or just run it back (no strategy)</button>
                    </div>
                  </>
                );
              })()}

              <SectionHeader title="WHAT NEXT?" suffix="" />
              <TabloidButton variant="secondary" onClick={()=>{ setSlotA(null); setSlotB(null); setSlotAType(null); setSlotBType(null); setOcBattleSim(null); setOcBattleNarr(null); setOcFantasyChar(""); setOcFantasyUni(""); setOcBuildTarget("oc"); setOcShowCustomStrategy(false); setOcCustomStrategyInput(""); setScreen("oc-slots"); }}>✦ New Matchup</TabloidButton>
              <div style={{ height:"8px" }}/>
              <TabloidButton variant="secondary" onClick={()=>setScreen("home")}>← Home</TabloidButton>

              <PageFooter />
            </div>
          );
        })()}

        {/* ── LOADING ───────────────────────────────────────────────── */}
        {screen === "loading" && (
          <div style={{ paddingTop:"60px" }}>
            <Masthead title={mastheadTitle} subtitle={experience === "arena" ? "STEPPING INTO THE RING" : experience === "feast" ? "LOADING THE TABLE" : "READING THE TEA LEAVES"} compact />
            <div style={{ textAlign:"center", marginTop:"40px" }}>
              <Stamp rotate={-3} size="12px">{experience === "arena" ? "★ STAY RINGSIDE ★" : experience === "feast" ? "★ BIB UP ★" : "★ STAY TUNED ★"}</Stamp>
              <h2 style={{ margin:"24px 0 12px", fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1.05 }}>
                {experience === "arena" ? <>ARE YOU READY<br/>TO RUMBLE?</> : experience === "feast" ? <>TUCK IN<br/>YOUR NAPKIN.</> : <>MATCHMAKING<br/>IN PROGRESS</>}
              </h2>
              <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", color:C.inkSoft, margin:"0 16px 6px" }}>{loadingLines.a}</p>
              <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", color:C.inkSoft, margin:"0 16px 16px" }}>{loadingLines.b}</p>
              <LoadingDots />
            </div>
            <PageFooter />
          </div>
        )}

        {/* ── PICK CATEGORY (retry) ─────────────────────────────────── */}
        {screen === "pick" && (
          <div>
            <Masthead title={mastheadTitle} subtitle={experience === "fc" ? "DIFFERENT CRUSH, SAME YOU" : "DIFFERENT UNIVERSE, SAME YOU"} compact />

            <div style={{ textAlign:"center", marginBottom:"20px" }}>
              <Stamp rotate={3}>★ ENCORE ★</Stamp>
              <h2 style={{ margin:"14px 0 6px", fontFamily:FONT.display, fontWeight:900, fontSize:"32px", lineHeight:1.05 }}>
                {experience === "fc" ? <>TRY A NEW<br/>CRUSH</> : <>TRY A NEW<br/>UNIVERSE</>}
              </h2>
              <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", color:C.inkSoft, margin:"0 8px" }}>
                {experience === "fc" ? "Same you. New target. Brand-new verdict." : "Same you. New world. Three brand-new matches."}
              </p>
            </div>

            <div style={{ border:`2px solid ${C.ink}`, padding:"18px", background:C.paperSoft, marginBottom:"16px" }}>
              {experience === "fc" ? (
                <>
                  <Label>Character</Label>
                  <TextInput value={crushCharacter} onChange={setCrushCharacter} placeholder="e.g. Hermione Granger" />
                  <div style={{ height:"14px" }}/>
                  <Label>From</Label>
                  <TextInput value={crushUniverse} onChange={setCrushUniverse} placeholder="e.g. Harry Potter" />
                </>
              ) : (
                <>
                  <Label>Pick Your World</Label>
                  <TextInput value={universe} onChange={setUniverse} placeholder="e.g. Harry Potter, Star Wars, Marvel…" />
                  <div style={{ fontFamily:FONT.display, fontSize:"13px", fontStyle:"italic", color:C.inkMute, marginTop:"6px", lineHeight:1.45 }}>
                    Any fictional universe — leave blank for a wild card.
                  </div>
                </>
              )}
            </div>

            <TabloidButton
              onClick={runAnalysis}
              disabled={experience === "fc" && !(crushCharacter.trim() && crushUniverse.trim())}
            >
              {experience === "fc" ? "✦ Find Out Again →" : "✦ Find Three More →"}
            </TabloidButton>
            <div style={{ textAlign:"center", marginTop:"2px" }}>
              <TextLink onClick={reset}>← Start completely over</TextLink>
            </div>
            <PageFooter />
          </div>
        )}

        {/* ── RESULTS — MARRY / KISS / KILL ─────────────────────────── */}
        {screen === "results" && experience === "mkk" && results && (
          <div>
            <Masthead title={mastheadTitle} no="1" subtitle="THE FANTASY EDITION" />

            <MetaRow
              left={`STARRING: ${(name||"ANONYMOUS").toUpperCase()}`}
              right={`ISSUE #${caseNo}`}
              border={false}
            />
            <RuleHr mb="18px" />

            {/* Headline section */}
            <div style={{ position:"relative", marginBottom:"22px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
                <span style={{ color:C.red, fontFamily:FONT.mono, fontSize:"11px", fontWeight:700, letterSpacing:"2px", textTransform:"uppercase" }}>★ EXCLUSIVE ★ THE BIG REVEAL</span>
                <div style={{ marginLeft:"auto" }}>
                  <Stamp rotate={6}>★ JUICY ★</Stamp>
                </div>
              </div>
              <h2 style={{ margin:0, fontFamily:FONT.display, fontWeight:900, fontSize:"42px", lineHeight:0.95, letterSpacing:"-1px", textTransform:"uppercase" }}>
                {headlineLines.map((line, i) => (
                  <React.Fragment key={i}>
                    {i === headlineLines.length - 1
                      ? <span style={{ fontStyle:"italic", fontWeight:700, color:C.red }}>{line}</span>
                      : <>{line}<br/></>}
                  </React.Fragment>
                ))}
              </h2>
              <p style={{ margin:"16px 0 6px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", lineHeight:1.45, color:C.ink, textAlign:"center" }}>
                Sources confirm {name || "the subject"} would absolutely marry {results.marry?.character} — and the rest of the lineup is even more incriminating. We don't make the rules.
              </p>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", textAlign:"center", color:C.inkSoft }}>By Our Resident Matchmaker · 4:47 AM</div>
            </div>

            <RuleHr double mb="18px" />

            {/* ── THE FATEFUL THREE — match cards (the payoff!) */}
            <SectionHeader title="THE FATEFUL THREE" suffix="we're not editorializing" />
            {["marry","screw","kill"].map((type, i) => (
              <MatchCard key={type} matchType={type} match={results[type]} tmdbImage={tmdbImages[type]} idx={i+1} />
            ))}

            <RuleHr double mb="18px" />

            {/* ── THE INSIDE SCOOP — score bars */}
            <SectionHeader title="THE INSIDE SCOOP" suffix="the receipts" />
            {EVIDENCE.map((e, i) => {
              const raw = results.profile[e.key];
              const score = Math.round((raw/7)*100);
              const isLow = lowAxes.has(e.key);
              const status = SCORE_STATUS[e.key][isLow ? "low" : "high"];
              return (
                <HatchedBar
                  key={e.key}
                  label={e.label}
                  score={score}
                  exhibit={`0${i+1}`.slice(-2)}
                  status={status}
                  isLow={isLow}
                />
              );
            })}

            {/* Gottman callout (kept, but reframed as a knowing wink) */}
            {results.coreValuesWarning && (
              <BlackCallout
                header="FROM THE RESEARCH DESK"
                title="Core Values: Inconveniently Strong"
                body={`Research says below-65% values matches make for the most opinionated long-term debates. We recommend snacks, low lighting, and a referee.`}
              />
            )}

            {/* Correspondent's report */}
            {romanticProfile && (
              <div style={{ border:`2px solid ${C.ink}`, padding:"18px 18px 20px", marginBottom:"24px", background:C.paperSoft }}>
                <div style={{ marginBottom:"10px", borderBottom:`1px solid ${C.ink}`, paddingBottom:"6px" }}>
                  <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", letterSpacing:"1px", textTransform:"uppercase" }}>The Matchmaker's Take</span>
                </div>
                <p style={{ margin:0, fontFamily:FONT.display, fontSize:"16px", lineHeight:1.55, fontStyle:"italic" }}>{romanticProfile}</p>
              </div>
            )}

            {/* Pull quote */}
            <PullQuote
              quote={`Three matches. Zero regrets. Several follow-up questions.`}
              attribution="Editor's Note"
            />

            {/* ── PUBLIC NOTICE — streaming rec */}
            {streamingRec && (
              <div style={{ border:`2px solid ${C.ink}`, marginBottom:"24px", background:C.paperSoft }}>
                <div style={{ background:C.ink, color:C.paper, padding:"8px 14px", display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase" }}>
                  <span>★ Editor's Pick</span>
                  <span>Watch This Weekend</span>
                </div>
                <a
                  href={STREAMING_LINKS[streamingRec.service] || `https://www.google.com/search?q=${encodeURIComponent(streamingRec.title+" "+streamingRec.service)}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ display:"block", padding:"16px 18px", textDecoration:"none", color:C.ink }}
                >
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkSoft, marginBottom:"4px" }}>BASED ON YOUR MARRY MATCH</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1.1 }}>{streamingRec.title}</div>
                  <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.45, color:C.inkSoft, margin:"4px 0 10px" }}>{streamingRec.why}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", border:`1.5px solid ${C.ink}`, padding:"4px 10px" }}>{streamingRec.service} · {streamingRec.genre}</span>
                    <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.red }}>WATCH IT →</span>
                  </div>
                </a>
              </div>
            )}

            {/* ── THE CLASSIFIEDS — affiliate recs */}
            {adRecs.length > 0 && (
              <div style={{ marginBottom:"24px", border:`2px solid ${C.ink}` }}>
                <div style={{ background:C.ink, color:C.paper, padding:"8px 14px", display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase" }}>
                  <span>★ The Classifieds</span>
                  <span>Picked For You</span>
                </div>
                <div>
                  {adRecs.map((rec, i) => (
                    <a key={i} href={buildAffiliateLink(rec.searchTerm, rec.category)} target="_blank" rel="noopener noreferrer" style={{
                      display:"flex", padding:"12px 14px", textDecoration:"none", color:C.ink,
                      borderBottom: i < adRecs.length-1 ? `1px dashed ${C.ink}` : "none", background:C.paper, gap:"12px", alignItems:"flex-start"
                    }}>
                      <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, width:"28px", textAlign:"center", color:C.red }}>§</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginBottom:"2px" }}>{rec.category}</div>
                        <div style={{ fontFamily:FONT.display, fontSize:"15px", fontWeight:700, lineHeight:1.2 }}>{rec.title}</div>
                        <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", lineHeight:1.4, color:C.inkSoft, marginTop:"2px" }}>{rec.description}</div>
                      </div>
                      <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.red, flexShrink:0, alignSelf:"center" }}>↗</span>
                    </a>
                  ))}
                </div>
                <div style={{ padding:"8px 14px", background:C.paperSoft, borderTop:`1px solid ${C.ink}`, fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, textAlign:"center" }}>
                  Affiliate links · Tiny commission, no extra cost
                </div>
              </div>
            )}

            <RuleHr double mb="18px" />

            {/* ── SPREAD THE WORD ── */}
            <SectionHeader title="SPREAD THE WORD" suffix="make it everyone's problem" />
            <div style={{ border:`1.5px dashed ${C.ink}`, padding:"16px 18px", marginBottom:"14px", background:C.paperSoft }}>
              <p style={{ margin:0, fontFamily:FONT.mono, fontSize:"12px", lineHeight:1.7, whiteSpace:"pre-wrap", color:C.ink }}>{shareText}</p>
            </div>
            <TabloidButton onClick={() => {
              navigator.clipboard?.writeText(shareText).then(()=>alert("Copied. Paste it anywhere.")).catch(()=>alert("Select the text above and copy manually."));
            }}>📋 Copy The Dish</TabloidButton>
            <div style={{ height:"8px" }}/>
            <TabloidButton variant="secondary" onClick={()=>setShowShare(true)}>📸 Tabloid Front Page</TabloidButton>

            <div style={{ height:"22px" }}/>
            <RuleHr double mb="18px" />

            {/* ── WHAT NEXT ── */}
            <SectionHeader title="WHAT NEXT?" suffix="again? again." />
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              <TabloidButton variant="secondary" onClick={retryCategory}>↻ Try a New Universe</TabloidButton>
              <TabloidButton variant="secondary" onClick={reset}>✕ Start Over</TabloidButton>
            </div>

            <PageFooter />
          </div>
        )}

        {/* ── RESULTS — FANTASY CRUSH ───────────────────────────────── */}
        {screen === "results" && experience === "fc" && fcResult && (
          <div>
            <Masthead title={mastheadTitle} no="1" subtitle="THE FANTASY EDITION" />

            <MetaRow
              left={`STARRING: ${(name||"ANONYMOUS").toUpperCase()}`}
              right={`ISSUE #${caseNo}`}
              border={false}
            />
            <RuleHr mb="18px" />

            {/* Headline */}
            <div style={{ position:"relative", marginBottom:"22px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
                <span style={{ color:C.red, fontFamily:FONT.mono, fontSize:"11px", fontWeight:700, letterSpacing:"2px", textTransform:"uppercase" }}>★ EXCLUSIVE ★ THE FEELING IS…</span>
                <div style={{ marginLeft:"auto" }}>
                  <Stamp rotate={6}>★ HOT ★</Stamp>
                </div>
              </div>
              <h2 style={{ margin:0, fontFamily:FONT.display, fontWeight:900, fontSize:"54px", lineHeight:0.92, letterSpacing:"-1.5px", textTransform:"uppercase", color:C.red }}>
                {fcResult.verdict}
              </h2>
              <p style={{ margin:"16px 0 6px", fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", lineHeight:1.45, color:C.ink, textAlign:"center" }}>
                Sources confirm: {fcResult.character} of {fcResult.universe} has {fcResult.score >= 50 ? "very strong" : "extremely mixed"} feelings about {name || "the reader"} — and we have receipts. Read on.
              </p>
              <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", textAlign:"center", color:C.inkSoft }}>By Our Resident Matchmaker · 4:47 AM</div>
            </div>

            <RuleHr double mb="18px" />

            {/* ── THE CRUSH — hero card */}
            <SectionHeader title="THE CRUSH" suffix="under interrogation" />
            <div style={{ border:`2px solid ${C.ink}`, background:C.paper, marginBottom:"22px" }}>
              {/* Header strip */}
              <div style={{ background:C.ink, color:C.paper, padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase" }}>★ THE CRUSH</span>
                <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"16px", letterSpacing:"4px", color:C.red }}>{fcResult.verdict}</span>
                <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red }}>{fcResult.score}%</span>
              </div>

              {/* Portrait */}
              {tmdbImages.fc && (
                <div style={{ height:"180px", overflow:"hidden", borderBottom:`1px solid ${C.ink}`, position:"relative" }}>
                  <img src={tmdbImages.fc} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", filter:"grayscale(60%) contrast(1.1) sepia(0.1)" }}/>
                  <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(241,232,210,0.05), rgba(15,15,15,0.40))" }}/>
                  <div style={{ position:"absolute", bottom:"6px", left:"10px", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.paper, textShadow:"0 1px 4px rgba(0,0,0,0.7)" }}>★ AS SEEN IN · {fcResult.universe}</div>
                </div>
              )}

              {/* Name + score */}
              <div style={{ padding:"16px 18px 12px", display:"flex", alignItems:"flex-end", gap:"14px", borderBottom:`1px solid ${C.ink}` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"4px" }}>THE CRUSH</div>
                  <div style={{ fontFamily:FONT.display, fontSize:"24px", fontWeight:900, lineHeight:1.05 }}>{fcResult.character}</div>
                  <div style={{ fontFamily:FONT.display, fontSize:"13px", fontStyle:"italic", color:C.inkSoft, marginTop:"2px" }}>of {fcResult.universe}</div>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginTop:"8px" }}>★ DO THEY LOVE YOU BACK? ★ {fcResult.verdict}</div>
                </div>
                <div style={{ textAlign:"right", flexShrink:0 }}>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"48px", color:C.red, lineHeight:1 }}>{fcResult.score}%</div>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute }}>Interest level</div>
                </div>
              </div>

              {/* First impression */}
              {fcResult.first_impression && (
                <div style={{ padding:"14px 18px 0" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"4px" }}>FIRST IMPRESSION</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"17px", lineHeight:1.5, color:C.ink }}>“{fcResult.first_impression}”</p>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginTop:"4px" }}>— {fcResult.character}, on the record</div>
                </div>
              )}

              {/* What they think */}
              <div style={{ padding:"14px 18px 0" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"4px" }}>WHAT {fcPro.subj} THINK{fcPro.thirdS} OF YOU</div>
                <p style={{ margin:0, fontFamily:FONT.display, fontSize:"16px", lineHeight:1.55, color:C.ink }}>{fcResult.what_they_think}</p>
              </div>

              {/* The relationship */}
              <div style={{ padding:"14px 18px 0" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"4px" }}>THE RELATIONSHIP</div>
                <p style={{ margin:0, fontFamily:FONT.display, fontSize:"16px", lineHeight:1.55, color:C.ink }}>{fcResult.relationship}</p>
              </div>

              {/* They'd love + would drive them crazy */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1px", background:C.ink, marginTop:"14px", borderTop:`1px solid ${C.ink}` }}>
                <div style={{ background:C.paperSoft, padding:"14px 16px" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginBottom:"6px" }}>★ {fcPro.subj}'D LOVE</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.45, color:C.ink }}>“{fcResult.they_love}”</p>
                </div>
                <div style={{ background:C.paperSoft, padding:"14px 16px" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginBottom:"6px" }}>★ WOULD DRIVE {fcPro.obj} CRAZY</div>
                  <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.45, color:C.ink }}>“{fcResult.they_dislike}”</p>
                </div>
              </div>

              {/* Opening line — dashed quote box */}
              {fcResult.opening_line && (
                <div style={{ padding:"14px 18px 18px" }}>
                  <div style={{ border:`1.5px dashed ${C.red}`, padding:"14px 16px", background:C.paper }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginBottom:"4px" }}>THE FIRST THING {fcPro.subj}'D SAY</div>
                    <p style={{ margin:0, fontFamily:FONT.display, fontStyle:"italic", fontSize:"17px", lineHeight:1.5, color:C.ink }}>“{fcResult.opening_line}”</p>
                  </div>
                </div>
              )}
            </div>

            <RuleHr double mb="18px" />

            {/* ── THE INSIDE SCOOP — score bars */}
            <SectionHeader title="THE INSIDE SCOOP" suffix="the receipts" />
            {EVIDENCE.map((e, i) => {
              const raw = fcResult.profile[e.key];
              const score = Math.round((raw/7)*100);
              const isLow = lowAxes.has(e.key);
              const status = SCORE_STATUS[e.key][isLow ? "low" : "high"];
              return (
                <HatchedBar
                  key={e.key}
                  label={e.label}
                  score={score}
                  exhibit={`0${i+1}`.slice(-2)}
                  status={status}
                  isLow={isLow}
                />
              );
            })}

            {fcResult.coreValuesWarning && (
              <BlackCallout
                header="FROM THE RESEARCH DESK"
                title="Core Values: Inconveniently Strong"
                body={`Research says below-65% values matches make for the most opinionated long-term debates. We recommend snacks, low lighting, and a referee.`}
              />
            )}

            {romanticProfile && (
              <div style={{ border:`2px solid ${C.ink}`, padding:"18px 18px 20px", marginBottom:"24px", background:C.paperSoft }}>
                <div style={{ marginBottom:"10px", borderBottom:`1px solid ${C.ink}`, paddingBottom:"6px" }}>
                  <span style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", letterSpacing:"1px", textTransform:"uppercase" }}>The Matchmaker's Take</span>
                </div>
                <p style={{ margin:0, fontFamily:FONT.display, fontSize:"16px", lineHeight:1.55, fontStyle:"italic" }}>{romanticProfile}</p>
              </div>
            )}

            <PullQuote
              quote={`The crush has spoken. Now we have to live with it.`}
              attribution="Editor's Note"
            />

            {/* Editor's Pick — streaming */}
            {streamingRec && (
              <div style={{ border:`2px solid ${C.ink}`, marginBottom:"24px", background:C.paperSoft }}>
                <div style={{ background:C.ink, color:C.paper, padding:"8px 14px", display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase" }}>
                  <span>★ Editor's Pick</span>
                  <span>Watch This Weekend</span>
                </div>
                <a
                  href={STREAMING_LINKS[streamingRec.service] || `https://www.google.com/search?q=${encodeURIComponent(streamingRec.title+" "+streamingRec.service)}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ display:"block", padding:"16px 18px", textDecoration:"none", color:C.ink }}
                >
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkSoft, marginBottom:"4px" }}>BASED ON YOUR CRUSH</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1.1 }}>{streamingRec.title}</div>
                  <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.45, color:C.inkSoft, margin:"4px 0 10px" }}>{streamingRec.why}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", border:`1.5px solid ${C.ink}`, padding:"4px 10px" }}>{streamingRec.service} · {streamingRec.genre}</span>
                    <span style={{ fontFamily:FONT.mono, fontSize:"11px", letterSpacing:"2px", textTransform:"uppercase", color:C.red }}>WATCH IT →</span>
                  </div>
                </a>
              </div>
            )}

            {/* The Classifieds */}
            {adRecs.length > 0 && (
              <div style={{ marginBottom:"24px", border:`2px solid ${C.ink}` }}>
                <div style={{ background:C.ink, color:C.paper, padding:"8px 14px", display:"flex", justifyContent:"space-between", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase" }}>
                  <span>★ The Classifieds</span>
                  <span>Picked For You</span>
                </div>
                <div>
                  {adRecs.map((rec, i) => (
                    <a key={i} href={buildAffiliateLink(rec.searchTerm, rec.category)} target="_blank" rel="noopener noreferrer" style={{
                      display:"flex", padding:"12px 14px", textDecoration:"none", color:C.ink,
                      borderBottom: i < adRecs.length-1 ? `1px dashed ${C.ink}` : "none", background:C.paper, gap:"12px", alignItems:"flex-start"
                    }}>
                      <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1, width:"28px", textAlign:"center", color:C.red }}>§</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, marginBottom:"2px" }}>{rec.category}</div>
                        <div style={{ fontFamily:FONT.display, fontSize:"15px", fontWeight:700, lineHeight:1.2 }}>{rec.title}</div>
                        <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"13px", lineHeight:1.4, color:C.inkSoft, marginTop:"2px" }}>{rec.description}</div>
                      </div>
                      <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.red, flexShrink:0, alignSelf:"center" }}>↗</span>
                    </a>
                  ))}
                </div>
                <div style={{ padding:"8px 14px", background:C.paperSoft, borderTop:`1px solid ${C.ink}`, fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.inkMute, textAlign:"center" }}>
                  Affiliate links · Tiny commission, no extra cost
                </div>
              </div>
            )}

            <RuleHr double mb="18px" />

            {/* Spread the word */}
            <SectionHeader title="SPREAD THE WORD" suffix="make it everyone's problem" />
            <div style={{ border:`1.5px dashed ${C.ink}`, padding:"16px 18px", marginBottom:"14px", background:C.paperSoft }}>
              <p style={{ margin:0, fontFamily:FONT.mono, fontSize:"12px", lineHeight:1.7, whiteSpace:"pre-wrap", color:C.ink }}>{shareText}</p>
            </div>
            <TabloidButton onClick={() => {
              navigator.clipboard?.writeText(shareText).then(()=>alert("Copied. Paste it anywhere.")).catch(()=>alert("Select the text above and copy manually."));
            }}>📋 Copy The Dish</TabloidButton>

            <div style={{ height:"22px" }}/>
            <RuleHr double mb="18px" />

            <SectionHeader title="WHAT NEXT?" suffix="again? again." />
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              <TabloidButton variant="secondary" onClick={retryCategory}>↻ Try a New Crush</TabloidButton>
              <TabloidButton variant="secondary" onClick={reset}>✕ Start Over</TabloidButton>
            </div>

            <PageFooter />
          </div>
        )}

        {/* ── ARENA RESULTS ─────────────────────────────────────────── */}
        {screen === "results" && (experience === "arena" || experience === "feast") && arenaResult && (() => {
          const isFeast = experience === "feast";
          const r = arenaResult;
          const readerName = (name || "You").trim() || "You";
          const youWon = (r.winner_role || "").toLowerCase().trim() === "reader";
          const winnerName = youWon ? readerName : r.character;
          const loserName  = youWon ? r.character : readerName;
          const fmtTime = (s) => {
            const n = Number(s) || 0;
            if (n < 60) return `${n}s`;
            if (n < 3600) return `${Math.floor(n/60)}m ${n%60}s`;
            return `${Math.floor(n/3600)}h ${Math.floor((n%3600)/60)}m`;
          };
          // Per-fight variant picks for the masthead subtitle and win/loss stamp (seeded from the result so re-renders are stable).
          const variantSeed = (() => {
            const s = `${r.character||""}|${r.location_pick||""}|${r.time_seconds||0}|${r.winner_role||""}`;
            let h = 0; for (let i=0; i<s.length; i++) h = (h*31 + s.charCodeAt(i)) >>> 0;
            return h;
          })();
          const subtitleVariants = isFeast
            ? ["FEAST REPORT","TABLE TALK","FORK & KNIFE EXCLUSIVE","THE GLUTTON GAZETTE","PLATE BY PLATE","BIG BITE BULLETIN"]
            : ["RING REPORT","FIGHT NIGHT","RINGSIDE EXCLUSIVE","THE SLUGFEST","ROUND BY ROUND","BLOW BY BLOW"];
          const winStampVariants  = isFeast
            ? ["★ STILL HUNGRY ★","★ EMPTY PLATE ★","★ ATE THEIR LUNCH ★","★ UNDEFEATED APPETITE ★","★ BELT-NOTCH NEEDED ★"]
            : ["★ UNDISPUTED ★","★ CLEAN SWEEP ★","★ KO IN THEIR FAVOR ★","★ WALKED IT OFF ★","★ STILL STANDING ★"];
          const lossStampVariants = isFeast
            ? ["★ TAPPED OUT ★","★ FOOD COMA ★","★ THREW IN THE NAPKIN ★","★ BLOATED & BEATEN ★","★ COULD NOT FINISH ★"]
            : ["★ KO CONFIRMED ★","★ DECISIVE LOSS ★","★ DOWN AND OUT ★","★ COUNTED OUT ★","★ FACE-PLANTED ★"];
          const subtitle = subtitleVariants[variantSeed % subtitleVariants.length];
          const stampText = youWon
            ? winStampVariants[variantSeed % winStampVariants.length]
            : lossStampVariants[variantSeed % lossStampVariants.length];
          return (
            <div>
              <Masthead title={mastheadTitle} no={String(globalIdx+1) || "1"} subtitle={subtitle} compact />
              <MetaRow left={`CASE №. ${caseNo}`} right={isFeast ? "FANTASY FEAST" : "FANTASY ARENA"} />
              <RuleHr double mb="14px" />

              <div style={{ textAlign:"center", marginBottom:"14px" }}>
                <Stamp rotate={-3} size="11px">{stampText}</Stamp>
              </div>

              {/* Ref ceremony — opens the fight/feast cleanly with a back-and-forth dialogue. Variant picked randomly. */}
              {(() => {
                const opp = r.character || "the opponent";
                const oppFirst = opp.split(/\s+/)[0];
                const oppLast  = opp.split(/\s+/).slice(-1)[0];
                const loc = r.location_pick || "right here";
                // When the opponent picks a location named after themselves, they'd say "my X" not "Steve's X".
                const personalize = (s) => {
                  if (!s) return s;
                  let out = s;
                  const escape = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                  out = out.replace(new RegExp(`\\b${escape(opp)}'s\\b`, "gi"), "my");
                  if (oppFirst) out = out.replace(new RegExp(`\\b${escape(oppFirst)}'s\\b`, "gi"), "my");
                  if (oppLast && oppLast !== oppFirst) out = out.replace(new RegExp(`\\b${escape(oppLast)}'s\\b`, "gi"), "my");
                  return out;
                };
                const locForOpp = personalize(loc);
                const READER = readerName.toUpperCase();
                const OPP = oppFirst.toUpperCase();
                const variants = isFeast ? [
                  // Eating contest MC
                  [
                    { who:"MC",     text:`${readerName}, who you want a piece of?` },
                    { who:READER,   text:`I challenge ${opp}.` },
                    { who:"MC",     text:`${opp}, the gauntlet's down. Where do we eat?` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"MC",     text:`Bibs on. Forks up.` },
                  ],
                  // County fair announcer
                  [
                    { who:"ANNOUNCER", text:`Ladies and gentlemen — ${readerName} has stepped forward. Who's the challenge?` },
                    { who:READER,      text:`${opp}.` },
                    { who:"ANNOUNCER", text:`${opp}, the table awaits. Where shall we set it?` },
                    { who:OPP,         text:`${locForOpp}.` },
                    { who:"ANNOUNCER", text:`Stomachs to the table. Let the feast begin.` },
                  ],
                  // Trash-talk diner
                  [
                    { who:"MC",     text:`${readerName} — who you out-eating today?` },
                    { who:READER,   text:`${opp}. Bring the food.` },
                    { who:"MC",     text:`${opp}, they think they can keep up. Where's the table?` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"MC",     text:`May the bigger stomach win.` },
                  ],
                  // Iron-chef framing
                  [
                    { who:"CHAIRMAN", text:`${readerName}, declare your challenger.` },
                    { who:READER,     text:`I call out ${opp}.` },
                    { who:"CHAIRMAN", text:`${opp}, you've been called. The venue is yours.` },
                    { who:OPP,        text:`${locForOpp}.` },
                    { who:"CHAIRMAN", text:`Allez cuisine.` },
                  ],
                  // Hype host
                  [
                    { who:"HOST",   text:`${readerName} — point at the one you can out-eat.` },
                    { who:READER,   text:`${opp}. That one.` },
                    { who:"HOST",   text:`${opp}, you've been picked. Set the table where you want.` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"HOST",   text:`First to tap out loses.` },
                  ],
                  // Tavernkeeper
                  [
                    { who:"TAVERNKEEP", text:`${readerName}, who do you eat against?` },
                    { who:READER,       text:`${opp}.` },
                    { who:"TAVERNKEEP", text:`${opp}, you've been chosen. Name the table.` },
                    { who:OPP,          text:`${locForOpp}.` },
                    { who:"TAVERNKEEP", text:`Aprons on. Plates coming.` },
                  ],
                ] : [
                  // Classic ref
                  [
                    { who:"REF",    text:`${readerName}, who do you choose?` },
                    { who:READER,   text:`I choose ${opp}.` },
                    { who:"REF",    text:`${opp}, you have been chosen. Where do you want to defend your honor?` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"REF",    text:`So be it.` },
                  ],
                  // Boxing announcer
                  [
                    { who:"ANNOUNCER", text:`In the red corner — ${readerName}. Name your opponent.` },
                    { who:READER,      text:`${opp}.` },
                    { who:"ANNOUNCER", text:`${opp}, you've been called out. Pick the venue.` },
                    { who:OPP,         text:`${locForOpp}.` },
                    { who:"ANNOUNCER", text:`Touch gloves. No backing out.` },
                  ],
                  // Street / playground
                  [
                    { who:"REF",    text:`${readerName} — who you got?` },
                    { who:READER,   text:`${opp}. Bring 'em.` },
                    { who:"REF",    text:`${opp}, they want smoke. Where you settling this?` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"REF",    text:`Then we go.` },
                  ],
                  // Medieval duel
                  [
                    { who:"HERALD", text:`${readerName}, declare thy challenger.` },
                    { who:READER,   text:`I call out ${opp}.` },
                    { who:"HERALD", text:`${opp}, the gauntlet hath been thrown. Where shall this be decided?` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"HERALD", text:`So it is set.` },
                  ],
                  // UFC face-off
                  [
                    { who:"REF",    text:`${readerName} — point to the one you want.` },
                    { who:READER,   text:`${opp}. That one.` },
                    { who:"REF",    text:`${opp}, you've been called out. Pick the ground.` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"REF",    text:`Step up. Let's go.` },
                  ],
                  // Cinematic standoff
                  [
                    { who:"REF",    text:`${readerName}. Name them.` },
                    { who:READER,   text:`${opp}.` },
                    { who:"REF",    text:`${opp}, you've been marked. Where do we end this?` },
                    { who:OPP,      text:`${locForOpp}.` },
                    { who:"REF",    text:`Then it's settled.` },
                  ],
                ];
                // Stable per-fight pick: seed from the result so re-renders keep the same script.
                const seedStr = `${opp}|${loc}|${r.time_seconds||0}|${r.winner_role||""}`;
                let seed = 0; for (let i=0; i<seedStr.length; i++) seed = (seed*31 + seedStr.charCodeAt(i)) >>> 0;
                const lines = variants[seed % variants.length];
                const headerLabel = lines[0].who === "ANNOUNCER" ? (isFeast ? "THE WEIGH-IN" : "THE WEIGH-IN")
                                  : lines[0].who === "HERALD"     ? "THE DECLARATION"
                                  : lines[0].who === "CHAIRMAN"   ? "THE CHALLENGE"
                                  : lines[0].who === "TAVERNKEEP" ? "THE INVITATION"
                                  : lines[0].who === "MC"         ? "THE INTRODUCTION"
                                  : lines[0].who === "HOST"       ? "THE SETUP"
                                  : "THE CEREMONY";
                return (
                  <div style={{ border:`3px solid ${C.ink}`, padding:"16px 14px", marginBottom:"14px", background:C.paperSoft }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, fontWeight:700, textAlign:"center", marginBottom:"12px" }}>
                      ★ {headerLabel} ★
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                      {lines.map((l, i) => (
                        <div key={i} style={{ display:"flex", gap:"8px", alignItems:"baseline" }}>
                          <div style={{ flexShrink:0, fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", fontWeight:700, color:C.red, minWidth:"78px", paddingTop:"2px" }}>
                            {l.who}
                          </div>
                          <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"15px", lineHeight:1.4, color:C.ink }}>
                            "{l.text}"
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={{ background:C.ink, color:C.paper, padding:"18px 16px", border:`3px solid ${C.ink}`, marginBottom:"14px" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"6px" }}>★ THE WINNER ★</div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:"10px" }}>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>
                    {winnerName}
                  </div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"30px", lineHeight:1, color:C.red }}>
                    {fmtTime(r.time_seconds)}
                  </div>
                </div>
                {r.time_narrative && (
                  <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:"rgba(241,232,210,0.85)", marginTop:"8px" }}>
                    {r.time_narrative}
                  </div>
                )}
              </div>

              <div style={{ border:`3px solid ${C.ink}`, marginBottom:"14px", position:"relative", boxShadow:`6px 6px 0 ${C.ink}`, height:"280px", background: tmdbImages.arena ? "transparent" : C.ink, overflow:"hidden" }}>
                {tmdbImages.arena ? (
                  <img src={tmdbImages.arena} alt={r.character} style={{ display:"block", width:"100%", height:"100%", objectFit:"cover", filter:"grayscale(20%) contrast(108%)" }} />
                ) : (
                  <div style={{ width:"100%", height:"100%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:"24px", color:C.paper, background:`repeating-linear-gradient(45deg, ${C.ink} 0 12px, #1a1a1a 12px 24px)` }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"12px", fontWeight:700 }}>★ NO PHOTO ON FILE ★</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"34px", lineHeight:1, letterSpacing:"-0.5px", textTransform:"uppercase" }}>{r.character}</div>
                    <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", color:"rgba(241,232,210,0.7)", marginTop:"6px" }}>of {r.universe}</div>
                    <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:"rgba(241,232,210,0.5)", marginTop:"14px" }}>Subject is camera-shy.</div>
                  </div>
                )}
                <div style={{ position:"absolute", top:"10px", left:"10px", background:C.red, color:C.paper, padding:"4px 8px", fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"1.5px", textTransform:"uppercase", fontWeight:700 }}>
                  OPPONENT
                </div>
                {tmdbImages.arena && (
                  <div style={{ position:"absolute", bottom:"10px", left:"10px", right:"10px", fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.paper, textShadow:"0 1px 4px rgba(0,0,0,0.7)" }}>
                    {r.character} · {r.universe}
                  </div>
                )}
              </div>

              <SectionHeader title="THE SCENE" suffix={`${r.character}'s pick`} />
              {r.location_pick && (
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginBottom:"6px" }}>
                  ▸ {r.location_pick}
                </div>
              )}
              <p style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"16px", lineHeight:1.5, color:C.ink, margin:"0 0 18px" }}>
                {r.environment}
              </p>

              <div style={{ background:C.ink, color:C.paper, padding:"14px 16px", border:`3px solid ${C.ink}`, marginBottom:"14px", textAlign:"center" }}>
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"6px" }}>★ {isFeast ? "ON YOUR MARKS" : "THE REF"} ★</div>
                <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"32px", letterSpacing:"4px", lineHeight:1 }}>
                  3 · 2 · 1
                </div>
                <div style={{ fontFamily:FONT.display, fontWeight:900, fontStyle:"italic", fontSize:"22px", color:C.red, marginTop:"4px", letterSpacing:"2px" }}>
                  {isFeast ? "EAT!" : "FIGHT!"}
                </div>
              </div>

              {isFeast && r.food && (
                <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, textAlign:"center", marginBottom:"10px" }}>
                  TONIGHT'S FOOD: <span style={{ color:C.ink, fontWeight:700 }}>{r.food}</span>
                </div>
              )}

              <SectionHeader title={isFeast ? "PLATE BY PLATE" : "PLAY BY PLAY"} suffix={isFeast ? "fork to finish" : "round one, last one"} />
              <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"18px" }}>
                {(r.play_by_play || []).map((beat, i) => (
                  <div key={i} style={{ display:"flex", gap:"12px", alignItems:"flex-start" }}>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", color:C.red, lineHeight:1, flexShrink:0, minWidth:"24px" }}>{i+1}</div>
                    <div style={{ fontFamily:FONT.display, fontSize:"16px", lineHeight:1.45, color:C.ink }}>{beat}</div>
                  </div>
                ))}
              </div>

              {isFeast && r.final_count && (
                <div style={{ border:`3px solid ${C.ink}`, padding:"14px 16px", marginBottom:"18px", background:C.paperSoft, textAlign:"center" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, marginBottom:"6px", fontWeight:700 }}>★ FINAL TALLY ★</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"18px", lineHeight:1.3 }}>{r.final_count}</div>
                </div>
              )}

              {(r.winner_quote || r.loser_quote) && (
                <>
                  <SectionHeader title={isFeast ? "POST-CONTEST QUOTES" : "POST-FIGHT QUOTES"} suffix={isFeast ? "straight from the table" : "straight from the source"} />
                  <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginBottom:"18px" }}>
                    {r.winner_quote && (
                      <div style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", background:C.paper }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"4px" }}>
                          ★ WINNER · {winnerName.toUpperCase()}
                        </div>
                        <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"17px", lineHeight:1.4, color:C.ink }}>
                          "{r.winner_quote}"
                        </div>
                      </div>
                    )}
                    {r.loser_quote && (
                      <div style={{ border:`2px solid ${C.ink}`, padding:"12px 14px", background:C.paperSoft }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, fontWeight:700, marginBottom:"4px" }}>
                          LOSER · {loserName.toUpperCase()}
                        </div>
                        <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"17px", lineHeight:1.4, color:C.ink }}>
                          "{r.loser_quote}"
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {r.condition_of_loser && (
                <BlackCallout header={`${isFeast ? "POST-CONTEST" : "POST-FIGHT"} · ${loserName.toUpperCase()}`} title={isFeast ? "THE COMA" : "THE DAMAGE"} body={r.condition_of_loser} />
              )}

              <div style={{ height:"18px" }}/>
              <div style={{ border:`3px solid ${C.ink}`, padding:"18px 16px", background:C.paperSoft, marginBottom:"14px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:"4px" }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"3px", textTransform:"uppercase", color:C.red, fontWeight:700 }}>★ ODDS OF WINNING REMATCH ★</div>
                  <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"36px", color:C.red, lineHeight:1 }}>{r.rematch_chance ?? "?"}%</div>
                </div>
                <div style={{ fontFamily:FONT.mono, fontSize:"9px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute }}>
                  chance {name || "you"} could take {r.character} with a smarter plan
                </div>
              </div>

              {!arenaShowRematch ? (
                <TabloidButton onClick={()=>setArenaShowRematch(true)}>↻ Try A Rematch</TabloidButton>
              ) : (
                <div style={{ border:`2px solid ${C.ink}`, padding:"16px", background:C.paperSoft, marginBottom:"12px" }}>
                  <Label>Pick Your Strategy</Label>
                  <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.inkMute, marginBottom:"12px" }}>
                    How does {name || "the reader"} go in this time?
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"12px" }}>
                    {(r.rematch_strategies || []).map((s, i) => (
                      <button
                        key={i}
                        onClick={()=>(isFeast ? runFeastAnalysis(s) : runArenaAnalysis(s))}
                        style={{
                          textAlign:"left", padding:"12px 14px", background:C.paper, color:C.ink,
                          border:`2px solid ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                          fontSize:"15px", lineHeight:1.4, fontWeight:600,
                          display:"flex", gap:"10px", alignItems:"flex-start",
                        }}
                      >
                        <span style={{ color:C.red, fontWeight:900, flexShrink:0 }}>★</span>
                        <span>{s}</span>
                      </button>
                    ))}
                    <button
                      onClick={()=>setArenaCustomStrategy(true)}
                      style={{
                        textAlign:"left", padding:"12px 14px", background:C.paper, color:C.ink,
                        border:`2px dashed ${C.ink}`, cursor:"pointer", fontFamily:FONT.display,
                        fontSize:"15px", lineHeight:1.4, fontWeight:600,
                        display:"flex", gap:"10px", alignItems:"flex-start",
                      }}
                    >
                      <span style={{ color:C.red, fontWeight:900, flexShrink:0 }}>✎</span>
                      <span>Choose your own strategy…</span>
                    </button>
                  </div>

                  {arenaCustomStrategy && (
                    <>
                      <Label>Your Strategy</Label>
                      <TextInput value={arenaRematchInput} onChange={setArenaRematchInput} placeholder="e.g. Go for the wand first, then run for the door." />
                      <div style={{ height:"12px" }}/>
                      <TabloidButton onClick={()=>{ const s = arenaRematchInput.trim(); if (s) (isFeast ? runFeastAnalysis(s) : runArenaAnalysis(s)); }} disabled={!arenaRematchInput.trim()}>
                        ✦ Ring The Bell →
                      </TabloidButton>
                    </>
                  )}
                  <div style={{ textAlign:"center", marginTop:"8px" }}>
                    <TextLink onClick={()=>{ setArenaShowRematch(false); setArenaRematchInput(""); setArenaCustomStrategy(false); }}>← Cancel</TextLink>
                  </div>
                </div>
              )}

              <div style={{ height:"22px" }}/>
              <RuleHr double mb="18px" />

              {/* ── WHERE TO WATCH ── */}
              {streamingRec && (
                <>
                  <SectionHeader title="STUDY THE OPPONENT" suffix="know thy enemy" />
                  <a
                    href={STREAMING_LINKS[streamingRec.service] || `https://www.google.com/search?q=${encodeURIComponent(streamingRec.title+" "+streamingRec.service)}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ display:"block", border:`3px solid ${C.ink}`, padding:"16px 18px", marginBottom:"14px", background:C.paperSoft, textDecoration:"none", color:C.ink }}
                  >
                    <div style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"2px", textTransform:"uppercase", color:C.red, marginBottom:"4px", fontWeight:700 }}>★ WATCH NEXT ★</div>
                    <div style={{ fontFamily:FONT.display, fontWeight:900, fontSize:"22px", lineHeight:1.1 }}>{streamingRec.title}</div>
                    <div style={{ fontFamily:FONT.display, fontStyle:"italic", fontSize:"14px", lineHeight:1.45, color:C.inkSoft, margin:"4px 0 10px" }}>{streamingRec.why}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", border:`1.5px solid ${C.ink}`, padding:"4px 10px" }}>{streamingRec.service} · {streamingRec.genre}</span>
                      <span style={{ fontFamily:FONT.mono, fontSize:"10px", letterSpacing:"1.5px", textTransform:"uppercase", color:C.red, fontWeight:700 }}>OPEN →</span>
                    </div>
                  </a>
                </>
              )}

              {/* ── SPREAD THE WORD ── */}
              <SectionHeader title="SPREAD THE WORD" suffix="rub it in" />
              <div style={{ border:`1.5px dashed ${C.ink}`, padding:"16px 18px", marginBottom:"14px", background:C.paperSoft }}>
                <p style={{ margin:0, fontFamily:FONT.mono, fontSize:"12px", lineHeight:1.7, whiteSpace:"pre-wrap", color:C.ink }}>{shareText}</p>
              </div>
              <TabloidButton onClick={() => {
                navigator.clipboard?.writeText(shareText).then(()=>alert("Copied. Paste it anywhere.")).catch(()=>alert("Select the text above and copy manually."));
              }}>{isFeast ? "📋 Copy The Bragging Rights" : "📋 Copy The Smack Talk"}</TabloidButton>
              <div style={{ height:"8px" }}/>
              <TabloidButton variant="secondary" onClick={()=>setShowShare(true)}>{isFeast ? "📸 Tabloid Feast Card" : "📸 Tabloid Fight Card"}</TabloidButton>

              <div style={{ height:"22px" }}/>
              <RuleHr double mb="18px" />

              <SectionHeader title="WHAT NEXT?" suffix="again? again." />
              <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                <TabloidButton variant="secondary" onClick={()=>{
                  setArenaResult(null); setArenaCharacter(""); setArenaUniverse(""); setArenaMode(""); setArenaWeapon("");
                  setArenaShowRematch(false); setArenaRematchInput(""); setArenaCustomStrategy(false);
                  setAnswers({}); setCurrentSection(0); setSecQIdx(0); setError(null);
                  setTmdbImages(prev => ({ ...prev, arena: null }));
                  setScreen("intro");
                }}>↻ Pick A New Opponent</TabloidButton>
                <TabloidButton variant="secondary" onClick={reset}>✕ Start Over</TabloidButton>
              </div>

              <PageFooter />
            </div>
          );
        })()}

        {showShare && results && experience !== "arena" && (
          <ShareCard results={results} name={name} tmdbImages={tmdbImages} caseNo={caseNo} onClose={()=>setShowShare(false)} />
        )}
        {showShare && (experience === "arena" || experience === "feast") && arenaResult && (
          <ArenaShareCard result={arenaResult} name={name} tmdbImage={tmdbImages.arena} caseNo={caseNo} variant={experience} onClose={()=>setShowShare(false)} />
        )}

      </div>
    </div>
  );
}
